import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { 
  Mail,
  Inbox,
  RefreshCw,
  Plus,
  Reply,
  ReplyAll,
  Forward,
  Send,
  Trash2,
  X,
  Search,
  Paperclip,
  Download,
  Eye,
  ChevronDown,
  ChevronLeft,
  Contrast,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  Layers,
  Star,
  MailOpen,
  UserPlus,
  User,
  ShieldAlert,
  ShieldCheck,
  Tag,
  FileText,
  Folder,
  FolderMinus,
  CheckSquare,
  UserMinus,
  Menu,
  MoreHorizontal,
  History,
  Sparkles,
  Loader2,
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AttachmentPreviewModal, { downloadAuthenticated, getPreviewKind } from './AttachmentPreviewModal';
import SpamMarkModal, {
  mergeSpamRuleIntoConfig,
  emailMatchesSpamPattern,
} from './SpamMarkModal';
import VariableInsertPanel, {
  insertTokenIntoInput,
  insertTokenIntoQuill,
} from './VariableInsertPanel';
import SelectField from './SelectField';
import { MediaAttachmentPickerButton } from './MediaLibrarySettings';
import { TemplatePickerButton } from './TemplatePickerButton';
import { PageSkeleton, Skeleton, SkeletonCircle } from './Skeleton';
import { useAuth } from '../context/AuthContext';
import { useUnreadInbox } from '../context/UnreadInboxContext';
import { useMailPreferences } from '../context/MailPreferencesContext';
import {
  getDesktopNotificationPermission,
  notifyNewMail,
  toastNewMail,
} from '../utils/desktopNotifications';
import { useMailFolders } from '../context/MailFoldersContext';
import { useMailDrag, MAIL_DRAG_MIME } from '../context/MailDragContext';
import { usePendingSend } from '../context/PendingSendContext';
import { useEmailHtmlDisplay } from '../hooks/useEmailHtmlDisplay';
import { buildOriginalQuoteHtml, combineReplyAndQuote, isEmptyRichText, readCachedMailPreferences, stripOpenTrackingPixels } from '../utils/mailPreferences';
import {
  COMPOSER_DRAFTS_CHANGED_EVENT,
  clearComposerDraft,
  composerDraftHasContent,
  composerDraftLabel,
  composerDraftAttachmentCount,
  composerDraftsContentEqual,
  draftAttachmentsToFiles,
  draftAttachmentsToPlaceholders,
  fetchComposerDrafts,
  filesToDraftAttachments,
  isComposerDraftDeleted,
  listComposerDrafts,
  makeComposerDraftId,
  removeComposerDraft,
  upsertComposerDraft,
} from '../utils/composerDrafts';
import { formatMailDateTime } from '../utils/mailDates';
import EmailHtmlBody from './EmailHtmlBody';
import ContactDetailModal from './ContactDetailModal';
import { withNormalizedSubscription } from '../utils/contactUnsubscribe';
import { quillHtmlToEmailHtml } from '../utils/quillEmailHtml';
import {
  looksLikeHtmlEmail,
  parseDesignJson,
  renderDesignToHtml,
} from '../utils/emailDesign';
import { guessGenderFromName } from '../utils/guessGender';
import {
  emailComposerFormats,
  emailComposerKeyboard,
  emailComposerToolbar,
  prepareEmailComposerQuill,
} from '../utils/quillToolbar';
import RecipientSuggestInput from './RecipientSuggestInput';
import EmailComposerAiButtons from './EmailComposerAiButtons';
import {
  applyHtmlToComposer,
  buildIncomingEmailPayload,
  plainTextToQuillHtml as aiPlainTextToQuillHtml,
  requestEmailAssist,
} from '../utils/emailAiAssist';

const withSeenFlag = (flags = []) => {
  const next = Array.isArray(flags) ? [...flags] : [];
  if (!next.includes('\\Seen')) next.push('\\Seen');
  return next;
};

const isFlagged = (flags) =>
  Array.isArray(flags) &&
  flags.some((f) => {
    const raw = String(f || '');
    return (
      raw === '\\Flagged' ||
      raw === 'Flagged' ||
      /flagged/i.test(raw)
    );
  });

const withFlaggedFlag = (flags = [], flagged = true) => {
  const next = Array.isArray(flags)
    ? flags.filter((f) => {
        const raw = String(f || '');
        return raw !== '\\Flagged' && raw !== 'Flagged' && !/flagged/i.test(raw);
      })
    : [];
  if (flagged) next.push('\\Flagged');
  return next;
};

const isInboxMailbox = (tab) =>
  tab === 'inbox' || tab === 'unread' || tab === 'important' || tab === 'spam' || tab === 'trash' || tab === 'category';

const normalizeMessageId = (value) =>
  String(value || '')
    .trim()
    .replace(/^<|>$/g, '')
    .toLowerCase();

const FOLDER_LABELS = {
  inbox: 'Empfangen',
  unread: 'Ungelesen',
  important: 'Wichtig',
  sent: 'Gesendet',
  drafts: 'Entwürfe',
  spam: 'Spam',
  trash: 'Papierkorb',
  category: 'Ordner',
};

/** Composer-Anhang: Thumbnail + Klick öffnet Vorschau */
const ComposerAttachmentChip = ({ file, onPreview, onRemove, formatFileSize }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const kind = getPreviewKind(file?.name, file?.type);
  const isImage = kind === 'image';

  useEffect(() => {
    if (!file || !isImage) {
      setThumbUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <div className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/80 pl-1 pr-1 py-1 max-w-full">
      <button
        type="button"
        onClick={onPreview}
        className="inline-flex items-center gap-1.5 min-w-0 text-left rounded-md px-1 py-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-700/80 transition-colors"
        title="Vorschau öffnen"
      >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt=""
            className="w-8 h-8 rounded object-cover flex-shrink-0 bg-neutral-200 dark:bg-neutral-700"
          />
        ) : (
          <span className="w-8 h-8 rounded bg-neutral-200/80 dark:bg-neutral-700 flex items-center justify-center flex-shrink-0">
            <Paperclip className="w-3.5 h-3.5 text-neutral-500" />
          </span>
        )}
        <span className="min-w-0">
          <span className="block text-[11px] font-medium text-neutral-800 dark:text-neutral-100 truncate max-w-[9rem] sm:max-w-[12rem]">
            {file.name}
          </span>
          <span className="block text-[10px] text-neutral-500 tabular-nums">
            {formatFileSize(file.size)}
            {kind !== 'other' && kind !== 'doc' ? ' · Vorschau' : ''}
          </span>
        </span>
        <Eye className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
        title="Anhang entfernen"
        aria-label="Anhang entfernen"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const resolveMailFolder = (tab) => {
  if (tab === 'spam') return 'spam';
  if (tab === 'trash') return 'trash';
  if (tab === 'important') return 'important';
  return 'inbox';
};

/** IMAP-Quellordner für Move/Delete/Details (Wichtig liegt in INBOX) */
const resolveImapSourceFolder = (folderOrTab) => {
  const f = String(folderOrTab || 'inbox').toLowerCase();
  if (f === 'spam' || f === 'junk') return 'spam';
  if (f === 'trash' || f === 'papierkorb') return 'trash';
  return 'inbox';
};

const FREE_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.de', 'outlook.com', 'hotmail.com',
  'live.com', 'msn.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'gmx.de', 'gmx.net',
  'gmx.at', 'gmx.ch', 'web.de', 'mail.de', 't-online.de', 'freenet.de', 'posteo.de',
  'protonmail.com', 'proton.me', 'mailbox.org', 'yandex.com', 'yandex.ru',
]);

const titleCaseWords = (value) =>
  String(value || '')
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const THREADS_PAGE_SIZE = 50;
const MESSAGES_PAGE_SIZE = 30;
/** Erste Inbox-Seite: schnell sichtbar */
const INBOX_PAGE_SIZE = 100;
/** Hintergrund-Nachladen / Scroll: größere IMAP-Header-Seiten */
const INBOX_FILL_PAGE_SIZE = 250;
const SENT_PAGE_SIZE = 100;
const AUTO_REFRESH_MS = 8_000;
const MAIL_LEAVE_MS = 320;
const INBOX_LOCAL_CACHE_PREFIX = 'inboxEmailsCache.v5:';
/** Header-Cache: möglichst die ganze INBOX, Quota-Fallback in writeInboxLocalCache */
const INBOX_LOCAL_CACHE_MAX = 8000;

const extractEmail = (emailString) => {
  if (!emailString) return '';
  const match = emailString.match(/<(.+)>/);
  return match ? match[1] : emailString;
};

const extractName = (emailString) => {
  if (!emailString) return '';
  const match = emailString.match(/^(.+)\s*<.+>$/);
  return match ? match[1].replace(/"/g, '').trim() : extractEmail(emailString);
};

const emailHasAttachment = (email) => {
  if (!email) return false;
  if (email.hasAttachment === true) return true;
  if (Number(email.attachments_count) > 0) return true;
  if (Array.isArray(email.attachments) && email.attachments.length > 0) return true;
  return false;
};

const inboxCacheStorageKey = (mailbox) =>
  `${INBOX_LOCAL_CACHE_PREFIX}${String(mailbox || '').trim().toLowerCase() || 'unknown'}`;

const clearAllInboxLocalCaches = () => {
  try {
    const drop = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith(INBOX_LOCAL_CACHE_PREFIX) ||
          key.startsWith('inboxEmailsCache.v4:') ||
          key.startsWith('inboxEmailsCache.v3:') ||
          key.startsWith('inboxEmailsCache.v2') ||
          key === 'inboxEmailsCache.v2' ||
          key === 'inboxEmailsCache.v1')
      ) {
        drop.push(key);
      }
    }
    drop.forEach((key) => localStorage.removeItem(key));
  } catch (_) {}
};

const readInboxLocalCache = (mailbox) => {
  try {
    const mb = String(mailbox || '').trim().toLowerCase();
    if (!mb) return null;
    const raw = localStorage.getItem(inboxCacheStorageKey(mb));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !Array.isArray(data.emails)) return null;
    if (data.folder && data.folder !== 'inbox') return null;
    if (data.mailbox && String(data.mailbox).toLowerCase() !== mb) return null;
    const emails = data.emails.slice(0, INBOX_LOCAL_CACHE_MAX);
    return {
      ...data,
      emails,
      hasMore: !!data.hasMore || emails.length < (Number(data.total) || emails.length) || data.emails.length > emails.length,
    };
  } catch (_) {
    return null;
  }
};

/** UIDs, die nach Löschen/Papierkorb nicht wieder in Empfangen auftauchen dürfen (Cache/Merge) */
const INBOX_TOMBSTONE_TTL_MS = 15 * 60 * 1000;
const inboxTombstonesByMailbox = new Map(); // mailbox -> Map(uid -> expiresAt)

const addInboxTombstones = (mailbox, uids) => {
  const mb = String(mailbox || '').trim().toLowerCase();
  if (!mb) return;
  let map = inboxTombstonesByMailbox.get(mb);
  if (!map) {
    map = new Map();
    inboxTombstonesByMailbox.set(mb, map);
  }
  const exp = Date.now() + INBOX_TOMBSTONE_TTL_MS;
  (uids || []).forEach((u) => {
    const key = String(u || '');
    if (key) map.set(key, exp);
  });
};

const clearInboxTombstones = (mailbox, uids) => {
  const mb = String(mailbox || '').trim().toLowerCase();
  const map = mb ? inboxTombstonesByMailbox.get(mb) : null;
  if (!map?.size) return;
  (uids || []).forEach((u) => map.delete(String(u || '')));
};

const filterInboxTombstones = (mailbox, emails = []) => {
  const mb = String(mailbox || '').trim().toLowerCase();
  const map = mb ? inboxTombstonesByMailbox.get(mb) : null;
  if (!map?.size) return emails || [];
  const now = Date.now();
  for (const [key, exp] of [...map.entries()]) {
    if (!exp || exp < now) map.delete(key);
  }
  if (!map.size) return emails || [];
  return (emails || []).filter((mail) => !map.has(String(mail?.uid)));
};

const writeInboxLocalCache = (mailbox, payload) => {
  const mb = String(mailbox || '').trim().toLowerCase();
  if (!mb) return;
  const persist = (maxEmails) => {
    const all = filterInboxTombstones(
      mb,
      Array.isArray(payload.emails) ? payload.emails : []
    );
    const truncated = all.length > maxEmails;
    const emails = truncated ? all.slice(0, maxEmails) : all;
    const uids = emails
      .map((m) => Number(m.uid))
      .filter((u) => Number.isFinite(u) && u > 0);
    const seqs = emails
      .map((m) => Number(m.seq))
      .filter((s) => Number.isFinite(s) && s > 0);
    const total = Number(payload.total);
    const resolvedTotal = Number.isFinite(total) && total > 0 ? total : emails.length;
    // Nach Kürzen der neuesten N Mails: Cursor aufs älteste gecachte Element,
    // sonst bleibt hasMore=false bei total>emails und ältere Mails fehlen dauerhaft.
    const hasMore =
      truncated ||
      !!payload.hasMore ||
      (resolvedTotal > 0 && emails.length < resolvedTotal);
    localStorage.setItem(
      inboxCacheStorageKey(mb),
      JSON.stringify({
        mailbox: mb,
        folder: 'inbox',
        savedAt: Date.now(),
        emails,
        total: resolvedTotal,
        hasMore,
        oldestUid: uids.length ? Math.min(...uids) : payload.oldestUid || null,
        oldestSeq: seqs.length ? Math.min(...seqs) : payload.oldestSeq || null,
      })
    );
  };
  try {
    persist(INBOX_LOCAL_CACHE_MAX);
  } catch (_) {
    try {
      persist(4000);
    } catch (__) {
      try {
        persist(1500);
      } catch (___) {
        try {
          persist(800);
        } catch (____) {}
      }
    }
  }
};

const patchInboxLocalCacheFlags = (uid, nextFlags, mailbox) => {
  const data = readInboxLocalCache(mailbox);
  if (!data?.emails?.length) return;
  const uidKey = String(uid);
  let changed = false;
  const emails = data.emails.map((mail) => {
    if (String(mail.uid) !== uidKey) return mail;
    changed = true;
    return { ...mail, flags: nextFlags };
  });
  if (changed) writeInboxLocalCache(data.mailbox || mailbox, { ...data, emails });
};

const removeFromInboxLocalCache = (uids, mailbox) => {
  const data = readInboxLocalCache(mailbox);
  if (!data?.emails?.length) return;
  const drop = new Set((uids || []).map((u) => String(u)));
  const emails = data.emails.filter((mail) => !drop.has(String(mail.uid)));
  if (emails.length === data.emails.length) return;
  writeInboxLocalCache(data.mailbox || mailbox, {
    ...data,
    emails,
    total: Math.max(0, (Number(data.total) || data.emails.length) - (data.emails.length - emails.length)),
  });
};

const inboxListSignature = (list = []) =>
  list
    .map((e) => `${e.uid}:${Array.isArray(e.flags) ? e.flags.join(',') : ''}:${e.subject || ''}`)
    .join('|');

const sentListSignature = (list = []) =>
  list
    .map((e) => `${e.id}:${e.uid || ''}:${e.opened || 0}:${e.subject || ''}`)
    .join('|');

const emptyFolderSnapshot = () => ({
  emails: [],
  total: 0,
  hasMore: false,
  oldestUid: null,
});

const BUILTIN_CONTACT_TEMPLATE_VARS = [
  { key: 'contact_name', label: 'Name des Kontakts', source: 'contact:name', value: '', options: {} },
  { key: 'contact_company', label: 'Unternehmen', source: 'contact:company', value: '', options: {} },
  { key: 'contact_email', label: 'E-Mail', source: 'contact:email', value: '', options: {} },
  { key: 'contact_phone', label: 'Telefon', source: 'contact:phone', value: '', options: {} },
  { key: 'contact_website', label: 'Website', source: 'contact:website', value: '', options: {} },
  { key: 'contact_anrede', label: 'Anrede (Sehr geehrte…)', source: 'contact:anrede', value: '', options: {} },
];

const EmailInbox = () => {
  const { user } = useAuth();
  const { refreshUnreadCount, decrementUnread, adjustFolderCount, setFolderCountAtLeast, setFolderCount } = useUnreadInbox();
  const { preferences, save: saveMailPreferences } = useMailPreferences();
  const desktopNotificationsEnabledRef = useRef(preferences.desktopNotifications !== false);
  useEffect(() => {
    desktopNotificationsEnabledRef.current = preferences.desktopNotifications !== false;
  }, [preferences.desktopNotifications]);
  const { categories, flatFolders, refreshCategories, spamRules, saveSpamRules } = useMailFolders();
  const { beginDrag, endDrag, setDragGhost, registerDropHandler } = useMailDrag();
  const { isDarkMode, invertEmailInDark, toggleEmailInvert, emailHtmlClassName } = useEmailHtmlDisplay();
  const {
    secondsLeft: sendCountdown,
    isPending: hasPendingSend,
    resumeNonce,
    startPending,
    cancelPending,
  } = usePendingSend();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const authMailbox = String(user?.email || '').trim().toLowerCase() || null;
  const mailboxUserRef = useRef(null);
  const allowImagesByDefault = !preferences.blockExternalContent;
  const [emails, setEmails] = useState([]);
  const [, setSpamMailboxPath] = useState('Spam');
  const [categoryMessageIds, setCategoryMessageIds] = useState(() => new Set());
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [detailMoreOpen, setDetailMoreOpen] = useState(false);
  const [selectedEmailCategories, setSelectedEmailCategories] = useState([]);
  const [movingSpam, setMovingSpam] = useState(false);
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [spamMarkPrompt, setSpamMarkPrompt] = useState(null); // { emails: [] }
  const [leavingUids, setLeavingUids] = useState(() => new Set());
  const [leavingKind, setLeavingKind] = useState(null); // 'spam' | 'delete' | null
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inboxTotal, setInboxTotal] = useState(0);
  const [inboxHasMore, setInboxHasMore] = useState(false);
  const [inboxOldestUid, setInboxOldestUid] = useState(null);
  const [inboxOldestSeq, setInboxOldestSeq] = useState(null);
  const [sentTotal, setSentTotal] = useState(0);
  const [sentHasMore, setSentHasMore] = useState(false);
  const [sentOldestUid, setSentOldestUid] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [imapConfigured, setImapConfigured] = useState(false);
  const [mailboxUser, setMailboxUser] = useState(null);
  const [, setDebugInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [threadSort, setThreadSort] = useState('newest'); // newest | oldest | most | unread
  const [visibleThreadLimit, setVisibleThreadLimit] = useState(THREADS_PAGE_SIZE);
  const [visibleMessageLimit, setVisibleMessageLimit] = useState(MESSAGES_PAGE_SIZE);
  const [selectedSentEmail, setSelectedSentEmail] = useState(null);
  const [selectedInboxEmail, setSelectedInboxEmail] = useState(null);
  const [selectedInboxUid, setSelectedInboxUid] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadDetails, setThreadDetails] = useState({});
  const [, setExpandedThreadUid] = useState(null);
  const [listExpandedThreadId, setListExpandedThreadId] = useState(null);
  const [inboxDetailLoading, setInboxDetailLoading] = useState(false);
  const [allowExternalImages, setAllowExternalImages] = useState(
    () => !readCachedMailPreferences().blockExternalContent
  );
  const [inboxSentHistory, setInboxSentHistory] = useState([]);
  const [inboxSentHistoryLoading, setInboxSentHistoryLoading] = useState(false);
  const [inboxSentHistoryExpandedId, setInboxSentHistoryExpandedId] = useState(null);
  const [inboxSentHistoryBodies, setInboxSentHistoryBodies] = useState({});
  const threadLoadIdRef = useRef(0);
  const markReadInFlightRef = useRef(new Set());
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox'); // inbox | unread | important | sent
  const [flagBusyUid, setFlagBusyUid] = useState(null);
  const [knownContactEmails, setKnownContactEmails] = useState(() => new Set());
  const [contactsByEmail, setContactsByEmail] = useState(() => new Map());
  const [showContactModal, setShowContactModal] = useState(false);
  const [viewedContact, setViewedContact] = useState(null);
  const [viewedContactLoading, setViewedContactLoading] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deletingContact, setDeletingContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    website: '',
    notes: '',
    gender: 'unknown',
    name2: '',
    gender2: 'unknown',
  });
  const [contactGenderManual, setContactGenderManual] = useState(false);
  const [contactGender2Manual, setContactGender2Manual] = useState(false);
  const [contactsForGender, setContactsForGender] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [showComposer, setShowComposer] = useState(false);
  const [composerMode, setComposerMode] = useState('new'); // new | reply | replyAll | forward
  const [composerSourceEmail, setComposerSourceEmail] = useState(null);
  const [aiReplyLoading, setAiReplyLoading] = useState(false);
  const [, setAutoRefreshing] = useState(false);
  const fetchInFlightRef = useRef(false);
  const inboxFillInFlightRef = useRef(false);
  const inboxFillGenerationRef = useRef(0);
  const sentFillInFlightRef = useRef(false);
  const sentFillGenerationRef = useRef(0);
  const emailsRef = useRef([]);
  const sentEmailsRef = useRef([]);
  const activeTabRef = useRef(activeTab);
  const emailsFolderRef = useRef('inbox');
  /** Welches Postfach die aktuelle Liste gehört — verhindert Merge fremder Konten */
  const emailsMailboxOwnerRef = useRef(null);
  const inboxMetaRef = useRef({ total: 0, hasMore: false, oldestUid: null, oldestSeq: null });
  const sentMetaRef = useRef({ total: 0, hasMore: false, oldestUid: null });
  /** Sitzungs-Cache: Ordnerlisten (inbox/spam/trash/sent) ohne erneutes IMAP */
  const folderSessionCacheRef = useRef(new Map());
  /** Sitzungs-Cache: geladene E-Mail-Details je Ordner+UID */
  const detailSessionCacheRef = useRef(new Map());
  const bulkDeletingRef = useRef(null);
  const [threadContextMenu, setThreadContextMenu] = useState(null); // { x, y, thread, assignedCategoryIds }
  const threadContextMenuFetchGenRef = useRef(0);
  const [bulkDeleting, setBulkDeleting] = useState(null); // { threadId, done, total, label }
  const [selectedMailUids, setSelectedMailUids] = useState(() => new Set());
  const [selectedSentKeys, setSelectedSentKeys] = useState(() => new Set());
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [composerDrafts, setComposerDrafts] = useState([]);
  const [composerDraftId, setComposerDraftId] = useState(null);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const composerDraftIdRef = useRef(null);
  /** true erst nach echter Nutzeränderung — verhindert Speichern beim bloßen Öffnen */
  const composerDraftDirtyRef = useRef(false);
  /** Serialisierte Anhänge für Entwurfsspeicherung (Base64) */
  const draftAttachmentsRef = useRef([]);
  const draftAttachmentsSyncRef = useRef(Promise.resolve([]));
  /** Aktuelle File-Objekte im Composer — damit Speichern nie Anhänge „vergisst“ */
  const attachmentsFilesRef = useRef([]);
  const [mailDeletePrompt, setMailDeletePrompt] = useState(null);
  // { title, message, confirmLabel, run }
  const listScrollRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreSentSentinelRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const [composerData, setComposerData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    html: '',
    text: ''
  });
  const [composerQuoteHtml, setComposerQuoteHtml] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [composerEditorKey, setComposerEditorKey] = useState(0);
  const [templateVars, setTemplateVars] = useState(BUILTIN_CONTACT_TEMPLATE_VARS);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [composerTemplateId, setComposerTemplateId] = useState('');
  /** Vollständiges Design-/HTML-Template — Quill nicht nutzen */
  const [composerLockedHtml, setComposerLockedHtml] = useState(null);
  const [insertTarget, setInsertTarget] = useState('content');
  const quillRef = useRef(null);
  const composerHtmlRef = useRef('<p><br></p>');
  const subjectRef = useRef(null);

  const quillModules = useMemo(() => ({
    toolbar: emailComposerToolbar,
    clipboard: {
      matchVisual: false,
    },
    keyboard: emailComposerKeyboard,
  }), []);

  const quillFormats = useMemo(() => emailComposerFormats, []);

  useEffect(() => {
    if (!showComposer || composerLockedHtml) return undefined;
    const timer = window.setTimeout(() => {
      try {
        prepareEmailComposerQuill(quillRef.current?.getEditor?.());
      } catch (_) {
        /* ignore */
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [showComposer, composerLockedHtml, composerEditorKey]);

  const rememberFolderSession = (folder, snapshot) => {
    if (!folder || folder === 'sent') return;
    const mailbox = String(mailboxUser || authMailbox || '').trim().toLowerCase();
    let emailsList = snapshot?.emails || [];
    if (folder === 'inbox') {
      emailsList = filterInboxTombstones(mailbox, emailsList);
    }
    if (!emailsList.length && !(Number(snapshot?.total) > 0)) return;
    folderSessionCacheRef.current.set(folder, {
      emails: emailsList,
      total: Number(snapshot?.total) || emailsList.length,
      hasMore: !!snapshot?.hasMore,
      oldestUid: snapshot?.oldestUid || null,
      oldestSeq: snapshot?.oldestSeq || null,
      savedAt: Date.now(),
    });
  };

  const rememberSentSession = (snapshot) => {
    const emailsList = snapshot?.emails || (Array.isArray(snapshot) ? snapshot : []);
    if (!emailsList.length && !(Number(snapshot?.total) > 0)) return;
    folderSessionCacheRef.current.set('sent', {
      emails: emailsList,
      total: Number(snapshot?.total) || emailsList.length,
      hasMore: !!snapshot?.hasMore,
      oldestUid: snapshot?.oldestUid || null,
      savedAt: Date.now(),
    });
  };

  const invalidateFolderSession = (...folders) => {
    folders.filter(Boolean).forEach((f) => folderSessionCacheRef.current.delete(f));
  };

  const detailCacheKey = (uid, folder) =>
    `${folder || 'inbox'}:${String(uid)}`;

  /** Nach Löschen: Client-Caches sofort bereinigen (verhindert Geister-Mails in Empfangen) */
  const purgeDeletedUidsFromCaches = (uids, { fromFolder = 'inbox' } = {}) => {
    const uidList = [...new Set((uids || []).map((u) => String(u)).filter(Boolean))];
    if (!uidList.length) return;
    const drop = new Set(uidList);
    const mailbox = String(mailboxUser || authMailbox || '').trim().toLowerCase();

    if (fromFolder === 'inbox' || fromFolder === 'important') {
      addInboxTombstones(mailbox, uidList);
    }

    const prev = emailsRef.current || [];
    const next = prev.filter((e) => !drop.has(String(e.uid)));
    if (next.length !== prev.length) {
      emailsRef.current = next;
      setEmails(next);
      setInboxTotal((t) => Math.max(0, (Number(t) || prev.length) - (prev.length - next.length)));
      rememberFolderSession(emailsFolderRef.current || fromFolder, {
        emails: next,
        total: Math.max(0, (inboxMetaRef.current.total || prev.length) - (prev.length - next.length)),
        hasMore: inboxMetaRef.current.hasMore,
        oldestUid: inboxMetaRef.current.oldestUid,
        oldestSeq: inboxMetaRef.current.oldestSeq,
      });
    }

    for (const [folder, snap] of [...folderSessionCacheRef.current.entries()]) {
      if (folder === 'sent') continue;
      const list = snap?.emails || [];
      const filtered = list.filter((e) => !drop.has(String(e.uid)));
      if (filtered.length === list.length) continue;
      folderSessionCacheRef.current.set(folder, {
        ...snap,
        emails: filtered,
        total: Math.max(0, (Number(snap.total) || list.length) - (list.length - filtered.length)),
        savedAt: Date.now(),
      });
    }

    removeFromInboxLocalCache(uidList, mailbox);
    uidList.forEach((uid) => {
      detailSessionCacheRef.current.delete(detailCacheKey(uid, fromFolder));
      detailSessionCacheRef.current.delete(detailCacheKey(uid, 'inbox'));
      detailSessionCacheRef.current.delete(detailCacheKey(uid, 'trash'));
      detailSessionCacheRef.current.delete(detailCacheKey(uid, 'spam'));
    });
  };

  const rememberEmailDetail = (uid, folder, email) => {
    if (uid == null || !email) return;
    detailSessionCacheRef.current.set(detailCacheKey(uid, folder), email);
  };

  const readEmailDetail = (uid, folder) =>
    detailSessionCacheRef.current.get(detailCacheKey(uid, folder)) || null;

  const applyFolderSnapshot = (folder, snap) => {
    const mailbox = String(mailboxUser || authMailbox || '').trim().toLowerCase();
    const emailsList =
      folder === 'inbox'
        ? filterInboxTombstones(mailbox, snap?.emails || [])
        : snap?.emails || [];
    emailsFolderRef.current = folder;
    emailsRef.current = emailsList;
    setEmails(emailsList);
    setInboxTotal(Number(snap?.total) || emailsList.length);
    setInboxHasMore(!!snap?.hasMore);
    setInboxOldestUid(snap?.oldestUid || null);
    setInboxOldestSeq(snap?.oldestSeq || null);
  };

  useEffect(() => {
    if (!imapConfigured) return;
    if (activeTab === 'drafts') return;
    if (activeTab === 'sent') {
      sentFillGenerationRef.current += 1;
      sentFillInFlightRef.current = false;
      // Aktuellen IMAP-Ordner in der Sitzung behalten
      const prevFolder = emailsFolderRef.current;
      if (prevFolder && prevFolder !== 'sent' && (emailsRef.current || []).length) {
        rememberFolderSession(prevFolder, {
          emails: emailsRef.current,
          ...inboxMetaRef.current,
        });
      }
      const cachedSent = folderSessionCacheRef.current.get('sent');
      if (cachedSent?.emails?.length) {
        setSentEmails(cachedSent.emails);
        sentEmailsRef.current = cachedSent.emails;
        setSentTotal(Number(cachedSent.total) || cachedSent.emails.length);
        setSentHasMore(!!cachedSent.hasMore);
        setSentOldestUid(cachedSent.oldestUid || null);
        sentMetaRef.current = {
          total: Number(cachedSent.total) || cachedSent.emails.length,
          hasMore: !!cachedSent.hasMore,
          oldestUid: cachedSent.oldestUid || null,
        };
        loadSentEmails({ silent: true });
      } else {
        loadSentEmails();
      }
      return;
    }

    const folder = resolveMailFolder(activeTab);
    const prevFolder = emailsFolderRef.current;
    const folderChanged = prevFolder !== folder;

    if (folderChanged) {
      if (prevFolder && (emailsRef.current || []).length) {
        rememberFolderSession(prevFolder, {
          emails: emailsRef.current,
          ...inboxMetaRef.current,
        });
      }
      setSelectedMailUids(new Set());

      const cached = folderSessionCacheRef.current.get(folder);
      if (cached?.emails?.length) {
        applyFolderSnapshot(folder, cached);
        // Im Hintergrund aktualisieren, Liste bleibt sofort sichtbar
        loadEmails(false, { folder, silent: true });
        return;
      }

      applyFolderSnapshot(folder, emptyFolderSnapshot());
      loadEmails(false, {
        folder,
        force: folder === 'spam' || folder === 'trash' || folder === 'important',
      });
      return;
    }

    if (!emailsRef.current.length) {
      const cached = folderSessionCacheRef.current.get(folder);
      if (cached?.emails?.length) {
        applyFolderSnapshot(folder, cached);
        loadEmails(false, { folder, silent: true });
      } else if (folder === 'inbox') {
        loadEmails(false, { folder: 'inbox' });
      } else {
        loadEmails(false, { folder, force: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, imapConfigured]);

  const getComposerHtml = () => {
    if (composerLockedHtml) return composerLockedHtml;
    try {
      const editor = quillRef.current?.getEditor?.();
      if (editor) return editor.root.innerHTML;
    } catch (_) {}
    return composerHtmlRef.current || composerData.html || '<p><br></p>';
  };

  const getComposerHtmlForSend = () => {
    if (composerLockedHtml) {
      if (composerQuoteHtml && !isEmptyRichText(composerQuoteHtml)) {
        if (/<\/body>/i.test(composerLockedHtml)) {
          return composerLockedHtml.replace(
            /<\/body>/i,
            `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e5e5e5;">${composerQuoteHtml}</div></body>`
          );
        }
        return `${composerLockedHtml}${composerQuoteHtml}`;
      }
      return composerLockedHtml;
    }
    const raw = getComposerHtml();
    const body = quillHtmlToEmailHtml(raw);
    const withQuote = combineReplyAndQuote(body, composerQuoteHtml);
    return withQuote;
  };

  const plainTextToQuillHtml = (text) => {
    const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
    if (!lines.length) return '<p><br></p>';
    return lines
      .map((line) => {
        const t = String(line || '');
        if (!t.trim()) return '<p><br></p>';
        return `<p>${escapeHtml(t)}</p>`;
      })
      .join('');
  };

  const isLockedMailTemplate = (template) => {
    if (!template) return false;
    if (parseDesignJson(template.design_json)) return true;
    const content = String(template.content || '').trim();
    if (!content) return false;
    // Nur echte Design-Mails sperren — Quill-Text-HTML mit <p>/<div> bleibt editierbar
    if (/data-email-design\s*=\s*["']1["']/i.test(content)) return true;
    return false;
  };

  const resolveTemplateHtml = (template) => {
    if (!template) return '';
    const design = parseDesignJson(template.design_json);
    if (design) return renderDesignToHtml(design);
    const content = String(template.content || '');
    if (isLockedMailTemplate(template) || looksLikeHtmlEmail(content)) return content;
    return plainTextToQuillHtml(content);
  };

  const loadTemplateAttachmentsAsFiles = async (template) => {
    const atts = Array.isArray(template?.attachments) ? template.attachments : [];
    if (!atts.length) return [];
    const files = [];
    for (const att of atts) {
      if (!att?.id) continue;
      try {
        const res = await axios.get(`/api/template-attachments/${att.id}`, {
          responseType: 'blob',
        });
        files.push(
          new File([res.data], att.filename || 'anhang', {
            type: att.contentType || res.data.type || 'application/octet-stream',
          })
        );
      } catch (_) {
        /* ignore single attachment errors */
      }
    }
    return files;
  };

  const loadMailTemplates = useCallback(async () => {
    try {
      const res = await axios.get('/api/templates');
      setMailTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setMailTemplates([]);
    }
  }, []);

  const applyMailTemplate = async (templateId) => {
    const id = String(templateId || '');
    setComposerTemplateId(id);
    composerDraftDirtyRef.current = true;
    if (!id) {
      setComposerLockedHtml(null);
      setTemplateVars(BUILTIN_CONTACT_TEMPLATE_VARS);
      return;
    }
    let template = mailTemplates.find((t) => String(t.id) === id);
    if (!template) {
      // Fallback: frisch laden, falls die Liste veraltet ist
      try {
        const res = await axios.get('/api/templates');
        const list = Array.isArray(res.data) ? res.data : [];
        setMailTemplates(list);
        template = list.find((t) => String(t.id) === id);
      } catch (_) {
        /* ignore */
      }
    }
    if (!template) {
      toast.error('Vorlage nicht gefunden');
      return;
    }
    const html = resolveTemplateHtml(template);
    const isDesign = isLockedMailTemplate(template);

    setComposerData((prev) => ({
      ...prev,
      // Antwort-Vorlagen ohne Betreff: bestehenden Re:-Betreff nicht überschreiben
      subject: String(template.subject || '').trim() ? template.subject : prev.subject,
      html: isDesign ? '<p><br></p>' : html,
      text: '',
    }));

    if (isDesign) {
      setComposerLockedHtml(html);
      composerHtmlRef.current = html;
    } else {
      setComposerLockedHtml(null);
      composerHtmlRef.current = html;
      setComposerEditorKey((k) => k + 1);
    }

    try {
      const files = await loadTemplateAttachmentsAsFiles(template);
      if (files.length) {
        setComposerAttachments((prev) => [...prev, ...files]);
      }
    } catch (_) {
      /* ignore */
    }

    toast.success(`Vorlage „${template.name}“ geladen`);
    try {
      const varsRes = await axios.get('/api/template-variables', {
        params: { templateId: template.id },
      });
      setTemplateVars(varsRes.data || BUILTIN_CONTACT_TEMPLATE_VARS);
    } catch (_) {
      setTemplateVars(BUILTIN_CONTACT_TEMPLATE_VARS);
    }
  };

  const applyContactsList = (list = []) => {
    const rows = Array.isArray(list) ? list : [];
    setContactsForGender(rows);
    const emails = new Set();
    const byEmail = new Map();
    rows.forEach((c) => {
      const key = normalizeContactEmail(c?.email);
      if (!key) return;
      emails.add(key);
      byEmail.set(key, c);
    });
    setKnownContactEmails(emails);
    setContactsByEmail(byEmail);
  };

  useEffect(() => {
    checkImapConfig();
    setComposerDraftId(null);
    composerDraftIdRef.current = null;
    setDraftSavedAt(null);
    setComposerDrafts(listComposerDrafts(authMailbox));
    fetchComposerDrafts(authMailbox)
      .then((list) => setComposerDrafts(list))
      .catch(() => setComposerDrafts(listComposerDrafts(authMailbox)));
    setTemplateVars(BUILTIN_CONTACT_TEMPLATE_VARS);
    loadMailTemplates();
    axios
      .get('/api/contacts')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        applyContactsList(list);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / Kontowechsel
  }, [authMailbox, loadMailTemplates]);

  // Beim Kontowechsel: Sitzungs-Caches leeren, damit keine fremden UIDs angezeigt werden
  useEffect(() => {
    const prev = mailboxUserRef.current;
    const next = authMailbox;
    if (prev && next && prev !== next) {
      folderSessionCacheRef.current.clear();
      detailSessionCacheRef.current.clear();
      clearAllInboxLocalCaches();
      emailsRef.current = [];
      sentEmailsRef.current = [];
      emailsFolderRef.current = 'inbox';
      emailsMailboxOwnerRef.current = null;
      inboxFillGenerationRef.current += 1;
      inboxFillInFlightRef.current = false;
      sentFillGenerationRef.current += 1;
      sentFillInFlightRef.current = false;
      setEmails([]);
      setSentEmails([]);
      setInboxTotal(0);
      setInboxHasMore(false);
      setInboxOldestUid(null);
      setInboxOldestSeq(null);
      setSentTotal(0);
      setSentHasMore(false);
      setSentOldestUid(null);
      sentMetaRef.current = { total: 0, hasMore: false, oldestUid: null };
      setSelectedInboxEmail(null);
      setSelectedInboxUid(null);
      setSelectedThreadId(null);
      setThreadMessages([]);
      setThreadDetails({});
      setSelectedMailUids(new Set());
      setSelectedSentKeys(new Set());
      setSelectedSentEmail(null);
      setActiveTab('inbox');
      setInitialLoading(true);
      setMailboxUser(next);
      setComposerDrafts(listComposerDrafts(next));
      fetchComposerDrafts(next)
        .then((list) => setComposerDrafts(list))
        .catch(() => setComposerDrafts(listComposerDrafts(next)));
      setComposerDraftId(null);
      composerDraftIdRef.current = null;
      setDraftSavedAt(null);
    }
    mailboxUserRef.current = next;
  }, [authMailbox]);

  useEffect(() => {
    composerDraftIdRef.current = composerDraftId;
  }, [composerDraftId]);

  const refreshComposerDrafts = useCallback(() => {
    setComposerDrafts(listComposerDrafts(authMailbox));
    return fetchComposerDrafts(authMailbox)
      .then((list) => {
        setComposerDrafts(list);
        return list;
      })
      .catch(() => listComposerDrafts(authMailbox));
  }, [authMailbox]);

  const buildComposerDraftPayload = () => ({
    mode: composerMode,
    to: composerData.to || '',
    cc: composerData.cc || '',
    bcc: composerData.bcc || '',
    subject: composerData.subject || '',
    html: getComposerHtml(),
    lockedHtml: composerLockedHtml || null,
    templateId: composerTemplateId || '',
    attachments: Array.isArray(draftAttachmentsRef.current) ? draftAttachmentsRef.current : [],
  });

  const syncDraftAttachmentsFromFiles = (files) => {
    const list = Array.isArray(files) ? files : [];
    attachmentsFilesRef.current = list;
    const oversized = list.filter(
      (f) => !f?.__draftPlaceholder && (Number(f?.size) || 0) > 4 * 1024 * 1024
    );
    if (oversized.length) {
      toast.error(
        oversized.length === 1
          ? `„${oversized[0].name}“ ist zu groß für den Entwurf (max. 4 MB)`
          : `${oversized.length} Anhänge zu groß für den Entwurf (max. 4 MB je Datei)`
      );
    }
    const previous = Array.isArray(draftAttachmentsRef.current)
      ? draftAttachmentsRef.current
      : [];
    const job = filesToDraftAttachments(
      list.filter((f) => !f?.__draftPlaceholder),
      previous
    )
      .then((atts) => {
        // Platzhalter behalten bestehende Base64-Daten
        if (!atts.length && previous.length && list.some((f) => f?.__draftPlaceholder)) {
          draftAttachmentsRef.current = previous;
          return previous;
        }
        draftAttachmentsRef.current = atts;
        return atts;
      })
      .catch(() => {
        return Array.isArray(draftAttachmentsRef.current) ? draftAttachmentsRef.current : [];
      });
    draftAttachmentsSyncRef.current = job;
    return job;
  };

  const ensureDraftAttachmentsReady = async () => {
    try {
      await draftAttachmentsSyncRef.current;
    } catch (_) {
      /* ignore */
    }
    const files = (attachmentsFilesRef.current || []).filter((f) => !f?.__draftPlaceholder);
    if (Array.isArray(files) && files.length > 0) {
      const serialized = Array.isArray(draftAttachmentsRef.current)
        ? draftAttachmentsRef.current
        : [];
      if (!serialized.length || serialized.length < files.length) {
        try {
          await syncDraftAttachmentsFromFiles(attachmentsFilesRef.current || []);
        } catch (_) {
          /* ignore */
        }
      }
    }
  };

  const setComposerAttachments = (nextOrUpdater) => {
    setAttachments((prev) => {
      const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
      const list = Array.isArray(next) ? next : [];
      attachmentsFilesRef.current = list;
      composerDraftDirtyRef.current = true;
      // Sofort speichern, sobald Base64 fertig — kein Extra-Delay
      void syncDraftAttachmentsFromFiles(list).then(() => {
        if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
        void persistActiveComposerDraft(null, {
          force: true,
          clearAttachments: list.length === 0,
        });
      });
      return list;
    });
  };

  const resolveComposerFilesForSend = async () => {
    await ensureDraftAttachmentsReady();
    const current = attachmentsFilesRef.current || [];
    const hasPlaceholder = current.some((f) => f?.__draftPlaceholder || (Number(f?.size) || 0) === 0);
    if (!hasPlaceholder && current.length) return current.filter(Boolean);

    const serialized = Array.isArray(draftAttachmentsRef.current)
      ? draftAttachmentsRef.current
      : [];
    if (serialized.length) {
      const files = await draftAttachmentsToFiles(serialized);
      if (files.length) {
        attachmentsFilesRef.current = files;
        setAttachments(files);
        return files;
      }
    }
    return current.filter((f) => f && !f.__draftPlaceholder);
  };

  const clearComposerAttachments = () => {
    draftAttachmentsRef.current = [];
    attachmentsFilesRef.current = [];
    draftAttachmentsSyncRef.current = Promise.resolve([]);
    setAttachments([]);
  };

  const persistActiveComposerDraft = async (payloadOverride = null, options = {}) => {
    const force = Boolean(options?.force);
    if (!payloadOverride) {
      await ensureDraftAttachmentsReady();
    }
    let payload = payloadOverride || buildComposerDraftPayload();
    // Niemals einen bestehenden Entwurf ohne Anhänge überschreiben, solange noch Dateien im Composer sind
    if (
      !payloadOverride &&
      attachmentsFilesRef.current?.length &&
      !(Array.isArray(payload.attachments) && payload.attachments.length)
    ) {
      await ensureDraftAttachmentsReady();
      payload = {
        ...payload,
        attachments: Array.isArray(draftAttachmentsRef.current)
          ? draftAttachmentsRef.current
          : [],
      };
    }
    if (!composerDraftHasContent(payload)) {
      const id = composerDraftIdRef.current || String(payload?.id || '').trim() || null;
      if (id) {
        void removeComposerDraft(authMailbox, id).then((list) => setComposerDrafts(list));
        setComposerDrafts(listComposerDrafts(authMailbox));
        setComposerDraftId(null);
        composerDraftIdRef.current = null;
      }
      setDraftSavedAt(null);
      composerDraftDirtyRef.current = false;
      return null;
    }

    const id =
      String(payload?.id || '').trim() ||
      composerDraftIdRef.current ||
      makeComposerDraftId();
    if (isComposerDraftDeleted(authMailbox, id)) {
      composerDraftDirtyRef.current = false;
      if (String(composerDraftIdRef.current) === String(id)) {
        setComposerDraftId(null);
        composerDraftIdRef.current = null;
        setDraftSavedAt(null);
      }
      return null;
    }
    const existing =
      listComposerDrafts(authMailbox).find((d) => String(d.id) === String(id)) || null;

    // Bloßes Öffnen / unveränderter Inhalt → Zeitstempel nicht anfassen
    if (existing && composerDraftsContentEqual(existing, { ...payload, id })) {
      composerDraftDirtyRef.current = false;
      if (!composerDraftIdRef.current) {
        setComposerDraftId(id);
        composerDraftIdRef.current = id;
      }
      return id;
    }
    if (!force && !composerDraftDirtyRef.current) {
      if (!composerDraftIdRef.current && existing) {
        setComposerDraftId(id);
        composerDraftIdRef.current = id;
      }
      return composerDraftIdRef.current || (existing ? id : null);
    }

    setComposerDraftId(id);
    composerDraftIdRef.current = id;
    setDraftSavedAt(new Date());
    composerDraftDirtyRef.current = false;
    const toSave = {
      ...payload,
      id,
      clearAttachments: options.clearAttachments === true,
    };
    void upsertComposerDraft(authMailbox, toSave).then((list) => {
      setComposerDrafts(list);
    });
    setComposerDrafts(listComposerDrafts(authMailbox));
    return id;
  };

  const discardComposerDraftById = (id) => {
    const want = String(id || '').trim();
    if (!want) return;
    // Ausstehende Autosaves abbrechen — sonst schreibt ein spätes Save den Entwurf zurück
    if (draftSaveTimerRef.current) {
      window.clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    const wasActive = String(composerDraftIdRef.current) === want;
    void removeComposerDraft(authMailbox, want).then((list) => setComposerDrafts(list));
    setComposerDrafts(listComposerDrafts(authMailbox));
    if (wasActive) {
      setComposerDraftId(null);
      composerDraftIdRef.current = null;
      setDraftSavedAt(null);
      composerDraftDirtyRef.current = false;
      // Offenen Composer leeren, damit kein neuer Entwurf aus dem alten Inhalt entsteht
      if (showComposer) {
        clearComposerAttachments();
        setComposerQuoteHtml('');
        setComposerData({ to: '', cc: '', bcc: '', subject: '', html: '<p><br></p>', text: '' });
        setComposerTemplateId('');
        setComposerLockedHtml(null);
        composerHtmlRef.current = '<p><br></p>';
        setComposerEditorKey((k) => k + 1);
        setShowComposer(false);
      }
    }
  };

  useEffect(() => {
    emailsRef.current = emails;
  }, [emails]);

  useEffect(() => {
    inboxMetaRef.current = {
      total: inboxTotal,
      hasMore: inboxHasMore,
      oldestUid: inboxOldestUid,
      oldestSeq: inboxOldestSeq,
    };
  }, [inboxTotal, inboxHasMore, inboxOldestUid, inboxOldestSeq]);

  useEffect(() => {
    const folder = emailsFolderRef.current;
    if (!['inbox', 'spam', 'trash', 'important'].includes(folder)) return;
    const list = emails || [];
    // Auch 0 setzen — sonst bleibt die Sidebar-Zahl nach dem letzten Löschen hängen
    if (!list.length && !(inboxTotal > 0)) {
      setFolderCount(folder, 0);
      return;
    }
    const total = Math.max(0, Number(inboxTotal) || list.length);
    setFolderCount(folder, total);
  }, [emails, inboxTotal, setFolderCount]);

  useEffect(() => {
    sentEmailsRef.current = sentEmails;
  }, [sentEmails]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Sidebar-URL → aktiver Ordner
  useEffect(() => {
    const view = searchParams.get('view') || 'inbox';
    const category = searchParams.get('category');
    if (view === 'unread') {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('view');
      setSearchParams(nextParams, { replace: true });
      return;
    }
    const next =
      view === 'category' && category
        ? 'category'
        : ['inbox', 'important', 'sent', 'drafts', 'spam', 'trash', 'category'].includes(view)
          ? view
          : 'inbox';
    if (next !== activeTabRef.current) {
      setActiveTab(next);
      threadLoadIdRef.current += 1;
      setSelectedThreadId(null);
      setThreadMessages([]);
      setThreadDetails({});
      setExpandedThreadUid(null);
      setListExpandedThreadId(null);
      setSelectedInboxUid(null);
      setSelectedInboxEmail(null);
      setSelectedSentEmail(null);
      setShowComposer(false);
      setCategoryMenuOpen(false);
      setDetailMoreOpen(false);
      setSelectedMailUids(new Set());
      setSelectedSentKeys(new Set());
    }
  }, [searchParams, setSearchParams]);

  const activeCategoryId = searchParams.get('category');

  useEffect(() => {
    if (activeTab !== 'category' || !activeCategoryId) {
      setCategoryMessageIds(new Set());
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/mail-categories/${activeCategoryId}/items`, {
        params: { includeChildren: 1 },
      })
      .then((res) => {
        if (cancelled) return;
        const ids = new Set(
          (res.data?.items || []).map((i) => normalizeMessageId(i.messageId)).filter(Boolean)
        );
        setCategoryMessageIds(ids);
      })
      .catch(() => {
        if (!cancelled) setCategoryMessageIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, activeCategoryId]);

  useEffect(() => {
    const messageId = selectedInboxEmail?.messageId;
    if (!messageId) {
      setSelectedEmailCategories([]);
      return undefined;
    }
    let cancelled = false;
    axios
      .get('/api/mail-category-assignments', { params: { messageId } })
      .then((res) => {
        if (cancelled) return;
        setSelectedEmailCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
      })
      .catch(() => {
        if (!cancelled) setSelectedEmailCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedInboxEmail?.messageId, selectedInboxEmail?.uid]);

  useEffect(() => {
    bulkDeletingRef.current = bulkDeleting;
  }, [bulkDeleting]);

  useEffect(() => {
    if (!threadContextMenu) {
      threadContextMenuFetchGenRef.current += 1;
      return undefined;
    }
    const close = () => setThreadContextMenu(null);
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [threadContextMenu]);

  const openThreadContextMenu = (e, thread, message = null) => {
    e.preventDefault();
    e.stopPropagation();
    const menuW = 220;
    const catExtra = flatFolders.length > 0 && activeTab !== 'sent' ? 28 + flatFolders.length * 32 : 0;
    const menuH = 160 + catExtra;
    const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);

    const scopeEmails = message ? [message] : thread.emails || [];
    const messageIds = [
      ...new Set(
        scopeEmails
          .map((m) => normalizeMessageId(m.messageId))
          .filter(Boolean)
      ),
    ];

    const initialAssigned = new Set();
    if (activeCategoryId && messageIds.some((id) => categoryMessageIds.has(id))) {
      initialAssigned.add(String(activeCategoryId));
    }
    const selectedId = normalizeMessageId(selectedInboxEmail?.messageId);
    if (
      selectedId &&
      messageIds.includes(selectedId) &&
      Array.isArray(selectedEmailCategories)
    ) {
      selectedEmailCategories.forEach((c) => {
        if (c?.id != null) initialAssigned.add(String(c.id));
      });
    }

    const fetchGen = (threadContextMenuFetchGenRef.current += 1);
    const threadKey = thread.id;
    const messageKey = message?.uid != null ? String(message.uid) : null;
    setThreadContextMenu({
      x: Math.max(8, x),
      y: Math.max(8, y),
      thread,
      message: message || null,
      assignedCategoryIds: initialAssigned,
    });

    if (!messageIds.length || activeTab === 'sent') return;

    Promise.all(
      messageIds.slice(0, 12).map((messageId) =>
        axios
          .get('/api/mail-category-assignments', { params: { messageId } })
          .then((res) => (Array.isArray(res.data?.categories) ? res.data.categories : []))
          .catch(() => [])
      )
    ).then((results) => {
      if (fetchGen !== threadContextMenuFetchGenRef.current) return;
      const ids = new Set();
      results.flat().forEach((c) => {
        if (c?.id != null) ids.add(String(c.id));
      });
      setThreadContextMenu((prev) => {
        if (!prev || prev.thread?.id !== threadKey) return prev;
        const prevMsgKey = prev.message?.uid != null ? String(prev.message.uid) : null;
        if (messageKey !== prevMsgKey) return prev;
        return { ...prev, assignedCategoryIds: ids };
      });
    });
  };
  const checkImapConfig = async () => {
    try {
      const response = await axios.get('/api/imap-config');
      const configured = !!(response.data.host && response.data.enabled);
      const user = response.data.user || null;
      setImapConfigured(configured);
      setMailboxUser(user);
      if (configured) {
        // Nur Cache des aktuellen Postfachs laden — nie fremdes Konto
        const cacheMailbox = String(user || authMailbox || '').trim().toLowerCase();
        const cached = cacheMailbox ? readInboxLocalCache(cacheMailbox) : null;
        if (cached?.emails?.length) {
          // Cache nur nutzen, wenn er exakt zum angemeldeten Postfach gehört
          const authOk =
            !authMailbox ||
            String(cached.mailbox || cacheMailbox).toLowerCase() === String(authMailbox).toLowerCase();
          if (authOk) {
            emailsMailboxOwnerRef.current = cacheMailbox;
            const cachedEmails = filterInboxTombstones(cacheMailbox, cached.emails);
            setEmails(cachedEmails);
            setInboxTotal(Number(cached.total) || cachedEmails.length);
            setInboxHasMore(!!cached.hasMore);
            setInboxOldestUid(cached.oldestUid || null);
            setInboxOldestSeq(cached.oldestSeq || null);
            emailsFolderRef.current = 'inbox';
            emailsRef.current = cachedEmails;
            rememberFolderSession('inbox', {
              emails: cachedEmails,
              total: Number(cached.total) || cachedEmails.length,
              hasMore: !!cached.hasMore,
              oldestUid: cached.oldestUid || null,
              oldestSeq: cached.oldestSeq || null,
            });
            setInitialLoading(false);
            loadEmails(false, { silent: true, mailbox: user, force: true });
          } else {
            clearAllInboxLocalCaches();
            emailsMailboxOwnerRef.current = cacheMailbox;
            await loadEmails(false, { mailbox: user, force: true });
          }
        } else {
          emailsMailboxOwnerRef.current = String(user || authMailbox || '').trim().toLowerCase() || null;
          await loadEmails(false, { mailbox: user });
        }
      }
    } catch (error) {
      console.error('Fehler beim Prüfen der IMAP-Konfiguration:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const mergeInboxEmails = (prev, incoming, options = {}) => {
    const map = new Map();
    const incomingList = incoming || [];
    [...(prev || []), ...incomingList].forEach((mail) => {
      if (mail?.uid == null) return;
      map.set(String(mail.uid), mail);
    });

    // Gelöschte / fremde Mails entfernen
    if (options.prune && incomingList.length > 0) {
      const incomingUids = new Set(incomingList.map((e) => String(e.uid)));
      const nums = incomingList
        .map((e) => Number(e.uid))
        .filter((u) => Number.isFinite(u) && u > 0);
      if (nums.length) {
        const minUid = Math.min(...nums);
        const maxUid = Math.max(...nums);
        for (const [key, mail] of [...map.entries()]) {
          const n = Number(mail.uid);
          if (!Number.isFinite(n) || incomingUids.has(key)) continue;
          // Erste-Seite-Refresh: alles ab minUid, das nicht in der Antwort ist, raus
          // (verhindert, dass UIDs eines anderen Kontos in der Liste bleiben)
          if (options.replaceWindow) {
            if (n >= minUid) map.delete(key);
            continue;
          }
          if (n >= minUid && n <= maxUid) map.delete(key);
        }
      }
    }

    const merged = Array.from(map.values()).sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
    );
    const mailbox = String(options.mailbox || mailboxUser || authMailbox || '').trim().toLowerCase();
    return filterInboxTombstones(mailbox, merged);
  };

  const mergeSentEmails = (prev, incoming) => {
    const map = new Map();
    [...(prev || []), ...(incoming || [])].forEach((mail) => {
      if (!mail) return;
      const key = mail.uid != null ? `imap-${mail.uid}` : `local-${mail.id}`;
      map.set(key, mail);
    });
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.sent_at || 0) - new Date(a.sent_at || 0)
    );
  };

  const filterSentForMailbox = (list, owner) => {
    if (!owner) return list || [];
    return (list || []).filter((e) => {
      const from = String(e.from_email || '').trim().toLowerCase();
      if (!from) return e.source === 'imap';
      return from === owner;
    });
  };

  const loadEmails = async (showToast = false, options = {}) => {
    const silent = options.silent === true;
    const forceRefresh = options.force === true || showToast === true;
    const folder = options.folder || resolveMailFolder(activeTabRef.current);
    if (silent && fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;

    if (!silent) {
    setLoading(true);
    setDebugInfo(null);
    } else {
      setAutoRefreshing(true);
    }

    try {
      const response = await axios.get('/api/emails', {
        params: {
          limit: INBOX_PAGE_SIZE,
          folder,
          ...(forceRefresh ? { refresh: 1 } : {}),
          // Spam-Filter nur bei manueller Aktualisierung — nicht bei jedem Force/Tab-Wechsel
          ...(showToast && folder === 'inbox' ? { applySpam: 1 } : {}),
        },
        timeout: 45000,
      });
      
      if (response.data.success) {
        const nextEmails = response.data.emails || [];
        const responseFolder = String(response.data.folder || folder || 'inbox')
          .trim()
          .toLowerCase();
        // Spam/Papierkorb/Wichtig: mailbox ist IMAP-Ordnerpfad bzw. virtuell, nicht die Konto-E-Mail
        const isImapSpecialFolder = ['spam', 'junk', 'trash', 'papierkorb', 'important'].includes(
          responseFolder
        );
        const accountMailbox = String(
          options.mailbox || mailboxUser || authMailbox || ''
        )
          .trim()
          .toLowerCase();
        const responseMailbox = isImapSpecialFolder
          ? accountMailbox
          : String(
              response.data.mailbox || options.mailbox || mailboxUser || authMailbox || ''
            )
              .trim()
              .toLowerCase();
        // Server liefert Mails eines anderen Postfachs → verwerfen (nur INBOX)
        if (
          !isImapSpecialFolder &&
          responseMailbox &&
          accountMailbox &&
          responseMailbox !== accountMailbox
        ) {
          console.warn('Inbox-Antwort Postfach-Mismatch:', {
            expectedMailbox: accountMailbox,
            responseMailbox,
          });
          return;
        }
        if (responseMailbox) {
          emailsMailboxOwnerRef.current = responseMailbox;
        }
        if (isImapSpecialFolder && (response.data.imapBox || response.data.mailbox)) {
          setSpamMailboxPath(response.data.imapBox || response.data.mailbox);
        }
        if (response.data.spamMoved > 0 && showToast) {
          toast.success(`${response.data.spamMoved} Nachricht${response.data.spamMoved === 1 ? '' : 'en'} als Spam gefiltert`);
        }
        const total = Number(response.data.total) || nextEmails.length;
        const responseHasMore = !!response.data.hasMore;
        const responseOldest = response.data.oldestUid || null;
        const responseOldestSeq = response.data.oldestSeq || null;

        // Antwort gehört zu einem anderen Ordner als dem aktuell angezeigten
        const stillOnFolder =
          emailsFolderRef.current === folder ||
          resolveMailFolder(activeTabRef.current) === folder;

        if (!stillOnFolder) {
          rememberFolderSession(folder, {
            emails: nextEmails,
            total,
            hasMore: responseHasMore,
            oldestUid: responseOldest,
            oldestSeq: responseOldestSeq,
          });
          if (folder === 'inbox') {
            writeInboxLocalCache(responseMailbox || options.mailbox || mailboxUser || authMailbox, {
              emails: nextEmails,
              total,
              hasMore: responseHasMore,
              oldestUid: responseOldest,
              oldestSeq: responseOldestSeq,
            });
          }
          return;
        }

        const prev = emailsRef.current || [];
        const ownerMatches =
          !emailsMailboxOwnerRef.current ||
          !responseMailbox ||
          emailsMailboxOwnerRef.current === responseMailbox;
        // Nie mit Mails eines anderen Kontos mergen (UIDs überlappen sich sonst)
        const mergeMailbox = String(
          responseMailbox || options.mailbox || mailboxUser || authMailbox || ''
        ).trim().toLowerCase();
        const silentMerge = silent && prev.length > 0 && ownerMatches;
        const merged = silentMerge
          ? mergeInboxEmails(prev, nextEmails, {
              prune: true,
              replaceWindow: true,
              mailbox: mergeMailbox,
            })
          : filterInboxTombstones(mergeMailbox, nextEmails);

        const unchanged =
          silent &&
          prev.length === merged.length &&
          inboxListSignature(prev) === inboxListSignature(merged);

        if (silentMerge) {
          const prevUids = new Set(prev.map((e) => String(e.uid)));
          const newMails = nextEmails.filter((e) => e.uid != null && !prevUids.has(String(e.uid)));
          const newCount = newMails.length;
          if (newCount > 0 && !response.data.cached) {
            const newest = newMails[0] || {};
            toastNewMail({
              count: newCount,
              from: newest.from || '',
              subject: newest.subject || '',
            });
            refreshUnreadCount();
            if (folder === 'inbox' || responseFolder === 'inbox') {
              notifyNewMail({
                count: newCount,
                subject: newest.subject || '',
                from: newest.from || '',
                enabled: desktopNotificationsEnabledRef.current,
              }).catch(() => {});
            }
          }
        }

        if (!unchanged) {
          setEmails(merged);
          emailsFolderRef.current = folder;
          setInboxTotal(total);
          if (folder === 'inbox') {
            const flaggedCount = merged.filter(
              (e) =>
                Array.isArray(e.flags) &&
                e.flags.some((f) => f === '\\Flagged' || f === 'Flagged' || /flagged/i.test(String(f || '')))
            ).length;
            // Mindestens die geladenen Wichtig-Mails anzeigen (API kann 0 liefern)
            if (flaggedCount > 0) {
              setFolderCountAtLeast('important', flaggedCount);
            }
          }
          if (folder === 'spam' || folder === 'trash' || folder === 'important') {
            setFolderCount(folder, total);
          }
          if (silentMerge) {
            const uids = merged.map((e) => Number(e.uid)).filter((u) => Number.isFinite(u) && u > 0);
            const seqs = merged.map((e) => Number(e.seq)).filter((s) => Number.isFinite(s) && s > 0);
            setInboxOldestUid(uids.length ? Math.min(...uids) : null);
            setInboxOldestSeq(seqs.length ? Math.min(...seqs) : responseOldestSeq);
            setInboxHasMore(merged.length < total);
          } else {
            setInboxHasMore(responseHasMore);
            setInboxOldestUid(responseOldest);
            setInboxOldestSeq(responseOldestSeq);
          }
        setDebugInfo({
          success: true,
            count: merged.length,
            total,
            cached: !!response.data.cached,
            message: response.data.cached
              ? `${merged.length} von ${total} E-Mails (Cache)`
              : `${merged.length} von ${total} E-Mails geladen`,
          });
        }

        const snapshotHasMore = silentMerge ? merged.length < total : responseHasMore;
        const snapshotOldest = silentMerge
          ? (() => {
              const uids = merged.map((e) => Number(e.uid)).filter((u) => Number.isFinite(u) && u > 0);
              return uids.length ? Math.min(...uids) : null;
            })()
          : responseOldest;
        const snapshotOldestSeq = silentMerge
          ? (() => {
              const seqs = merged.map((e) => Number(e.seq)).filter((s) => Number.isFinite(s) && s > 0);
              return seqs.length ? Math.min(...seqs) : responseOldestSeq;
            })()
          : responseOldestSeq;

        // Nur INBOX in LocalStorage cachen – nie Spam/Papierkorb
        if (folder === 'inbox') {
          writeInboxLocalCache(options.mailbox || mailboxUser || authMailbox, {
            emails: merged,
            total,
            hasMore: snapshotHasMore,
            oldestUid: snapshotOldest,
            oldestSeq: snapshotOldestSeq,
          });
        }

        // Sitzungs-Cache für alle IMAP-Ordner
        rememberFolderSession(folder, {
          emails: merged,
          total,
          hasMore: snapshotHasMore,
          oldestUid: snapshotOldest,
          oldestSeq: snapshotOldestSeq,
        });

        // Unread-Badge hat eigenen Poll — nicht extra IMAP-Counts bei jedem Listen-Load
        if (showToast) refreshUnreadCount();

        if (showToast) {
          toast.success(
            total > merged.length
              ? `${merged.length} von ${total} E-Mails geladen`
              : `${merged.length} E-Mails geladen`
          );
        }
      } else {
        setDebugInfo({
          success: false,
          error: response.data.error
        });
        if (showToast) {
          toast.error('Fehler beim Laden der E-Mails');
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      setDebugInfo({
        success: false,
        error: errorMessage
      });
      if (showToast) {
        toast.error('Fehler beim Laden der E-Mails: ' + errorMessage);
      }
    } finally {
      fetchInFlightRef.current = false;
      if (!silent) setLoading(false);
      setAutoRefreshing(false);
      const fillFolder = options.folder || resolveMailFolder(activeTabRef.current);
      if (
        fillFolder === 'inbox' ||
        fillFolder === 'spam' ||
        fillFolder === 'trash' ||
        fillFolder === 'important'
      ) {
        window.setTimeout(() => {
          fillRemainingInbox(fillFolder);
        }, silent ? 400 : 120);
      }
    }
  };

  const loadMoreEmails = async ({
    quiet = false,
    beforeUid: beforeUidArg = null,
    beforeSeq: beforeSeqArg = null,
    folder: folderArg = null,
    limit: limitArg = null,
  } = {}) => {
    const folder = folderArg || resolveMailFolder(activeTabRef.current);
    const uidOnlyFolder = folder === 'important';
    const beforeSeq = uidOnlyFolder
      ? null
      : beforeSeqArg || inboxOldestSeq || inboxMetaRef.current.oldestSeq;
    const beforeUid = beforeUidArg || inboxOldestUid || inboxMetaRef.current.oldestUid;
    const totalHint = inboxMetaRef.current.total || inboxTotal || 0;
    const loadedCount = emailsRef.current?.length || 0;
    const apparentlyMore =
      !!inboxHasMore ||
      !!inboxMetaRef.current.hasMore ||
      (totalHint > 0 && loadedCount < totalHint);

    if ((!beforeSeq && !beforeUid) || fetchInFlightRef.current) {
      return { hasMore: false, oldestUid: null, oldestSeq: null, count: 0 };
    }
    if (!quiet && (loadingMore || inboxFillInFlightRef.current || !apparentlyMore)) {
      return { hasMore: false, oldestUid: null, oldestSeq: null, count: 0 };
    }

    if (!quiet) setLoadingMore(true);
    fetchInFlightRef.current = true;
    const pageLimit = Math.min(
      750,
      Math.max(1, parseInt(limitArg, 10) || (quiet ? INBOX_FILL_PAGE_SIZE : INBOX_PAGE_SIZE))
    );
    try {
      const response = await axios.get('/api/emails', {
        params: {
          limit: pageLimit,
          ...(beforeSeq ? { beforeSeq } : { beforeUid }),
          folder,
        },
        timeout: 60000,
      });
      if (!response.data?.success) {
        if (!quiet) {
          toast.error(response.data?.error || 'Ältere E-Mails konnten nicht geladen werden');
        }
        return { hasMore: false, oldestUid: null, oldestSeq: null, count: 0 };
      }
      const older = response.data.emails || [];
      setEmails((prev) => {
        const merged = mergeInboxEmails(prev, older, {
          mailbox: mailboxUser || authMailbox,
        });
        emailsRef.current = merged;
        const nextTotal = Number(response.data.total) || inboxMetaRef.current.total || merged.length;
        const nextHasMore =
          !!response.data.hasMore || (nextTotal > 0 && merged.length < nextTotal);
        rememberFolderSession(folder, {
          emails: merged,
          total: nextTotal,
          hasMore: nextHasMore,
          oldestUid: response.data.oldestUid || null,
          oldestSeq: response.data.oldestSeq || null,
        });
        if (folder === 'inbox') {
          writeInboxLocalCache(mailboxUser || authMailbox, {
            emails: merged,
            total: nextTotal,
            hasMore: nextHasMore,
            oldestUid: response.data.oldestUid || null,
            oldestSeq: response.data.oldestSeq || null,
          });
        }
        return merged;
      });
      const nextTotal = Number(response.data.total) || inboxTotal;
      setInboxTotal(nextTotal);
      const mergedLen = emailsRef.current?.length || 0;
      const nextHasMore =
        !!response.data.hasMore || (nextTotal > 0 && mergedLen < nextTotal);
      const nextOldest = response.data.oldestUid || null;
      const nextOldestSeq = response.data.oldestSeq || null;
      setInboxHasMore(nextHasMore);
      if (nextOldest) setInboxOldestUid(nextOldest);
      if (nextOldestSeq) setInboxOldestSeq(nextOldestSeq);
      inboxMetaRef.current = {
        total: nextTotal || inboxMetaRef.current.total || 0,
        hasMore: nextHasMore,
        oldestUid: nextOldest || inboxMetaRef.current.oldestUid,
        oldestSeq: nextOldestSeq || null,
      };
      if (!quiet && older.length) {
        toast.success(`${older.length} ältere E-Mails geladen`);
      }
      setDebugInfo((prev) => ({
        success: true,
        count: emailsRef.current?.length || 0,
        total: nextTotal || prev?.total || 0,
        message: `${emailsRef.current?.length || 0} E-Mails geladen`,
      }));
      return {
        hasMore: nextHasMore,
        oldestUid: nextOldest,
        oldestSeq: nextOldestSeq,
        count: older.length,
      };
    } catch (error) {
      if (!quiet) {
        toast.error(error.response?.data?.error || error.message || 'Fehler beim Nachladen');
      }
      return { hasMore: false, oldestUid: null, oldestSeq: null, count: 0 };
    } finally {
      fetchInFlightRef.current = false;
      if (!quiet) setLoadingMore(false);
    }
  };

  /** Seiten hintereinander nachladen, bis die Mailbox einmal komplett ist. */
  const fillRemainingInbox = async (folder = 'inbox') => {
    if (folder !== 'inbox' && folder !== 'spam' && folder !== 'trash' && folder !== 'important') {
      return;
    }
    if (inboxFillInFlightRef.current) return;

    const totalHint = inboxMetaRef.current.total || inboxTotal || 0;
    const loadedCount = emailsRef.current?.length || 0;
    const apparentlyMore =
      !!inboxMetaRef.current.hasMore || (totalHint > 0 && loadedCount < totalHint);
    if (!apparentlyMore) return;

    inboxFillInFlightRef.current = true;
    const generation = ++inboxFillGenerationRef.current;
    try {
      const uidOnly = folder === 'important';
      let nextSeq = uidOnly ? null : inboxMetaRef.current.oldestSeq;
      let nextUid = inboxMetaRef.current.oldestUid;
      let hasMore = apparentlyMore;
      let pages = 0;
      let emptyStreak = 0;

      while (hasMore && pages < 400) {
        if (inboxFillGenerationRef.current !== generation) break;
        if (!nextSeq && !nextUid) break;

        let wait = 0;
        while (fetchInFlightRef.current && wait < 40) {
          await new Promise((r) => setTimeout(r, 100));
          wait += 1;
        }
        if (inboxFillGenerationRef.current !== generation) break;

        pages += 1;
        const result = await loadMoreEmails({
          quiet: true,
          beforeSeq: uidOnly ? null : nextSeq || null,
          beforeUid: uidOnly || !nextSeq ? nextUid : null,
          folder,
          limit: INBOX_FILL_PAGE_SIZE,
        });

        hasMore = !!result.hasMore;
        if (!uidOnly) {
          if (result.oldestSeq) nextSeq = result.oldestSeq;
          else if (!result.hasMore) nextSeq = null;
        }
        if (result.oldestUid) nextUid = result.oldestUid;

        if (!result.count) {
          emptyStreak += 1;
          // Leere Seite: Cursor weiter nach unten (Seq), sonst abbrechen
          if (!uidOnly && nextSeq && nextSeq > 1) {
            nextSeq = Math.max(1, nextSeq - 1);
            continue;
          }
          if (emptyStreak >= 2) break;
          continue;
        }
        emptyStreak = 0;
      }
    } finally {
      if (inboxFillGenerationRef.current === generation) {
        inboxFillInFlightRef.current = false;
      }
    }
  };

  // Automatische Aktualisierung — IMAP frisch abfragen (nicht nur Server-Cache)
  useEffect(() => {
    if (!imapConfigured) return undefined;

    const refreshQuietly = () => {
      const bgPollOk =
        desktopNotificationsEnabledRef.current &&
        getDesktopNotificationPermission() === 'granted';
      if (document.hidden && !bgPollOk) return;
      if (bulkDeletingRef.current) return;
      if (fetchInFlightRef.current) return;
      const opts = { silent: true, force: true };
      if (activeTabRef.current === 'spam') {
        loadEmails(false, { ...opts, folder: 'spam' });
      } else if (activeTabRef.current === 'trash') {
        loadEmails(false, { ...opts, folder: 'trash' });
      } else if (activeTabRef.current === 'important') {
        loadEmails(false, { ...opts, folder: 'important' });
      } else if (isInboxMailbox(activeTabRef.current)) {
        loadEmails(false, { ...opts, folder: 'inbox' });
      } else if (activeTabRef.current === 'sent') {
        loadSentEmails({ silent: true });
      }
    };

    const intervalId = setInterval(refreshQuietly, AUTO_REFRESH_MS);

    const onVisibility = () => {
      if (!document.hidden) refreshQuietly();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Polling nur an IMAP-Status binden
  }, [imapConfigured]);

  const formatDate = (dateString) => formatMailDateTime(dateString);

  const normalizeContactEmail = (value) =>
    String(value || '').trim().toLowerCase();

  const inboxCounterpartEmail = useMemo(() => {
    if (!selectedInboxEmail) return '';
    return normalizeContactEmail(
      extractEmail(selectedInboxEmail.from) || selectedInboxEmail.from || ''
    );
  }, [selectedInboxEmail]);

  const showInboxSentHistory = preferences.showInboxSentHistory !== false;
  // Optimistic UI: Wenn der Button geklickt wird, soll die Timeline sofort auf/zu gehen,
  // auch wenn das Speichern der Preferences (API/Server) kurz verzögert oder fehlschlägt.
  const [inboxSentHistoryVisible, setInboxSentHistoryVisible] = useState(showInboxSentHistory);

  useEffect(() => {
    setInboxSentHistoryVisible(showInboxSentHistory);
  }, [showInboxSentHistory]);

  useEffect(() => {
    if (
      !inboxSentHistoryVisible ||
      !inboxCounterpartEmail ||
      activeTab === 'sent' ||
      !selectedInboxEmail
    ) {
      setInboxSentHistory([]);
      setInboxSentHistoryLoading(false);
      setInboxSentHistoryExpandedId(null);
      return undefined;
    }
    let cancelled = false;
    setInboxSentHistoryLoading(true);
    setInboxSentHistoryExpandedId(null);
    setInboxSentHistoryBodies({});
    axios
      .get('/api/sent-emails/to-recipient', {
        params: { email: inboxCounterpartEmail, limit: 20 },
        timeout: 20000,
      })
      .then((res) => {
        if (!cancelled) setInboxSentHistory(Array.isArray(res.data?.emails) ? res.data.emails : []);
      })
      .catch(() => {
        if (!cancelled) setInboxSentHistory([]);
      })
      .finally(() => {
        if (!cancelled) setInboxSentHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inboxSentHistoryVisible, inboxCounterpartEmail, selectedInboxEmail?.uid, activeTab]);

  const toggleInboxSentHistoryPref = async () => {
    const next = !inboxSentHistoryVisible;
    const restore = showInboxSentHistory;
    setInboxSentHistoryVisible(next);
    try {
      await saveMailPreferences({ ...preferences, showInboxSentHistory: next });
    } catch {
      // Server/Storage fehlgeschlagen: UI zurückrollen.
      setInboxSentHistoryVisible(restore);
      toast.error('Einstellung konnte nicht gespeichert werden');
    }
  };

  const toggleInboxSentHistoryItem = async (item) => {
    const id = item?.id;
    if (id == null) return;
    if (inboxSentHistoryExpandedId === id) {
      setInboxSentHistoryExpandedId(null);
      return;
    }
    setInboxSentHistoryExpandedId(id);
    if (inboxSentHistoryBodies[id] != null) return;
    try {
      const res = await axios.get(`/api/sent-email/${id}`, { timeout: 20000 });
      const email = res.data?.email || res.data || {};
      const html = email.html || '';
      const text = email.text || '';
      setInboxSentHistoryBodies((prev) => ({
        ...prev,
        [id]: { html, text, subject: email.subject || item.subject },
      }));
    } catch {
      setInboxSentHistoryBodies((prev) => ({
        ...prev,
        [id]: { html: '', text: 'Inhalt konnte nicht geladen werden.', subject: item.subject },
      }));
    }
  };

  /** Gesendet-Liste: nach Detail-Fetch kann id von lokalem DB-id auf imap-{uid} wechseln */
  const isSameSentListEmail = (a, b) => {
    if (!a || !b) return false;
    if (a === b) return true;
    if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true;
    if (a.uid != null && b.uid != null && String(a.uid) === String(b.uid)) return true;
    const aMid = normalizeMessageId(a.message_id || a.messageId);
    const bMid = normalizeMessageId(b.message_id || b.messageId);
    if (aMid && bMid && aMid === bMid) return true;
    return false;
  };

  const getSelectedSentThreadAddr = (email = selectedSentEmail) =>
    normalizeContactEmail(
      extractEmail(email?.to_email) ||
        email?.to_email ||
        extractEmail(email?.contact_email) ||
        email?.contact_email ||
        ''
    );

  const isKnownContactEmail = (emailAddr) => {
    const key = normalizeContactEmail(emailAddr);
    return !!(key && knownContactEmails.has(key));
  };

  const findContactByEmail = (emailAddr) => {
    const key = normalizeContactEmail(emailAddr);
    if (!key) return null;
    return contactsByEmail.get(key) || null;
  };

  const openContactDetailsFromEmail = async (emailAddr, fallback = {}) => {
    const key = normalizeContactEmail(emailAddr);
    const cached = findContactByEmail(key);
    if (!cached?.id) {
      toast.error('Kein Kontakt zu dieser E-Mail gefunden');
      return;
    }
    setThreadContextMenu(null);
    setViewedContactLoading(true);
    setViewedContact({
      id: cached.id,
      name: cached.name || fallback.name || '',
      email: cached.email || key,
      company: cached.company || fallback.company || '',
    });
    try {
      const res = await axios.get(`/api/contacts/${cached.id}`);
      setViewedContact(withNormalizedSubscription(res.data));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kontakt konnte nicht geladen werden');
      setViewedContact(null);
    } finally {
      setViewedContactLoading(false);
    }
  };

  const closeViewedContact = () => {
    setViewedContact(null);
    setViewedContactLoading(false);
  };

  const confirmDeleteViewedContact = async () => {
    const c = contactToDelete;
    if (!c?.id || deletingContact) return;
    setDeletingContact(true);
    try {
      await axios.delete(`/api/contacts/${c.id}`);
      toast.success('Kontakt gelöscht');
      const key = normalizeContactEmail(c.email);
      setKnownContactEmails((prev) => {
        const next = new Set(prev);
        if (key) next.delete(key);
        return next;
      });
      setContactsByEmail((prev) => {
        const next = new Map(prev);
        if (key) next.delete(key);
        return next;
      });
      setContactsForGender((prev) => prev.filter((x) => Number(x.id) !== Number(c.id)));
      setContactToDelete(null);
      closeViewedContact();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setDeletingContact(false);
    }
  };

  const buildContactDraftFromSender = (fromRaw, fallbackName = '', fallbackAddr = '') => {
    const email = normalizeContactEmail(extractEmail(fromRaw) || fallbackAddr);
    let name = String(extractName(fromRaw) || fallbackName || '').trim();
    if (!name || name.toLowerCase() === email) {
      const local = email.split('@')[0] || '';
      name = titleCaseWords(local.replace(/\d+/g, ' ')) || email;
    }

    let company = '';
    let website = '';
    const domain = email.includes('@') ? email.split('@')[1] : '';
    if (domain && !FREE_MAIL_DOMAINS.has(domain.toLowerCase())) {
      const base = domain.split('.')[0] || '';
      company = titleCaseWords(base);
      website = `https://${domain}`;
    }

    const gender = guessGenderFromName(name, contactsForGender);
    return {
      name,
      email,
      company,
      phone: '',
      website,
      notes: '',
      gender: gender || 'unknown',
      name2: '',
      gender2: 'unknown',
    };
  };

  const openCreateContactFromSender = (fromRaw, fallbackName = '', fallbackAddr = '') => {
    const draft = buildContactDraftFromSender(fromRaw, fallbackName, fallbackAddr);
    if (!draft.email) {
      toast.error('Keine Absender-E-Mail gefunden');
      return;
    }
    if (isKnownContactEmail(draft.email)) {
      toast.error('Dieser Absender ist bereits als Kontakt gespeichert');
      return;
    }
    setContactForm(draft);
    setContactGenderManual(false);
    setContactGender2Manual(false);
    setShowContactModal(true);
    setThreadContextMenu(null);
  };

  const handleContactNameChange = (value) => {
    setContactForm((prev) => {
      const next = { ...prev, name: value };
      if (!contactGenderManual) {
        next.gender = guessGenderFromName(value, contactsForGender);
      }
      return next;
    });
  };

  const handleContactGenderChange = (value) => {
    setContactGenderManual(true);
    setContactForm((prev) => ({ ...prev, gender: value }));
  };

  const handleContactName2Change = (value) => {
    setContactForm((prev) => {
      const next = { ...prev, name2: value };
      if (!String(value || '').trim()) {
        next.gender2 = 'unknown';
        return next;
      }
      if (!contactGender2Manual) {
        next.gender2 = guessGenderFromName(value, contactsForGender);
      }
      return next;
    });
  };

  const handleContactGender2Change = (value) => {
    setContactGender2Manual(true);
    setContactForm((prev) => ({ ...prev, gender2: value }));
  };

  const closeContactModal = () => {
    if (savingContact) return;
    setShowContactModal(false);
    setContactGenderManual(false);
    setContactGender2Manual(false);
  };

  const saveContactFromEmail = async (e) => {
    e?.preventDefault?.();
    const email = normalizeContactEmail(contactForm.email);
    const name = String(contactForm.name || '').trim();
    if (!name || !email) {
      toast.error('Name und E-Mail sind erforderlich');
      return;
    }
    if (isKnownContactEmail(email)) {
      toast.error('Dieser Absender ist bereits als Kontakt gespeichert');
      return;
    }
    try {
      setSavingContact(true);
      const res = await axios.post('/api/contacts', {
        name,
        email,
        company: contactForm.company || '',
        phone: contactForm.phone || '',
        website: contactForm.website || '',
        notes: contactForm.notes || '',
        gender: contactForm.gender || 'unknown',
        name2: contactForm.name2 || '',
        gender2: contactForm.gender2 || 'unknown',
      });
      const created = {
        id: Number(res.data?.id),
        name,
        email,
        company: contactForm.company || '',
        phone: contactForm.phone || '',
        website: contactForm.website || '',
        notes: contactForm.notes || '',
        gender: contactForm.gender || 'unknown',
        name2: contactForm.name2 || '',
        gender2: contactForm.gender2 || 'unknown',
      };
      setKnownContactEmails((prev) => new Set([...prev, email]));
      if (created.id) {
        setContactsByEmail((prev) => {
          const next = new Map(prev);
          next.set(email, created);
          return next;
        });
      }
      setContactsForGender((prev) => [...prev, created]);
      setShowContactModal(false);
      setContactGenderManual(false);
      setContactGender2Manual(false);
      toast.success('Kontakt angelegt');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Kontakt konnte nicht angelegt werden');
    } finally {
      setSavingContact(false);
    }
  };

  const getInitials = (nameOrEmail) => {
    if (!nameOrEmail) return '?';
    const base = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
    const parts = base.trim().split(/[\s._-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 2).toUpperCase();
  };

  const escapeHtml = (str) =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const plainTextToHtml = (text) => {
    if (!text) return '<p class="text-neutral-500">Keine Nachricht verfügbar</p>';
    const lines = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    let html = '';
    let inQuote = false;
    let blankRun = 0;

    lines.forEach((line) => {
      const isQuote = /^>/.test(line);
      const trimmed = line.trim();

      if (!trimmed) {
        blankRun += 1;
        if (blankRun === 1) {
          if (inQuote) {
            html += '</blockquote>';
            inQuote = false;
          }
          html += '<div class="email-gap"></div>';
        }
        return;
      }
      blankRun = 0;

      if (isQuote && !inQuote) {
        html += '<blockquote class="email-quote">';
        inQuote = true;
      } else if (!isQuote && inQuote) {
        html += '</blockquote>';
        inQuote = false;
      }

      const content = escapeHtml(isQuote ? line.replace(/^>\s?/, '') : line);
      html += `<div class="email-line">${content}</div>`;
    });

    if (inQuote) html += '</blockquote>';
    return html;
  };

  const filteredEmails = useMemo(() => {
    let list = emails;
    if (activeTab === 'unread') {
      list = list.filter((e) => !(e.flags && e.flags.includes('\\Seen')));
    } else if (activeTab === 'important') {
      list = list.filter((e) => isFlagged(e.flags));
    } else if (activeTab === 'category') {
      list = list.filter((e) => categoryMessageIds.has(normalizeMessageId(e.messageId)));
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (e) =>
          (e.subject || '').toLowerCase().includes(q) ||
          (e.from || '').toLowerCase().includes(q) ||
          (e.to || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [emails, searchTerm, activeTab, categoryMessageIds]);

  const activeCategoryChildren = useMemo(() => {
    if (activeTab !== 'category' || !activeCategoryId) return [];
    const folder = flatFolders.find((f) => String(f.id) === String(activeCategoryId));
    return Array.isArray(folder?.children) ? folder.children : [];
  }, [activeTab, activeCategoryId, flatFolders]);

  // Nachrichten je Kunde gruppieren, neueste Konversation oben
  const conversationThreads = useMemo(() => {
    const map = new Map();
    filteredEmails.forEach((email) => {
      const addr = (extractEmail(email.from) || email.from || 'unbekannt').toLowerCase().trim();
      if (!map.has(addr)) {
        map.set(addr, {
          id: addr,
          from: email.from,
          fromName: extractName(email.from) || extractEmail(email.from) || 'Unbekannt',
          fromAddr: extractEmail(email.from) || addr,
          emails: [],
          unreadCount: 0,
          importantCount: 0,
          hasAttachment: false,
        });
      }
      const thread = map.get(addr);
      thread.emails.push(email);
      if (!(email.flags && email.flags.includes('\\Seen'))) {
        thread.unreadCount += 1;
      }
      if (isFlagged(email.flags)) {
        thread.importantCount += 1;
      }
      if (emailHasAttachment(email)) {
        thread.hasAttachment = true;
      }
    });

    return Array.from(map.values())
      .map((thread) => {
        const sorted = [...thread.emails].sort(
          (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
        );
        return {
          ...thread,
          emails: sorted,
          latest: sorted[0],
          latestDate: sorted[0]?.date || null,
          count: sorted.length,
          isImportant: thread.importantCount > 0,
          hasAttachment:
            thread.hasAttachment || sorted.some((e) => emailHasAttachment(e)),
        };
      })
      .sort((a, b) => {
        if (threadSort === 'most') {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.latestDate || 0) - new Date(a.latestDate || 0);
        }
        if (threadSort === 'unread') {
          if ((b.unreadCount || 0) !== (a.unreadCount || 0)) {
            return (b.unreadCount || 0) - (a.unreadCount || 0);
          }
          return new Date(b.latestDate || 0) - new Date(a.latestDate || 0);
        }
        if (threadSort === 'oldest') {
          return new Date(a.latestDate || 0) - new Date(b.latestDate || 0);
        }
        return new Date(b.latestDate || 0) - new Date(a.latestDate || 0);
      });
  }, [filteredEmails, threadSort]);

  const visibleConversationThreads = useMemo(
    () => conversationThreads.slice(0, visibleThreadLimit),
    [conversationThreads, visibleThreadLimit]
  );

  // Gesendete Nachrichten je Empfänger gruppieren (wie Empfangen)
  const sentThreads = useMemo(() => {
    const map = new Map();
    const query = searchTerm.trim().toLowerCase();
    const pool = !query
      ? sentEmails
      : sentEmails.filter((email) => {
          const hay = `${email.to_email || ''} ${email.contact_email || ''} ${email.contact_name || ''} ${email.subject || ''}`.toLowerCase();
          return hay.includes(query);
        });

    pool.forEach((email) => {
      const addr = (email.to_email || email.contact_email || 'unbekannt').toLowerCase().trim();
      if (!map.has(addr)) {
        map.set(addr, {
          id: addr,
          toName: email.contact_name || email.to_email || email.contact_email || 'Unbekannt',
          toAddr: email.to_email || email.contact_email || addr,
          emails: []
        });
      }
      map.get(addr).emails.push(email);
    });

    return Array.from(map.values())
      .map((thread) => {
        const sorted = [...thread.emails].sort(
          (a, b) => new Date(b.sent_at || 0) - new Date(a.sent_at || 0)
        );
        return {
          ...thread,
          emails: sorted,
          latest: sorted[0],
          latestDate: sorted[0]?.sent_at || null,
          count: sorted.length,
          hasAttachment: sorted.some((e) => emailHasAttachment(e)),
        };
      })
      .sort((a, b) => {
        if (threadSort === 'most') {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.latestDate || 0) - new Date(a.latestDate || 0);
        }
        if (threadSort === 'oldest') {
          return new Date(a.latestDate || 0) - new Date(b.latestDate || 0);
        }
        return new Date(b.latestDate || 0) - new Date(a.latestDate || 0);
      });
  }, [sentEmails, searchTerm, threadSort]);

  const visibleSentThreads = useMemo(
    () => sentThreads.slice(0, visibleThreadLimit),
    [sentThreads, visibleThreadLimit]
  );

  useEffect(() => {
    setVisibleThreadLimit(THREADS_PAGE_SIZE);
  }, [searchTerm, threadSort, activeTab]);

  useEffect(() => {
    setVisibleMessageLimit(MESSAGES_PAGE_SIZE);
  }, [listExpandedThreadId]);

  // Sichtbaren Bereich erweitern, wenn Auswahl außerhalb der ersten Seite liegt
  useEffect(() => {
    if (!selectedThreadId || !isInboxMailbox(activeTab)) return;
    const idx = conversationThreads.findIndex((t) => t.id === selectedThreadId);
    if (idx >= visibleThreadLimit) {
      setVisibleThreadLimit(idx + 1);
    }
  }, [selectedThreadId, conversationThreads, visibleThreadLimit, activeTab]);

  useEffect(() => {
    if (!listExpandedThreadId || selectedInboxUid == null || !isInboxMailbox(activeTab)) return;
    const thread = conversationThreads.find((t) => t.id === listExpandedThreadId);
    if (!thread) return;
    const idx = thread.emails.findIndex((m) => String(m.uid) === String(selectedInboxUid));
    if (idx >= visibleMessageLimit) {
      setVisibleMessageLimit(idx + 1);
    }
  }, [listExpandedThreadId, selectedInboxUid, conversationThreads, visibleMessageLimit, activeTab]);

  useEffect(() => {
    if (!listExpandedThreadId || !selectedSentEmail || activeTab !== 'sent') return;
    const thread = sentThreads.find((t) => t.id === listExpandedThreadId);
    if (!thread) return;
    const idx = thread.emails.findIndex((m) => isSameSentListEmail(m, selectedSentEmail));
    if (idx >= visibleMessageLimit) {
      setVisibleMessageLimit(idx + 1);
    }
  }, [listExpandedThreadId, selectedSentEmail, sentThreads, visibleMessageLimit, activeTab]);

  const selectedThread = useMemo(
    () => conversationThreads.find((t) => t.id === selectedThreadId) || null,
    [conversationThreads, selectedThreadId]
  );

  const selectedSenderEmail = normalizeContactEmail(
    selectedThread?.fromAddr ||
      extractEmail(selectedInboxEmail?.from) ||
      selectedInboxEmail?.from ||
      ''
  );
  const canCreateContactFromSelected =
    isInboxMailbox(activeTab) &&
    !!selectedSenderEmail &&
    !isKnownContactEmail(selectedSenderEmail);
  const canOpenContactFromSelected =
    isInboxMailbox(activeTab) &&
    !!selectedSenderEmail &&
    isKnownContactEmail(selectedSenderEmail);

  const selectedSentRecipientEmail = normalizeContactEmail(
    extractEmail(selectedSentEmail?.to_email) ||
      selectedSentEmail?.to_email ||
      extractEmail(selectedSentEmail?.contact_email) ||
      selectedSentEmail?.contact_email ||
      ''
  );
  const canOpenContactFromSent =
    activeTab === 'sent' &&
    !!selectedSentRecipientEmail &&
    isKnownContactEmail(selectedSentRecipientEmail);
  const canCreateContactFromSent =
    activeTab === 'sent' &&
    !!selectedSentRecipientEmail &&
    !isKnownContactEmail(selectedSentRecipientEmail);

  // Offenen Verlauf bei Auto-Refresh aktualisieren
  useEffect(() => {
    if (!selectedThreadId) return;
    const thread = conversationThreads.find((t) => t.id === selectedThreadId);
    if (!thread) return;
    setThreadMessages(thread.emails);
  }, [conversationThreads, selectedThreadId]);

  const cancelPendingSend = async ({ silent = false } = {}) => {
    setSending(false);
    await cancelPending({ silent });
  };

  const closeComposer = ({ discardDraft = false, cancelSend } = {}) => {
    const shouldCancel = cancelSend != null ? cancelSend : !hasPendingSend;
    if (shouldCancel) {
      cancelPendingSend({ silent: true });
    }
    if (draftSaveTimerRef.current) {
      window.clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }

    // Vor dem Zurücksetzen: aktuellen Stand übernehmen oder leeren Entwurf verwerfen
    if (!hasPendingSend) {
      if (discardDraft) {
        const id = composerDraftIdRef.current;
        if (id) discardComposerDraftById(id);
        else {
          void clearComposerDraft(authMailbox).then(() => {
            setComposerDrafts(listComposerDrafts(authMailbox));
          });
        }
        setComposerDrafts(listComposerDrafts(authMailbox));
        clearComposerAttachments();
      } else {
        const draftId = composerDraftIdRef.current || makeComposerDraftId();
        const filesSnapshot = Array.isArray(attachmentsFilesRef.current)
          ? [...attachmentsFilesRef.current]
          : [];
        const syncPromise = draftAttachmentsSyncRef.current;
        const payloadBase = {
          mode: composerMode,
          to: composerData.to || '',
          cc: composerData.cc || '',
          bcc: composerData.bcc || '',
          subject: composerData.subject || '',
          html: getComposerHtml(),
          lockedHtml: composerLockedHtml || null,
          templateId: composerTemplateId || '',
          id: draftId,
          attachments: Array.isArray(draftAttachmentsRef.current)
            ? draftAttachmentsRef.current
            : [],
        };
        // Speichern mit Snapshot — UI darf sofort geleert werden
        void (async () => {
          let attachments = Array.isArray(payloadBase.attachments)
            ? payloadBase.attachments
            : [];
          try {
            const atts = await syncPromise;
            if (Array.isArray(atts) && atts.length) attachments = atts;
          } catch (_) {
            /* keep payloadBase attachments */
          }
          if (!attachments.length && filesSnapshot.length) {
            try {
              attachments = await filesToDraftAttachments(
                filesSnapshot.filter((f) => f && !f.__draftPlaceholder),
                attachments
              );
            } catch (_) {
              attachments = [];
            }
          }
          await persistActiveComposerDraft(
            { ...payloadBase, attachments },
            { force: true }
          );
        })();
        clearComposerAttachments();
      }
    } else {
      clearComposerAttachments();
    }

    setShowComposer(false);
    setComposerMode('new');
    setComposerSourceEmail(null);
    setShowCc(false);
    setShowBcc(false);
    setComposerQuoteHtml('');
    setComposerData({ to: '', cc: '', bcc: '', subject: '', html: '<p><br></p>', text: '' });
    setComposerTemplateId('');
    setComposerLockedHtml(null);
    setDraftSavedAt(null);
    // Draft-ID erst nach dem Flush-Schedule zurücksetzen — Persist nutzt snapshot.id
    setComposerDraftId(null);
    composerDraftIdRef.current = null;
    composerDraftDirtyRef.current = false;
  };

  const clearInboxSelection = () => {
    threadLoadIdRef.current += 1;
    setSelectedThreadId(null);
    setThreadMessages([]);
    setThreadDetails({});
    setExpandedThreadUid(null);
    setListExpandedThreadId(null);
    setSelectedInboxUid(null);
    setSelectedInboxEmail(null);
    setAllowExternalImages(allowImagesByDefault);
    setInboxDetailLoading(false);
    setCategoryMenuOpen(false);
    setDetailMoreOpen(false);
    if (showComposer && composerMode !== 'new') closeComposer();
  };

  const clearSentSelection = () => {
    setSelectedSentEmail(null);
    if (showComposer && composerMode !== 'new') closeComposer();
  };

  const loadThreadEmail = async (uid) => {
    const params = {};
    const folder = resolveMailFolder(activeTabRef.current);
    const detailFolder = resolveImapSourceFolder(folder);
    if (detailFolder !== 'inbox') params.folder = detailFolder;

    const cached = readEmailDetail(uid, detailFolder);
    if (cached) return cached;

    const response = await axios.get(`/api/emails/${uid}`, { params, timeout: 25000 });
    if (response.data.success && response.data.email) {
      rememberEmailDetail(uid, detailFolder, response.data.email);
      return response.data.email;
    }
    throw new Error(response.data?.error || 'E-Mail nicht gefunden');
  };

  const applyLocalSeen = (uid) => {
    const uidKey = String(uid);
    const folder = resolveImapSourceFolder(resolveMailFolder(activeTabRef.current));
    setEmails((prev) =>
      prev.map((e) =>
        String(e.uid) === uidKey ? { ...e, flags: withSeenFlag(e.flags) } : e
      )
    );
    setThreadMessages((prev) =>
      prev.map((e) =>
        String(e.uid) === uidKey ? { ...e, flags: withSeenFlag(e.flags) } : e
      )
    );
    setThreadDetails((prev) => {
      const existing = prev[uidKey];
      if (!existing) return prev;
      const next = { ...existing, flags: withSeenFlag(existing.flags) };
      rememberEmailDetail(uid, folder, next);
      return { ...prev, [uidKey]: next };
    });
    setSelectedInboxEmail((prev) =>
      prev && String(prev.uid) === uidKey
        ? { ...prev, flags: withSeenFlag(prev.flags) }
        : prev
    );
    const cachedDetail = readEmailDetail(uid, folder);
    if (cachedDetail) {
      rememberEmailDetail(uid, folder, { ...cachedDetail, flags: withSeenFlag(cachedDetail.flags) });
    }
    const current = (emailsRef.current || []).find((e) => String(e.uid) === uidKey);
    patchInboxLocalCacheFlags(uid, withSeenFlag(current?.flags), mailboxUser || authMailbox);
  };

  const markEmailAsRead = async (uid, currentFlags) => {
    if (uid == null) return;
    const uidKey = String(uid);
    const flags = currentFlags || emailsRef.current.find((e) => String(e.uid) === uidKey)?.flags;
    if (flags && flags.includes('\\Seen')) return;
    if (markReadInFlightRef.current.has(uidKey)) return;

    markReadInFlightRef.current.add(uidKey);
    applyLocalSeen(uid);
    decrementUnread(1);

    try {
      await axios.post(`/api/emails/${uid}/read`, null, { timeout: 15000 });
      refreshUnreadCount();
    } catch (error) {
      console.error('Konnte E-Mail nicht als gelesen markieren:', error);
      refreshUnreadCount();
    } finally {
      markReadInFlightRef.current.delete(uidKey);
    }
  };

  const handleThreadClick = async (thread) => {
    if (!thread?.emails?.length) return;

    closeComposer();
    setSelectedSentEmail(null);
    setSelectedThreadId(thread.id);
    setListExpandedThreadId(thread.id);
    setThreadMessages(thread.emails);
    setAllowExternalImages(allowImagesByDefault);

    const folder = resolveImapSourceFolder(resolveMailFolder(activeTabRef.current));
    const cachedDetails = {};
    (thread.emails || []).forEach((mail) => {
      if (mail?.uid == null) return;
      const cached = readEmailDetail(mail.uid, folder);
      if (cached) cachedDetails[String(mail.uid)] = cached;
    });
    setThreadDetails(cachedDetails);

    const latest = thread.emails[0];
    const latestKey = latest?.uid != null ? String(latest.uid) : null;
    const cachedLatest = latestKey ? cachedDetails[latestKey] : null;

    setSelectedInboxUid(latest.uid || null);
    setExpandedThreadUid(latestKey);
    setSelectedInboxEmail(cachedLatest || null);
    setInboxDetailLoading(!cachedLatest);

    const loadId = ++threadLoadIdRef.current;

    try {
      // Neueste Nachricht zuerst laden (oder aus Sitzungs-Cache)
      const firstEmail = await loadThreadEmail(latest.uid);
      if (threadLoadIdRef.current !== loadId) return;

      setThreadDetails((prev) => ({ ...prev, [String(latest.uid)]: firstEmail }));
      setSelectedInboxEmail(firstEmail);
      setInboxDetailLoading(false);
      markEmailAsRead(latest.uid, firstEmail.flags || latest.flags);
    } catch (error) {
      if (threadLoadIdRef.current !== loadId) return;
      if (!cachedLatest) {
        toast.error(error.response?.data?.error || error.message || 'Fehler beim Laden');
        clearInboxSelection();
      }
    } finally {
      if (threadLoadIdRef.current === loadId) {
        setInboxDetailLoading(false);
      }
    }
  };

  const toggleListThread = (thread) => {
    if (!thread?.emails?.length) return;
    // Erneuter Klick auf geöffnete Konversation → Lesebereich schließen
    if (selectedThreadId === thread.id) {
      clearInboxSelection();
      return;
    }
    handleThreadClick(thread);
  };

  const handleListMessageClick = async (thread, header, event) => {
    if (event) event.stopPropagation();
    if (!thread?.emails?.length || !header?.uid) return;

    // Erneuter Klick auf die bereits geöffnete Nachricht → schließen
    if (
      selectedThreadId === thread.id &&
      String(selectedInboxUid) === String(header.uid)
    ) {
      clearInboxSelection();
      return;
    }

    closeComposer();
    setSelectedSentEmail(null);
    setSelectedThreadId(thread.id);
    setListExpandedThreadId(thread.id);
    setThreadMessages(thread.emails);
    setAllowExternalImages(allowImagesByDefault);

    const uidKey = String(header.uid);
    const existing = threadDetails[uidKey];
    setSelectedInboxUid(header.uid);
    setExpandedThreadUid(uidKey);

    if (existing) {
      setSelectedInboxEmail(existing);
      setInboxDetailLoading(false);
      markEmailAsRead(header.uid, existing.flags || header.flags);
      return;
    }

    // Andere Thread-Auswahl: Inhalt leeren und gezielt laden
    if (selectedThreadId !== thread.id || selectedInboxUid !== header.uid) {
      setSelectedInboxEmail(null);
      setInboxDetailLoading(true);
    }

    const loadId = ++threadLoadIdRef.current;
    try {
      const detail = await loadThreadEmail(header.uid);
      if (threadLoadIdRef.current !== loadId) return;
      setThreadDetails((prev) => ({ ...prev, [uidKey]: detail }));
      setSelectedInboxEmail(detail);
      markEmailAsRead(header.uid, detail.flags || header.flags);
    } catch (error) {
      if (threadLoadIdRef.current !== loadId) return;
      toast.error(error.response?.data?.error || 'Nachricht konnte nicht geladen werden');
    } finally {
      if (threadLoadIdRef.current === loadId) {
        setInboxDetailLoading(false);
      }
    }
  };

  const handleSentClick = async (email) => {
    closeComposer();
    threadLoadIdRef.current += 1;
    setSelectedThreadId(null);
    setThreadMessages([]);
    setThreadDetails({});
    setExpandedThreadUid(null);
    setSelectedInboxUid(null);
    setSelectedInboxEmail(null);
    setAllowExternalImages(allowImagesByDefault);
    setInboxDetailLoading(true);
    setSelectedSentEmail({ ...email, attachments: email.attachments || [] });

    try {
      const isImap = email.source === 'imap' || (email.uid != null && String(email.id).startsWith('imap-'));
      if (isImap && email.uid != null) {
        const res = await axios.get(`/api/sent-emails/imap/${email.uid}`, {
          params: email.mailbox ? { mailbox: email.mailbox } : {},
          timeout: 30000,
        });
        if (res.data?.success && res.data.email) {
          setSelectedSentEmail((prev) => ({
            ...(prev || email),
            ...res.data.email,
            // Listen-Identität behalten (Highlight nach Detail-Load)
            id: prev?.id ?? email.id ?? res.data.email.id,
            uid: prev?.uid ?? email.uid ?? res.data.email.uid,
            source: prev?.source || email.source || res.data.email.source || 'imap',
            mailbox: prev?.mailbox || email.mailbox || res.data.email.mailbox,
            contact_name: prev?.contact_name || email.contact_name || res.data.email.contact_name,
            contact_email: prev?.contact_email || email.contact_email || res.data.email.contact_email,
            to_email: prev?.to_email || email.to_email || res.data.email.to_email,
            opened: prev?.opened ?? email.opened,
            opened_at: prev?.opened_at ?? email.opened_at,
            open_count: prev?.open_count ?? email.open_count,
            tracking_token: prev?.tracking_token ?? email.tracking_token,
          }));
        }
      } else if (email.id && !String(email.id).startsWith('imap-')) {
        const res = await axios.get(`/api/sent-email/${email.id}`);
        if (res.data?.success && res.data.email) {
          setSelectedSentEmail((prev) => ({
            ...(prev || email),
            ...res.data.email,
            id: prev?.id ?? email.id ?? res.data.email.id,
            source: 'local',
            contact_name: prev?.contact_name || email.contact_name,
            contact_email: prev?.contact_email || email.contact_email,
            to_email: prev?.to_email || email.to_email || res.data.email.to_email,
          }));
        }
      }
    } catch (_) {
      // Liste bleibt sichtbar, Anhänge ggf. ohne Detail
    } finally {
      setInboxDetailLoading(false);
    }
  };

  const toggleListSentThread = (thread) => {
    if (!thread?.emails?.length) return;
    const selectedAddr = getSelectedSentThreadAddr();
    // Erneuter Klick auf geöffnete Gesendet-Konversation → schließen
    if (selectedAddr === thread.id && selectedSentEmail) {
      clearSentSelection();
      setListExpandedThreadId(null);
      return;
    }
    setListExpandedThreadId(thread.id);
    handleSentClick(thread.latest || thread.emails[0]);
  };

  const handleListSentMessageClick = (thread, email, event) => {
    if (event) event.stopPropagation();
    if (isSameSentListEmail(selectedSentEmail, email)) {
      clearSentSelection();
      setListExpandedThreadId(null);
      return;
    }
    if (listExpandedThreadId !== thread.id) {
      setListExpandedThreadId(thread.id);
    }
    handleSentClick(email);
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isPreviewableAttachment = () => true;

  const getInboxAttachmentUrl = (uid, att, inline = false) => {
    const idx = att?.index ?? 0;
    const folder = resolveImapSourceFolder(resolveMailFolder(activeTabRef.current));
    const params = new URLSearchParams();
    if (inline) params.set('inline', '1');
    if (folder && folder !== 'inbox') params.set('folder', folder);
    const q = params.toString();
    return `/api/emails/${uid}/attachments/${idx}${q ? `?${q}` : ''}`;
  };

  const downloadAttachmentFile = async (url, filename) => {
    try {
      await downloadAuthenticated(url, filename || 'download');
    } catch (err) {
      toast.error(err.message || 'Download fehlgeschlagen');
    }
  };

  const getSentAttachmentUrl = (att, inline = false, email = selectedSentEmail) => {
    if (att?.id && !String(att.id).startsWith('imap') && email?.source !== 'imap') {
      return `/api/attachment/${att.id}${inline ? '?inline=1' : ''}`;
    }
    const uid = email?.uid;
    const mailbox = email?.mailbox ? `&mailbox=${encodeURIComponent(email.mailbox)}` : '';
    const idx = att?.index ?? 0;
    return `/api/sent-emails/imap/${uid}/attachments/${idx}?${inline ? 'inline=1' : 'x=1'}${mailbox}`;
  };

  const openAttachment = (url, att) => {
    const cleanDownload = url.replace(/([?&])inline=1&?/, '$1').replace(/[?&]$/, '');
    setPreviewAttachment({
      url: url.includes('inline=1') ? url : `${url}${url.includes('?') ? '&' : '?'}inline=1`,
      filename: att.filename || 'Anhang',
      contentType: att.contentType || att.content_type || 'application/octet-stream',
      downloadUrl: cleanDownload || url
    });
  };

  const openComposerAttachmentPreview = (file) => {
    if (!file) return;
    setPreviewAttachment({
      file,
      filename: file.name || 'Anhang',
      contentType: file.type || 'application/octet-stream',
    });
  };

  const switchTab = (tab, opts = {}) => {
    const params = new URLSearchParams();
    if (tab && tab !== 'inbox') params.set('view', tab);
    if (tab === 'category' && opts.category) params.set('category', String(opts.category));
    const qs = params.toString();
    navigate(qs ? `/inbox?${qs}` : '/inbox');
    setActiveTab(tab);
    closeComposer();
    threadLoadIdRef.current += 1;
    setSelectedThreadId(null);
    setThreadMessages([]);
    setThreadDetails({});
    setExpandedThreadUid(null);
    setListExpandedThreadId(null);
    setSelectedInboxUid(null);
    setSelectedInboxEmail(null);
    setSelectedSentEmail(null);
    setSearchTerm('');
    setVisibleThreadLimit(THREADS_PAGE_SIZE);
    setCategoryMenuOpen(false);
    // Laden übernimmt der activeTab-Effect inkl. Sitzungs-Cache
  };

  const applyFlagLocally = (uid, flagged) => {
    const uidKey = String(uid);
    const patch = (e) =>
      String(e.uid) === uidKey ? { ...e, flags: withFlaggedFlag(e.flags, flagged) } : e;

    setEmails((prev) => prev.map(patch));
    emailsRef.current = emailsRef.current.map(patch);
    setThreadDetails((prev) => {
      const existing = prev[uidKey];
      if (!existing) return prev;
      return { ...prev, [uidKey]: { ...existing, flags: withFlaggedFlag(existing.flags, flagged) } };
    });
    setSelectedInboxEmail((prev) =>
      prev && String(prev.uid) === uidKey
        ? { ...prev, flags: withFlaggedFlag(prev.flags, flagged) }
        : prev
    );
    const current = emailsRef.current.find((e) => String(e.uid) === uidKey);
    if (current) patchInboxLocalCacheFlags(uid, current.flags, mailboxUser || authMailbox);
  };

  const toggleEmailImportant = async (email, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!email?.uid || flagBusyUid) return;
    const uidKey = String(email.uid);
    const nextFlagged = !isFlagged(email.flags);
    setFlagBusyUid(uidKey);
    applyFlagLocally(email.uid, nextFlagged);
    adjustFolderCount('important', nextFlagged ? 1 : -1);
    invalidateFolderSession('important');
    try {
      await axios.post(`/api/emails/${email.uid}/flag`, { flagged: nextFlagged });
      toast.success(nextFlagged ? 'Als wichtig markiert' : 'Wichtig-Markierung entfernt');
    } catch (error) {
      applyFlagLocally(email.uid, !nextFlagged);
      adjustFolderCount('important', nextFlagged ? -1 : 1);
      toast.error(error.response?.data?.error || error.message || 'Markierung fehlgeschlagen');
    } finally {
      setFlagBusyUid(null);
    }
  };

  const toggleThreadImportant = async (thread, event) => {
    event?.stopPropagation?.();
    const target =
      thread?.emails?.find((e) => String(e.uid) === String(selectedInboxUid)) ||
      thread?.latest ||
      thread?.emails?.[0];
    if (!target) return;
    await toggleEmailImportant(target, event);
    setThreadContextMenu(null);
  };

  const openReplyComposer = (mode, email) => {
    if (!email) return;
    const fromAddr = extractEmail(email.from);
    const shouldQuote = mode === 'forward' ? true : preferences.replyQuoteOriginal;
    const quoteHtml = shouldQuote
      ? buildOriginalQuoteHtml(email, {
          formatDate,
          mode: mode === 'forward' ? 'forward' : 'reply',
        })
      : '';

    if (mode === 'reply') {
      setComposerData({
        to: fromAddr,
        cc: '',
        bcc: '',
        subject: email.subject?.startsWith('Re: ') ? email.subject : `Re: ${email.subject || ''}`,
        html: '<p><br></p>',
        text: ''
      });
      setShowCc(false);
    } else if (mode === 'replyAll') {
      setComposerData({
        to: fromAddr,
        cc: email.cc || '',
        bcc: '',
        subject: email.subject?.startsWith('Re: ') ? email.subject : `Re: ${email.subject || ''}`,
        html: '<p><br></p>',
        text: ''
      });
      setShowCc(!!email.cc);
    } else if (mode === 'forward') {
      setComposerData({
        to: '',
        cc: '',
        bcc: '',
        subject: email.subject?.startsWith('Fwd: ') ? email.subject : `Fwd: ${email.subject || ''}`,
        html: '<p><br></p>',
        text: ''
      });
      setShowCc(false);
    }

    setComposerQuoteHtml(quoteHtml);
    setComposerMode(mode);
    setComposerSourceEmail(email);
    clearComposerAttachments();
    setComposerTemplateId('');
    setComposerLockedHtml(null);
    composerHtmlRef.current = '<p><br></p>';
    setComposerEditorKey((k) => k + 1);
    setComposerDraftId(null);
    composerDraftIdRef.current = null;
    setDraftSavedAt(null);
    composerDraftDirtyRef.current = false;
    setShowComposer(true);
    loadMailTemplates();
  };

  const handleAiReplyQuick = async () => {
    const source = composerSourceEmail || selectedInboxEmail;
    if (!source || aiReplyLoading) return;
    setAiReplyLoading(true);
    try {
      if (!showComposer || (composerMode !== 'reply' && composerMode !== 'replyAll')) {
        openReplyComposer('reply', source);
        await new Promise((resolve) => {
          window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
        });
      }
      const incoming = buildIncomingEmailPayload(source);
      const text = await requestEmailAssist({ mode: 'draft_reply', incoming });
      applyHtmlToComposer({
        html: aiPlainTextToQuillHtml(text),
        composerHtmlRef,
        setComposerData,
        setComposerEditorKey,
      });
      composerDraftDirtyRef.current = true;
      toast.success('Antwortentwurf eingefügt — bitte prüfen und senden');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'KI-Antwort fehlgeschlagen');
    } finally {
      setAiReplyLoading(false);
    }
  };

  const flushComposerDraftIfNeeded = () => {
    if (!showComposer) return;
    const draftId = composerDraftIdRef.current || makeComposerDraftId();
    void (async () => {
      await ensureDraftAttachmentsReady();
      await persistActiveComposerDraft(
        { ...buildComposerDraftPayload(), id: draftId },
        { force: composerDraftDirtyRef.current || Boolean(draftAttachmentsRef.current?.length) }
      );
    })();
  };

  const openNewComposer = () => {
    flushComposerDraftIfNeeded();
    threadLoadIdRef.current += 1;
    setSelectedThreadId(null);
    setThreadMessages([]);
    setThreadDetails({});
    setExpandedThreadUid(null);
    setListExpandedThreadId(null);
    setSelectedInboxUid(null);
    setSelectedInboxEmail(null);
    setSelectedSentEmail(null);
    setAllowExternalImages(allowImagesByDefault);
    setInboxDetailLoading(false);
    setComposerMode('new');
    setComposerSourceEmail(null);
    const html = '<p><br></p>';
    setComposerData({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      html,
      text: '',
    });
    setComposerQuoteHtml('');
    clearComposerAttachments();
    setShowCc(false);
    setShowBcc(false);
    setComposerTemplateId('');
    setComposerLockedHtml(null);
    composerHtmlRef.current = html;
    setComposerEditorKey((k) => k + 1);
    setDraftSavedAt(null);
    setComposerDraftId(null);
    composerDraftIdRef.current = null;
    composerDraftDirtyRef.current = false;
    setShowComposer(true);
    loadMailTemplates();
  };

  const openComposerFromDraft = (draftInput = null) => {
    if (showComposer) flushComposerDraftIfNeeded();
    const draft =
      draftInput && typeof draftInput === 'object'
        ? draftInput
        : listComposerDrafts(authMailbox).find((d) => String(d.id) === String(draftInput)) ||
          listComposerDrafts(authMailbox)[0] ||
          null;
    if (!draft || !composerDraftHasContent(draft)) {
      if (draft?.id) discardComposerDraftById(draft.id);
      else refreshComposerDrafts();
      toast.error('Kein gespeicherter Entwurf vorhanden');
      return;
    }
    threadLoadIdRef.current += 1;
    setSelectedThreadId(null);
    setThreadMessages([]);
    setThreadDetails({});
    setExpandedThreadUid(null);
    setListExpandedThreadId(null);
    setSelectedInboxUid(null);
    setSelectedInboxEmail(null);
    setSelectedSentEmail(null);
    setAllowExternalImages(allowImagesByDefault);
    setInboxDetailLoading(false);
    setComposerMode(draft.mode === 'new' || !draft.mode ? 'new' : draft.mode);
    const locked =
      draft.lockedHtml &&
      (/data-email-design\s*=\s*["']1["']/i.test(draft.lockedHtml) ||
        /^<!DOCTYPE\s+html/i.test(String(draft.lockedHtml).trim()) ||
        /^<html[\s>]/i.test(String(draft.lockedHtml).trim()))
        ? draft.lockedHtml
        : null;
    const html = locked ? '<p><br></p>' : draft.html || '<p><br></p>';
    setComposerData({
      to: draft.to || '',
      cc: draft.cc || '',
      bcc: draft.bcc || '',
      subject: draft.subject || '',
      html,
      text: '',
    });
    setComposerQuoteHtml('');
    const attMeta = Array.isArray(draft.attachments) ? draft.attachments : [];
    draftAttachmentsRef.current = attMeta;
    // Sofort Namen anzeigen — Decode läuft im Hintergrund
    const placeholders = draftAttachmentsToPlaceholders(attMeta);
    attachmentsFilesRef.current = placeholders;
    draftAttachmentsSyncRef.current = Promise.resolve(attMeta);
    setAttachments(placeholders);
    setShowCc(!!draft.cc);
    setShowBcc(!!draft.bcc);
    setComposerTemplateId(draft.templateId ? String(draft.templateId) : '');
    setComposerLockedHtml(locked);
    composerHtmlRef.current = locked || html;
    setComposerEditorKey((k) => k + 1);
    setDraftSavedAt(draft.savedAt ? new Date(draft.savedAt) : new Date());
    setComposerDraftId(draft.id);
    composerDraftIdRef.current = draft.id;
    composerDraftDirtyRef.current = false;
    setShowComposer(true);

    if (attMeta.length) {
      const draftIdForLoad = draft.id;
      void draftAttachmentsToFiles(attMeta).then((files) => {
        if (String(composerDraftIdRef.current) !== String(draftIdForLoad)) return;
        if (!files.length) return;
        attachmentsFilesRef.current = files;
        setAttachments(files);
      });
    }
  };

  const requestDeleteComposerDraft = (draft, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!draft?.id) return;
    setDraftToDelete(draft);
  };

  const confirmDiscardComposerDraft = () => {
    const draft = draftToDelete;
    if (!draft?.id) {
      setDraftToDelete(null);
      return;
    }
    discardComposerDraftById(draft.id);
    setDraftToDelete(null);
    toast.success('Entwurf gelöscht');
  };

  // Reminder-Banner: zurück zur E-Mail (Composer mit Entwurf)
  useEffect(() => {
    if (!resumeNonce) return;
    openComposerFromDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei Reminder-Klick
  }, [resumeNonce]);

  useEffect(() => {
    const syncDrafts = () => refreshComposerDrafts();
    const clearDraftUi = (event) => {
      const removedId = event?.detail?.draftId;
      if (removedId && String(composerDraftIdRef.current) === String(removedId)) {
        setComposerDraftId(null);
        composerDraftIdRef.current = null;
        setDraftSavedAt(null);
      }
      refreshComposerDrafts();
    };
    window.addEventListener('focus', syncDrafts);
    window.addEventListener('storage', syncDrafts);
    window.addEventListener(COMPOSER_DRAFTS_CHANGED_EVENT, syncDrafts);
    window.addEventListener('odium:composer-draft-cleared', clearDraftUi);
    return () => {
      window.removeEventListener('focus', syncDrafts);
      window.removeEventListener('storage', syncDrafts);
      window.removeEventListener(COMPOSER_DRAFTS_CHANGED_EVENT, syncDrafts);
      window.removeEventListener('odium:composer-draft-cleared', clearDraftUi);
    };
  }, [refreshComposerDrafts]);

  useEffect(() => {
    const onSentRefresh = () => {
      loadSentEmails({ silent: true });
    };
    window.addEventListener('odium:sent-refresh', onSentRefresh);
    return () => window.removeEventListener('odium:sent-refresh', onSentRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Event-Listener einmalig
  }, []);

  const deleteInboxEmail = async (email, { skipConfirm = false } = {}) => {
    if (!email?.uid) return toast.error('UID fehlt');
    const listFolder = resolveMailFolder(activeTab);
    const fromFolder = resolveImapSourceFolder(listFolder);
    const permanent = fromFolder === 'trash';
    if (permanent && !skipConfirm) {
      setMailDeletePrompt({
        title: 'E-Mail endgültig löschen?',
        message: 'Diese E-Mail wird unwiderruflich aus dem Papierkorb entfernt.',
        confirmLabel: 'Endgültig löschen',
        run: () => deleteInboxEmail(email, { skipConfirm: true }),
      });
      return;
    }

    const deletedUid = String(email.uid);
    const deletedThreadId = (
      extractEmail(email.from) || email.from || selectedThreadId || ''
    ).toLowerCase().trim();
    const snapshot = { ...email };

    const remaining = threadMessages.filter((m) => String(m.uid) !== deletedUid);
    if (remaining.length === 0) {
      clearInboxSelection();
    } else {
      setThreadMessages(remaining);
      setThreadDetails((prev) => {
        const next = { ...prev };
        delete next[deletedUid];
        return next;
      });
      const nextLatest = remaining[0];
      const detail = threadDetails[String(nextLatest.uid)];
      setSelectedInboxUid(nextLatest.uid);
      setExpandedThreadUid(String(nextLatest.uid));
      setSelectedInboxEmail(detail || null);
    }

    setEmails((prev) => prev.filter((e) => String(e.uid) !== deletedUid));
    // emailsRef bewusst noch nicht filtern — purgeDeletedUidsFromCaches senkt sonst inboxTotal nicht
    purgeDeletedUidsFromCaches([deletedUid], { fromFolder: permanent ? 'trash' : fromFolder });
    applySidebarCountsForMailChange([snapshot], {
      fromFolder,
      toFolder: permanent ? null : 'trash',
      permanent,
      sourceCountSynced: true,
    });

    try {
      await axios.delete(`/api/emails/${deletedUid}`, {
        params: permanent ? { folder: 'trash', permanent: 1 } : { folder: fromFolder },
      });
      refreshUnreadCount();

      if (remaining.length === 0 && listExpandedThreadId === deletedThreadId) {
        window.setTimeout(() => {
          setListExpandedThreadId((cur) => (cur === deletedThreadId ? null : cur));
        }, 340);
      }

      if (permanent) {
        toast.success('Endgültig gelöscht');
        return;
      }

      const undoSecs = Math.min(60, Math.max(0, Number(preferences.undoDelaySeconds) || 0));
      toast.custom(
        (t) => (
          <div className="flex items-center gap-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg px-3 py-2.5 text-sm">
            <span className="text-neutral-700 dark:text-neutral-200">In den Papierkorb verschoben</span>
            {undoSecs > 0 ? (
              <button
                type="button"
                className="text-primary-600 dark:text-primary-400 font-semibold hover:underline"
                onClick={async () => {
                  toast.dismiss(t.id);
                  try {
                    await axios.post('/api/emails/move', {
                      uids: [snapshot.uid],
                      from: 'trash',
                      to: fromFolder === 'spam' ? 'spam' : 'inbox',
                    });
                    clearInboxTombstones(mailboxUser || authMailbox, [deletedUid]);
                    setEmails((prev) => {
                      if (prev.some((e) => String(e.uid) === deletedUid)) return prev;
                      return [snapshot, ...prev];
                    });
                    toast.success('Wiederhergestellt');
                    refreshUnreadCount();
                  } catch (err) {
                    toast.error(err.response?.data?.error || 'Wiederherstellen fehlgeschlagen');
                  }
                }}
              >
                Rückgängig
              </button>
            ) : null}
          </div>
        ),
        { duration: undoSecs > 0 ? undoSecs * 1000 : 3000 }
      );
    } catch (err) {
      clearInboxTombstones(mailboxUser || authMailbox, [deletedUid]);
      setEmails((prev) => {
        if (prev.some((e) => String(e.uid) === deletedUid)) return prev;
        return [snapshot, ...prev];
      });
      toast.error('Löschen fehlgeschlagen: ' + (err.response?.data?.error || err.message));
    }
  };

  const removeEmailsLocally = (uids) => {
    purgeDeletedUidsFromCaches(uids, {
      fromFolder: emailsFolderRef.current || 'inbox',
    });
    clearInboxSelection();
  };

  /** Sidebar-Badges sofort anpassen — IMAP folder-counts kommt oft erst Sekunden später. */
  const applySidebarCountsForMailChange = (
    removedEmails,
    {
      fromFolder = 'inbox',
      toFolder = null,
      permanent = false,
      sourceCountSynced = false,
    } = {}
  ) => {
    const list = (removedEmails || []).filter((e) => e && e.uid != null);
    if (!list.length) return;

    const unreadN = list.filter(
      (e) => !(Array.isArray(e.flags) && e.flags.includes('\\Seen'))
    ).length;
    if (unreadN > 0) {
      if (fromFolder === 'inbox' || fromFolder === 'important') {
        adjustFolderCount('unread', -unreadN);
      }
      if (toFolder === 'inbox') {
        adjustFolderCount('unread', unreadN);
      }
    }

    const flaggedN = list.filter((e) => isFlagged(e.flags)).length;
    if (flaggedN > 0) {
      if (
        (fromFolder === 'inbox' || fromFolder === 'important') &&
        emailsFolderRef.current !== 'important'
      ) {
        adjustFolderCount('important', -flaggedN);
      }
      if (toFolder === 'inbox' && fromFolder !== 'inbox' && fromFolder !== 'important') {
        adjustFolderCount('important', flaggedN);
      }
    }

    // Quell-Ordner: nur wenn nicht schon über inboxTotal/setFolderCount synchronisiert
    if (
      !sourceCountSynced &&
      !permanent &&
      ['inbox', 'spam', 'trash', 'important'].includes(fromFolder)
    ) {
      adjustFolderCount(fromFolder, -list.length);
    }

    if (!permanent && toFolder && ['inbox', 'spam', 'trash'].includes(toFolder)) {
      adjustFolderCount(toFolder, list.length);
    }
  };

  const beginMailLeave = (uids, kind = 'delete') => {
    const keys = [...new Set((uids || []).map((u) => String(u)).filter(Boolean))];
    if (!keys.length) return [];
    setLeavingKind(kind);
    setLeavingUids((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
    return keys;
  };

  const clearMailLeave = (keys = []) => {
    setLeavingUids((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      keys.forEach((k) => next.delete(String(k)));
      return next.size ? next : new Set();
    });
    setLeavingKind(null);
  };

  const markEmailsAsSpam = async (emailList = []) => {
    const list = (emailList || []).filter((e) => e?.uid != null);
    if (!list.length) return;
    setSpamMarkPrompt({ emails: list });
  };

  const unsubscribeFromEmail = async (email) => {
    if (!email?.uid || !email?.subscription?.canUnsubscribe) return;
    const from =
      extractName(email.from) || extractEmail(email.from) || email.from || 'diesem Absender';
    if (!window.confirm(`Von „${from}“ abmelden?`)) return;
    try {
      setUnsubscribing(true);
      const res = await axios.post(`/api/emails/${email.uid}/unsubscribe`, {
        mailbox: email.mailbox || 'INBOX',
      });
      if (res.data?.openUrl) {
        window.open(res.data.openUrl, '_blank', 'noopener,noreferrer');
        toast.success(
          res.data?.result?.needsBrowser
            ? 'Abmelde-Seite geöffnet — bitte dort bestätigen'
            : 'Abmeldung ausgelöst'
        );
      } else if (res.data?.result?.method === 'mailto') {
        toast.success('Abmelde-Mail wurde gesendet');
      } else {
        toast.success('Abmeldung ausgelöst');
      }
      setSelectedInboxEmail((prev) =>
        prev && String(prev.uid) === String(email.uid)
          ? {
              ...prev,
              subscription: prev.subscription
                ? { ...prev.subscription, canUnsubscribe: false }
                : prev.subscription,
            }
          : prev
      );
    } catch (err) {
      const openUrl = err.response?.data?.openUrl || email.subscription?.httpUrl;
      if (openUrl) {
        window.open(openUrl, '_blank', 'noopener,noreferrer');
        toast.error('Automatische Abmeldung fehlgeschlagen — Link geöffnet');
      } else {
        toast.error(err.response?.data?.error || 'Abmelden fehlgeschlagen');
      }
    } finally {
      setUnsubscribing(false);
    }
  };

  const confirmMarkEmailsAsSpam = async ({ withRule, field, pattern, extendRuleId, createNew }) => {
    const list = spamMarkPrompt?.emails || [];
    if (!list.length) {
      setSpamMarkPrompt(null);
      return;
    }
    const fromFolder = resolveImapSourceFolder(resolveMailFolder(activeTab));
    setSpamMarkPrompt(null);
    let leaveUids = [];

    try {
      setMovingSpam(true);

      let ruleNote = '';
      if (withRule && pattern) {
        const next = mergeSpamRuleIntoConfig(spamRules, {
          field,
          pattern,
          extendRuleId,
          createNew: !!createNew,
        });
        if (next && next.action !== 'exists') {
          await saveSpamRules({
            enabled: next.enabled !== false,
            version: next.version,
            rules: next.rules,
          });
          ruleNote = next.action === 'extended' ? ' · Regel erweitert' : ' · Regel gespeichert';
        } else if (next?.action === 'exists') {
          ruleNote = ' · Regel war schon vorhanden';
        }
      }

      // Zusätzlich: bereits geladene Mails, die zum Muster passen, mitnehmen
      const selectedKeys = new Set(list.map((e) => String(e.uid)));
      let moveList = [...list];
      if (withRule && pattern && fromFolder !== 'spam') {
        const extras = (emails || []).filter((e) => {
          if (e?.uid == null || selectedKeys.has(String(e.uid))) return false;
          return emailMatchesSpamPattern(e, { field, pattern });
        });
        if (extras.length) moveList = [...list, ...extras];
      }

      leaveUids = moveList.map((e) => e.uid).filter((u) => u != null);
      beginMailLeave(leaveUids, 'spam');

      await Promise.all([
        axios.post('/api/emails/move', {
          uids: leaveUids,
          from: fromFolder,
          to: 'spam',
          messageIds: moveList.map((e) => e.messageId).filter(Boolean),
        }),
        new Promise((resolve) => window.setTimeout(resolve, MAIL_LEAVE_MS)),
      ]);

      const removed = new Set(leaveUids.map(String));
      let serverExtra = 0;
      if (withRule && pattern && fromFolder === 'inbox') {
        try {
          const applyRes = await axios.post('/api/spam-rules/apply-inbox', { limit: 300 });
          const matched = Array.isArray(applyRes.data?.matchedUids)
            ? applyRes.data.matchedUids.map(String)
            : [];
          matched.forEach((u) => {
            if (!removed.has(u)) {
              removed.add(u);
              serverExtra += 1;
            }
          });
        } catch (_) {
          /* optional */
        }
      }

      invalidateFolderSession('spam');
      const removedList = (emailsRef.current || []).filter((e) => removed.has(String(e.uid)));
      // per Filter zusätzlich entfernte UIDs ohne lokales Objekt
      const missingUids = [...removed].filter(
        (u) => !removedList.some((e) => String(e.uid) === String(u))
      );
      removeEmailsLocally([...removed]);
      applySidebarCountsForMailChange(
        [
          ...removedList,
          ...missingUids.map((uid) => ({ uid, flags: ['\\Seen'] })),
        ],
        { fromFolder, toFolder: 'spam', sourceCountSynced: true }
      );
      setLeavingUids((prev) => {
        const next = new Set(prev);
        leaveUids.forEach((u) => next.delete(String(u)));
        return next.size ? next : new Set();
      });
      setLeavingKind(null);

      const movedCount = removed.size;
      toast.success(
        (movedCount === 1
          ? 'Als Spam markiert'
          : `${movedCount} als Spam markiert`) +
          ruleNote +
          (serverExtra > 0 ? ` · +${serverExtra} weitere per Filter` : '')
      );
      refreshUnreadCount();
    } catch (err) {
      clearMailLeave(leaveUids);
      setLeavingKind(null);
      toast.error(err.response?.data?.error || 'Verschieben nach Spam fehlgeschlagen');
    } finally {
      setMovingSpam(false);
    }
  };

  const markEmailsAsNotSpam = async (emailList = []) => {
    const list = (emailList || []).filter((e) => e?.uid != null);
    if (!list.length) return;
    try {
      setMovingSpam(true);
      await axios.post('/api/emails/move', {
        uids: list.map((e) => e.uid),
        from: 'spam',
        to: 'inbox',
        messageIds: list.map((e) => e.messageId).filter(Boolean),
      });
      invalidateFolderSession('inbox');
      removeEmailsLocally(list.map((e) => e.uid));
      applySidebarCountsForMailChange(list, {
        fromFolder: 'spam',
        toFolder: 'inbox',
        sourceCountSynced: true,
      });
      toast.success('Nicht mehr Spam — im Posteingang');
      refreshUnreadCount();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Zurück in den Posteingang fehlgeschlagen');
    } finally {
      setMovingSpam(false);
    }
  };

  const patchContextMenuCategory = (categoryId, assigned) => {
    const id = String(categoryId);
    setThreadContextMenu((prev) => {
      if (!prev) return prev;
      const next = new Set(
        [...(prev.assignedCategoryIds || [])].map(String)
      );
      if (assigned) next.add(id);
      else next.delete(id);
      return { ...prev, assignedCategoryIds: next };
    });
  };

  const assignEmailsToCategory = async (emailList, categoryId) => {
    const list = (emailList || []).filter((e) => e?.messageId);
    if (!list.length) {
      toast.error('Keine Message-ID — Zuordnung nicht möglich');
      return;
    }
    try {
      await Promise.all(
        list.map((email) =>
          axios.post(`/api/mail-categories/${categoryId}/items`, {
            messageId: email.messageId,
            subject: email.subject || '',
            from: email.from || '',
          })
        )
      );
      await refreshCategories();
      patchContextMenuCategory(categoryId, true);
      if (String(activeCategoryId) === String(categoryId)) {
        setCategoryMessageIds((prev) => {
          const next = new Set(prev);
          list.forEach((email) => {
            const id = normalizeMessageId(email.messageId);
            if (id) next.add(id);
          });
          return next;
        });
      }
      const selectedId = normalizeMessageId(selectedInboxEmail?.messageId);
      if (selectedId && list.some((e) => normalizeMessageId(e.messageId) === selectedId)) {
        const cat = categories.find((c) => String(c.id) === String(categoryId));
        if (cat) {
          setSelectedEmailCategories((prev) =>
            prev.some((c) => String(c.id) === String(categoryId))
              ? prev
              : [...prev, cat].sort((a, b) => String(a.name).localeCompare(String(b.name), 'de'))
          );
        }
      }
      const count = list.length;
      toast.success(
        count === 1
          ? 'Zum Ordner hinzugefügt'
          : `${count} E-Mails zum Ordner hinzugefügt`
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Zuordnung fehlgeschlagen');
    }
  };

  const removeEmailsFromCategory = async (emailList, categoryId) => {
    const list = (emailList || []).filter((e) => e?.messageId);
    if (!list.length) {
      toast.error('Keine Message-ID — Entfernen nicht möglich');
      return;
    }
    if (!categoryId) return;
    try {
      await Promise.all(
        list.map((email) =>
          axios.delete(`/api/mail-categories/${categoryId}/items`, {
            data: { messageId: email.messageId },
          })
        )
      );
      await refreshCategories();
      patchContextMenuCategory(categoryId, false);
      const removedIds = new Set(
        list.map((e) => normalizeMessageId(e.messageId)).filter(Boolean)
      );
      if (String(activeCategoryId) === String(categoryId)) {
        setCategoryMessageIds((prev) => {
          const next = new Set(prev);
          removedIds.forEach((id) => next.delete(id));
          return next;
        });
      }
      const selectedId = normalizeMessageId(selectedInboxEmail?.messageId);
      if (selectedId && removedIds.has(selectedId)) {
        setSelectedEmailCategories((prev) =>
          prev.filter((c) => String(c.id) !== String(categoryId))
        );
      }
      const count = list.length;
      toast.success(
        count === 1 ? 'Aus Ordner entfernt' : `${count} E-Mails aus Ordner entfernt`
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Entfernen fehlgeschlagen');
    }
  };

  const removeSelectedFromActiveCategory = async () => {
    if (!activeCategoryId || !selectedMailUids.size) return;
    const selected = (emailsRef.current || []).filter((e) =>
      selectedMailUids.has(String(e.uid))
    );
    await removeEmailsFromCategory(selected, activeCategoryId);
    clearMailSelection();
  };

  const assignSelectedToCategory = async (categoryId) => {
    const email = selectedInboxEmail;
    if (!email?.messageId) {
      toast.error('Keine Message-ID — Zuordnung nicht möglich');
      return;
    }
    const alreadyAssigned = selectedEmailCategories.some((c) => String(c.id) === String(categoryId));
    try {
      if (alreadyAssigned) {
        await removeEmailsFromCategory([email], categoryId);
      } else {
        await assignEmailsToCategory([email], categoryId);
      }
      setCategoryMenuOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Zuordnung fehlgeschlagen');
    }
  };

  const mailDropHandlerRef = useRef(null);
  mailDropHandlerRef.current = async (payload, target) => {
    const emails = payload?.emails || [];
    if (!emails.length || !target) return false;

    if (target.type === 'category') {
      await assignEmailsToCategory(emails, target.categoryId);
      return true;
    }

    if (target.type === 'important') {
      const list = emails.filter((e) => e?.uid != null);
      if (!list.length) return false;
      try {
        setFlagBusyUid(String(list[0].uid));
        await Promise.all(
          list.map((email) => axios.post(`/api/emails/${email.uid}/flag`, { flagged: true }))
        );
        list.forEach((email) => applyFlagLocally(email.uid, true));
        toast.success(
          list.length === 1 ? 'Als wichtig markiert' : `${list.length} als wichtig markiert`
        );
        return true;
      } catch (err) {
        toast.error(err.response?.data?.error || 'Wichtig-Markierung fehlgeschlagen');
        return false;
      } finally {
        setFlagBusyUid(null);
      }
    }

    if (target.type === 'folder') {
      const to = target.folder;
      if (!['inbox', 'spam', 'trash'].includes(to)) return false;
      const list = emails.filter((e) => e?.uid != null);
      if (!list.length) return false;
      const fromRaw = payload.fromFolder || 'inbox';
      const from = resolveImapSourceFolder(fromRaw);
      if (from === to) {
        toast('Bereits in diesem Ordner', { icon: 'ℹ️' });
        return false;
      }
      const labels = { inbox: 'Empfangen', spam: 'Spam', trash: 'Papierkorb' };
      const uids = list.map((e) => e.uid);
      const leaveKind = to === 'spam' ? 'spam' : 'delete';
      const sourceListFolder = fromRaw === emailsFolderRef.current ? fromRaw : null;
      try {
        setMovingSpam(true);
        if (sourceListFolder) {
          beginMailLeave(uids, leaveKind);
        }
        await Promise.all([
          axios.post('/api/emails/move', {
            uids,
            from,
            to,
            messageIds: list.map((e) => e.messageId).filter(Boolean),
          }),
          sourceListFolder
            ? new Promise((resolve) => window.setTimeout(resolve, MAIL_LEAVE_MS))
            : Promise.resolve(),
        ]);
        // Ziel-Ordner neu laden beim nächsten Öffnen; Quelle lokal anpassen
        invalidateFolderSession(to);
        if (sourceListFolder) {
          removeEmailsLocally(uids);
          applySidebarCountsForMailChange(list, {
            fromFolder: from,
            toFolder: to,
            sourceCountSynced: true,
          });
          clearMailLeave(uids);
        } else {
          invalidateFolderSession(fromRaw);
          if (from === 'inbox') invalidateFolderSession('important');
          applySidebarCountsForMailChange(list, {
            fromFolder: from,
            toFolder: to,
            sourceCountSynced: false,
          });
        }
        toast.success(
          list.length === 1
            ? `Nach ${labels[to] || to} verschoben`
            : `${list.length} nach ${labels[to] || to} verschoben`
        );
        refreshUnreadCount();
        return true;
      } catch (err) {
        clearMailLeave(uids);
        toast.error(err.response?.data?.error || 'Verschieben fehlgeschlagen');
        return false;
      } finally {
        setMovingSpam(false);
      }
    }
    return false;
  };

  useEffect(
    () => registerDropHandler((payload, target) => mailDropHandlerRef.current?.(payload, target)),
    [registerDropHandler]
  );

  const startMailDrag = useCallback((e, emails, meta = {}) => {
    const list = (emails || []).filter((em) => em?.uid != null);
    if (!list.length) {
      e.preventDefault();
      return;
    }

    const kind = meta.kind || (list.length > 1 ? 'thread' : 'message');
    const title = meta.title || meta.label || (list.length > 1 ? 'Konversation' : 'E-Mail');
    const subtitle =
      meta.subtitle ||
      (kind === 'thread'
        ? `${list.length} Nachricht${list.length === 1 ? '' : 'en'}`
        : list[0]?.subject || '(Kein Betreff)');
    const initials = meta.initials || getInitials(title);

    const ghost = document.createElement('div');
    ghost.className = 'mail-drag-ghost';
    ghost.innerHTML = `
      <div class="mail-drag-ghost__avatar">${escapeHtml(initials)}</div>
      <div class="mail-drag-ghost__text">
        <div class="mail-drag-ghost__title">${escapeHtml(title)}</div>
        <div class="mail-drag-ghost__sub">${escapeHtml(subtitle)}</div>
      </div>
      ${list.length > 1 ? `<div class="mail-drag-ghost__badge">${list.length}</div>` : ''}
    `;
    document.body.appendChild(ghost);
    setDragGhost(ghost);
    try {
      e.dataTransfer.setDragImage(ghost, 24, 24);
    } catch (_) {
      /* ignore */
    }

    beginDrag({
      emails: list.map((em) => ({
        uid: em.uid,
        messageId: em.messageId,
        subject: em.subject,
        from: em.from,
        flags: em.flags,
        date: em.date,
      })),
      fromFolder: resolveMailFolder(activeTabRef.current),
      label: title,
      kind,
      preview: { title, subtitle, initials },
    });
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData(MAIL_DRAG_MIME, '1');
      e.dataTransfer.setData('text/plain', title);
    } catch (_) {
      /* ignore */
    }
  }, [beginDrag, setDragGhost]);

  const finishMailDrag = useCallback(() => {
    endDrag();
  }, [endDrag]);

  const deleteInboxThread = async (thread) => {
    setThreadContextMenu(null);
    if (bulkDeleting) return;
    const emailsInThread = [...(thread?.emails || [])].sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
    );
    if (!emailsInThread.length) return;
    const label = thread.fromName || thread.id || 'diesen Kontakt';
    const folder = resolveImapSourceFolder(resolveMailFolder(activeTabRef.current));
    const permanent = folder === 'trash';
    const count = emailsInThread.length;
    setMailDeletePrompt({
      title: permanent ? 'Thread endgültig löschen?' : 'Thread löschen?',
      message: permanent
        ? `Alle ${count} Nachricht${count === 1 ? '' : 'en'} von „${label}“ unwiderruflich löschen?`
        : `Alle ${count} Nachricht${count === 1 ? '' : 'en'} von „${label}“ in den Papierkorb verschieben?`,
      confirmLabel: permanent ? 'Endgültig löschen' : 'Löschen',
      run: async () => {
        const uids = emailsInThread.map((m) => m.uid).filter(Boolean);
        setListExpandedThreadId(thread.id);
        await bulkDeleteUids(uids, {
          threadId: thread.id,
          label,
          skipConfirm: true,
        });
        if (selectedThreadId === thread.id) {
          clearInboxSelection();
        }
        window.setTimeout(() => {
          setListExpandedThreadId((cur) => (cur === thread.id ? null : cur));
        }, 360);
      },
    });
  };

  const bulkDeleteUids = async (rawUids, opts = {}) => {
    if (bulkDeleting) return { deleted: 0, failed: 0 };
    const uids = [...new Set((rawUids || []).map((u) => String(u)).filter(Boolean))];
    if (!uids.length) return { deleted: 0, failed: 0 };

    const folder = resolveImapSourceFolder(resolveMailFolder(activeTabRef.current));
    const permanent = folder === 'trash';
    const total = uids.length;
    const label = opts.label || `${total} E-Mail${total === 1 ? '' : 's'}`;

    if (!opts.skipConfirm) {
      setMailDeletePrompt({
        title: permanent ? 'E-Mails endgültig löschen?' : 'E-Mails löschen?',
        message: permanent
          ? `${total} E-Mail${total === 1 ? '' : 's'} unwiderruflich löschen?`
          : `${total} E-Mail${total === 1 ? '' : 's'} in den Papierkorb verschieben?`,
        confirmLabel: permanent ? 'Endgültig löschen' : 'Löschen',
        run: () => bulkDeleteUids(uids, { ...opts, skipConfirm: true }),
      });
      return { deleted: 0, failed: 0 };
    }

    const BATCH_SIZE = 25;
    let deleted = 0;
    let failed = 0;
    const progressToast = toast.loading(`Lösche 0 / ${total}…`);

    setBulkDeleting({
      threadId: opts.threadId || '__selection__',
      done: 0,
      total,
      label,
      currentUids: [],
    });

    const removeUidsFromUi = async (uidList) => {
      const uidSet = new Set(uidList.map(String));
      const removedEmails = (emailsRef.current || []).filter((e) => uidSet.has(String(e.uid)));
      beginMailLeave([...uidSet], 'delete');
      await new Promise((resolve) => window.setTimeout(resolve, MAIL_LEAVE_MS));
      purgeDeletedUidsFromCaches([...uidSet], { fromFolder: folder });
      applySidebarCountsForMailChange(removedEmails, {
        fromFolder: folder,
        toFolder: permanent ? null : 'trash',
        permanent,
        sourceCountSynced: true,
      });
      setThreadDetails((prev) => {
        let changed = false;
        const next = { ...prev };
        uidSet.forEach((key) => {
          if (next[key]) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      setThreadMessages((prev) => prev.filter((m) => !uidSet.has(String(m.uid))));
      setSelectedMailUids((prev) => {
        if (!prev.size) return prev;
        const next = new Set(prev);
        let changed = false;
        uidSet.forEach((u) => {
          if (next.delete(u)) changed = true;
        });
        return changed ? next : prev;
      });
      if (uidSet.has(String(selectedInboxUid))) {
        setSelectedInboxUid(null);
        setSelectedInboxEmail(null);
        setExpandedThreadUid(null);
      }
      clearMailLeave([...uidSet]);
    };

    const deleteOneWithTimeout = async (uid) => {
      const uidKey = String(uid);
      setBulkDeleting((prev) => prev && {
        ...prev,
        currentUids: [uidKey],
        done: deleted,
      });
      await axios.delete(`/api/emails/${uid}`, {
        timeout: 15000,
        params: {
          folder,
          ...(permanent ? { permanent: 1 } : {}),
        },
      });
      deleted += 1;
      await removeUidsFromUi([uid]);
      setBulkDeleting((prev) => prev && {
        ...prev,
        done: deleted,
        currentUids: [],
      });
      toast.loading(`Lösche ${deleted} / ${total}…`, { id: progressToast });
      await new Promise((resolve) => window.setTimeout(resolve, 40));
    };

    for (let i = 0; i < uids.length; i += BATCH_SIZE) {
      const batch = uids.slice(i, i + BATCH_SIZE);
      const batchKeys = batch.map(String);
      const doneBeforeBatch = deleted;
      setBulkDeleting((prev) => prev && {
        ...prev,
        currentUids: batchKeys,
        done: doneBeforeBatch,
      });
      toast.loading(`Lösche ${doneBeforeBatch} / ${total}… (${batch.length} aktiv)`, { id: progressToast });

      try {
        await axios.post(
          '/api/emails/bulk-delete',
          { uids: batch, folder, permanent },
          { timeout: 120000 }
        );
        deleted += batch.length;
        const doneAfterBatch = deleted;
        await removeUidsFromUi(batch);
        setBulkDeleting((prev) => prev && {
          ...prev,
          done: doneAfterBatch,
          currentUids: [],
        });
        toast.loading(`Lösche ${doneAfterBatch} / ${total}…`, { id: progressToast });
        refreshUnreadCount();
        await new Promise((resolve) => window.setTimeout(resolve, 40));
      } catch (_) {
        for (const uid of batch) {
          try {
            await deleteOneWithTimeout(uid);
          } catch (_) {
            failed += 1;
            const doneAfterFail = deleted;
            setBulkDeleting((prev) => prev && {
              ...prev,
              currentUids: [],
              done: doneAfterFail,
            });
            toast.loading(`Lösche ${doneAfterFail} / ${total}… (${failed} Fehler)`, { id: progressToast });
          }
        }
        refreshUnreadCount();
      }
    }

    setBulkDeleting(null);
    refreshUnreadCount();

    if (deleted && !failed) {
      toast.success(
        permanent
          ? `${deleted} endgültig gelöscht`
          : `${deleted} Nachricht${deleted === 1 ? '' : 'en'} gelöscht`,
        { id: progressToast }
      );
    } else if (deleted) {
      toast.success(`${deleted} gelöscht, ${failed} fehlgeschlagen`, { id: progressToast });
    } else {
      toast.error('Löschen fehlgeschlagen', { id: progressToast });
    }

    window.setTimeout(() => {
      loadEmails(false, { silent: true });
    }, 800);

    return { deleted, failed };
  };

  const toggleMailUidSelected = (uid, checked) => {
    const key = String(uid);
    if (!key) return;
    setSelectedMailUids((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleThreadSelected = (thread, checked) => {
    const uids = (thread?.emails || []).map((m) => String(m.uid)).filter(Boolean);
    if (!uids.length) return;
    setSelectedMailUids((prev) => {
      const next = new Set(prev);
      uids.forEach((u) => {
        if (checked) next.add(u);
        else next.delete(u);
      });
      return next;
    });
  };

  const selectAllVisibleThreads = () => {
    const next = new Set();
    visibleConversationThreads.forEach((thread) => {
      (thread.emails || []).forEach((m) => {
        if (m?.uid != null) next.add(String(m.uid));
      });
    });
    setSelectedMailUids(next);
  };

  const clearMailSelection = () => setSelectedMailUids(new Set());

  const getSentMailKey = (email) => {
    if (!email) return null;
    const isImap =
      email.source === 'imap' ||
      (email.uid != null && String(email.id || '').startsWith('imap-'));
    if (isImap && email.uid != null) return `imap:${email.uid}`;
    if (email.id != null && !String(email.id).startsWith('imap-')) return `db:${email.id}`;
    if (email.uid != null) return `imap:${email.uid}`;
    return null;
  };

  const clearSentMailSelection = () => setSelectedSentKeys(new Set());

  const toggleSentKeySelected = (email, checked) => {
    const key = getSentMailKey(email);
    if (!key) return;
    setSelectedSentKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleSentThreadSelected = (thread, checked) => {
    const keys = (thread?.emails || []).map(getSentMailKey).filter(Boolean);
    if (!keys.length) return;
    setSelectedSentKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => {
        if (checked) next.add(k);
        else next.delete(k);
      });
      return next;
    });
  };

  const selectAllVisibleSent = () => {
    const next = new Set();
    visibleSentThreads.forEach((thread) => {
      (thread.emails || []).forEach((m) => {
        const key = getSentMailKey(m);
        if (key) next.add(key);
      });
    });
    setSelectedSentKeys(next);
  };

  const visibleSelectableSentKeys = useMemo(() => {
    const keys = [];
    visibleSentThreads.forEach((thread) => {
      (thread.emails || []).forEach((m) => {
        const key = getSentMailKey(m);
        if (key) keys.push(key);
      });
    });
    return keys;
  }, [visibleSentThreads]);

  const allVisibleSentSelected =
    visibleSelectableSentKeys.length > 0 &&
    visibleSelectableSentKeys.every((k) => selectedSentKeys.has(k));

  const deleteSentEmailsBulk = async (emails, { skipConfirm = false } = {}) => {
    const list = (emails || []).filter(Boolean);
    if (!list.length || bulkDeleting) return;
    if (!skipConfirm) {
      setMailDeletePrompt({
        title: list.length === 1 ? 'Gesendete E-Mail löschen?' : 'Gesendete E-Mails löschen?',
        message:
          list.length === 1
            ? 'Diese gesendete E-Mail wird unwiderruflich gelöscht.'
            : `${list.length} gesendete E-Mails werden unwiderruflich gelöscht.`,
        confirmLabel: list.length === 1 ? 'Löschen' : 'Alle löschen',
        run: () => deleteSentEmailsBulk(list, { skipConfirm: true }),
      });
      return;
    }

    const progressToast = toast.loading(
      list.length === 1 ? 'Löschen…' : `Lösche 0/${list.length}…`
    );
    setBulkDeleting({ threadId: 'sent', done: 0, total: list.length, label: 'Löschen' });

    const deletedKeys = new Set();
    let deleted = 0;
    let failed = 0;

    for (let i = 0; i < list.length; i += 1) {
      const email = list[i];
      const key = getSentMailKey(email);
      const isImap =
        email.source === 'imap' ||
        (email.uid != null && String(email.id || '').startsWith('imap-'));
      try {
        if (isImap && email.uid != null) {
          await axios.delete(`/api/sent-emails/imap/${email.uid}`, {
            params: email.mailbox ? { mailbox: email.mailbox } : {},
            data: email.mailbox ? { mailbox: email.mailbox } : {},
          });
        } else if (email.id && !String(email.id).startsWith('imap-')) {
          await axios.delete(`/api/sent-email/${email.id}`);
        } else {
          failed += 1;
          continue;
        }
        if (key) deletedKeys.add(key);
        deleted += 1;
      } catch (_) {
        failed += 1;
      }
      setBulkDeleting({
        threadId: 'sent',
        done: i + 1,
        total: list.length,
        label: 'Löschen',
      });
      if (list.length > 1) {
        toast.loading(`Lösche ${i + 1}/${list.length}…`, { id: progressToast });
      }
    }

    setSentEmails((prev) => prev.filter((m) => !deletedKeys.has(getSentMailKey(m))));
    setSelectedSentKeys((prev) => {
      const next = new Set(prev);
      deletedKeys.forEach((k) => next.delete(k));
      return next;
    });
    if (deleted > 0) adjustFolderCount('sent', -deleted);

    if (selectedSentEmail && deletedKeys.has(getSentMailKey(selectedSentEmail))) {
      clearSentSelection();
    }

    setBulkDeleting(null);

    if (deleted && !failed) {
      toast.success(
        deleted === 1 ? 'Gesendete E-Mail gelöscht' : `${deleted} gesendete E-Mails gelöscht`,
        { id: progressToast }
      );
    } else if (deleted) {
      toast.success(`${deleted} gelöscht, ${failed} fehlgeschlagen`, { id: progressToast });
    } else {
      toast.error('Löschen fehlgeschlagen', { id: progressToast });
    }

    window.setTimeout(() => {
      loadSentEmails({ silent: true });
      refreshUnreadCount();
    }, 450);
  };

  const deleteSelectedSentMails = async () => {
    if (!selectedSentKeys.size || bulkDeleting) return;
    const selected = sentEmails.filter((m) => selectedSentKeys.has(getSentMailKey(m)));
    await deleteSentEmailsBulk(selected);
  };

  const deleteSentThread = async (thread) => {
    const emails = thread?.emails || [];
    if (!emails.length) return;
    await deleteSentEmailsBulk(emails);
  };

  const visibleSelectableUids = useMemo(() => {
    const uids = [];
    visibleConversationThreads.forEach((thread) => {
      (thread.emails || []).forEach((m) => {
        if (m?.uid != null) uids.push(String(m.uid));
      });
    });
    return uids;
  }, [visibleConversationThreads]);

  const allVisibleMailsSelected =
    visibleSelectableUids.length > 0 &&
    visibleSelectableUids.every((u) => selectedMailUids.has(u));

  const deleteSelectedMails = async () => {
    if (!selectedMailUids.size || bulkDeleting) return;
    await bulkDeleteUids([...selectedMailUids]);
  };

  const deleteSentEmail = async (email, { skipConfirm = false } = {}) => {
    if (!email) return toast.error('E-Mail fehlt');
    await deleteSentEmailsBulk([email], { skipConfirm });
  };

  const handleComposerChange = (field, value) => {
    composerDraftDirtyRef.current = true;
    setComposerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const insertComposerPlaceholder = (key) => {
    const token = `{{${key}}}`;
    composerDraftDirtyRef.current = true;
    if (insertTarget === 'subject') {
      insertTokenIntoInput(
        subjectRef.current,
        composerData.subject,
        token,
        (next) => handleComposerChange('subject', next)
      );
      return;
    }
    if (composerLockedHtml) {
      let next = composerLockedHtml;
      if (/<\/body>/i.test(next)) {
        next = next.replace(/<\/body>/i, `${token}</body>`);
      } else {
        next = `${next}${token}`;
      }
      setComposerLockedHtml(next);
      composerHtmlRef.current = next;
      return;
    }
    if (!insertTokenIntoQuill(quillRef, token)) {
      const html = getComposerHtml();
      const next = html.replace(/<\/p>\s*$/i, `${token}</p>`);
      composerHtmlRef.current = next.includes(token) ? next : `${html}${token}`;
      setComposerEditorKey((k) => k + 1);
    }
  };

  const handleQuillChange = (val, _delta, source) => {
    if (source === 'user') {
      composerDraftDirtyRef.current = true;
      composerHtmlRef.current = val;
      if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = window.setTimeout(() => {
        void (async () => {
          await ensureDraftAttachmentsReady();
          await persistActiveComposerDraft();
        })();
      }, 800);
    }
  };

  const loadMoreSentEmails = async ({
    quiet = false,
    beforeUid: beforeUidArg = null,
    limit: limitArg = null,
  } = {}) => {
    const beforeUid = beforeUidArg || sentOldestUid || sentMetaRef.current.oldestUid;
    const totalHint = sentMetaRef.current.total || sentTotal || 0;
    const loadedCount = sentEmailsRef.current?.length || 0;
    const apparentlyMore =
      !!sentHasMore ||
      !!sentMetaRef.current.hasMore ||
      (totalHint > 0 && loadedCount < totalHint);

    if (!beforeUid || fetchInFlightRef.current) {
      return { hasMore: false, oldestUid: null, count: 0 };
    }
    if (!quiet && (loadingMore || sentFillInFlightRef.current || !apparentlyMore)) {
      return { hasMore: false, oldestUid: null, count: 0 };
    }

    if (!quiet) setLoadingMore(true);
    fetchInFlightRef.current = true;
    const pageLimit = Math.min(
      750,
      Math.max(1, parseInt(limitArg, 10) || (quiet ? INBOX_FILL_PAGE_SIZE : SENT_PAGE_SIZE))
    );
    try {
      const response = await axios.get('/api/sent-emails', {
        params: {
          limit: pageLimit,
          beforeUid,
        },
        timeout: 60000,
      });
      if (!response.data?.success) {
        if (!quiet) {
          toast.error(response.data?.error || 'Ältere gesendete E-Mails konnten nicht geladen werden');
        }
        return { hasMore: false, oldestUid: null, count: 0 };
      }
      const owner = String(
        response.data.accountMailbox || mailboxUser || authMailbox || ''
      )
        .trim()
        .toLowerCase();
      const older = filterSentForMailbox(response.data.emails || [], owner);
      setSentEmails((prev) => {
        const merged = mergeSentEmails(prev, older);
        sentEmailsRef.current = merged;
        const nextTotal = Number(response.data.total) || sentMetaRef.current.total || merged.length;
        const nextHasMore =
          !!response.data.hasMore || (nextTotal > 0 && merged.length < nextTotal);
        rememberSentSession({
          emails: merged,
          total: nextTotal,
          hasMore: nextHasMore,
          oldestUid: response.data.oldestUid || null,
        });
        return merged;
      });
      const nextTotal = Number(response.data.total) || sentTotal;
      setSentTotal(nextTotal);
      const mergedLen = sentEmailsRef.current?.length || 0;
      const nextHasMore =
        !!response.data.hasMore || (nextTotal > 0 && mergedLen < nextTotal);
      const nextOldest = response.data.oldestUid || null;
      setSentHasMore(nextHasMore);
      if (nextOldest) setSentOldestUid(nextOldest);
      sentMetaRef.current = {
        total: nextTotal || sentMetaRef.current.total || 0,
        hasMore: nextHasMore,
        oldestUid: nextOldest || sentMetaRef.current.oldestUid,
      };
      return {
        hasMore: nextHasMore,
        oldestUid: nextOldest,
        count: older.length,
      };
    } catch (error) {
      if (!quiet) {
        toast.error(error.response?.data?.error || error.message || 'Fehler beim Nachladen');
      }
      return { hasMore: false, oldestUid: null, count: 0 };
    } finally {
      fetchInFlightRef.current = false;
      if (!quiet) setLoadingMore(false);
    }
  };

  const fillRemainingSent = async () => {
    if (sentFillInFlightRef.current) return;

    const totalHint = sentMetaRef.current.total || sentTotal || 0;
    const loadedCount = sentEmailsRef.current?.length || 0;
    const apparentlyMore =
      !!sentMetaRef.current.hasMore || (totalHint > 0 && loadedCount < totalHint);
    if (!apparentlyMore) return;

    sentFillInFlightRef.current = true;
    const generation = ++sentFillGenerationRef.current;
    try {
      let nextUid = sentMetaRef.current.oldestUid;
      let hasMore = apparentlyMore;
      let pages = 0;
      let emptyStreak = 0;

      while (hasMore && pages < 400) {
        if (sentFillGenerationRef.current !== generation) break;
        if (activeTabRef.current !== 'sent') break;
        if (!nextUid) break;

        let wait = 0;
        while (fetchInFlightRef.current && wait < 40) {
          await new Promise((r) => setTimeout(r, 100));
          wait += 1;
        }
        if (sentFillGenerationRef.current !== generation) break;

        pages += 1;
        const result = await loadMoreSentEmails({
          quiet: true,
          beforeUid: nextUid,
          limit: INBOX_FILL_PAGE_SIZE,
        });

        hasMore = !!result.hasMore;
        if (result.oldestUid) nextUid = result.oldestUid;

        if (!result.count) {
          emptyStreak += 1;
          if (emptyStreak >= 2) break;
          continue;
        }
        emptyStreak = 0;
      }
    } finally {
      if (sentFillGenerationRef.current === generation) {
        sentFillInFlightRef.current = false;
      }
    }
  };

  const loadSentEmails = async (options = {}) => {
    const silent = options.silent === true;
    const expectedMailbox = String(
      options.mailbox || mailboxUser || authMailbox || ''
    )
      .trim()
      .toLowerCase();
    if (silent && fetchInFlightRef.current) return;
    if (!silent) setLoading(true);
    else setAutoRefreshing(true);
    fetchInFlightRef.current = true;
    try {
      const res = await axios.get('/api/sent-emails', {
        params: { limit: SENT_PAGE_SIZE },
        timeout: 45000,
      });
      if (res.data.success) {
        const accountMailbox = String(res.data.accountMailbox || '')
          .trim()
          .toLowerCase();
        // Fremdes Postfach in der Antwort → verwerfen (Sicherheitsnetz)
        if (
          expectedMailbox &&
          accountMailbox &&
          accountMailbox !== expectedMailbox
        ) {
          console.warn('Gesendet-Antwort Postfach-Mismatch:', {
            expectedMailbox,
            accountMailbox,
          });
          return;
        }
        const owner = accountMailbox || expectedMailbox;
        const incoming = filterSentForMailbox(res.data.emails || [], owner);
        const total = Number(res.data.total) || incoming.length;
        const responseHasMore = !!res.data.hasMore || (total > 0 && incoming.length < total);
        const responseOldest = res.data.oldestUid || null;
        const prev = sentEmailsRef.current || [];
        const unchanged =
          silent &&
          prev.length > 0 &&
          prev.length === incoming.length &&
          sentListSignature(prev) === sentListSignature(incoming);

        if (!unchanged) {
          setSentEmails((current) => {
            const merged =
              silent && current.length > incoming.length
                ? mergeSentEmails(current, incoming)
                : incoming;
            sentEmailsRef.current = merged;
            rememberSentSession({
              emails: merged,
              total,
              hasMore: responseHasMore || merged.length < total,
              oldestUid: responseOldest,
            });
            return merged;
          });
        } else {
          rememberSentSession({
            emails: prev,
            total,
            hasMore: responseHasMore || prev.length < total,
            oldestUid: responseOldest,
          });
        }

        setSentTotal(total);
        setSentHasMore(responseHasMore || (sentEmailsRef.current?.length || 0) < total);
        setSentOldestUid(responseOldest);
        sentMetaRef.current = {
          total,
          hasMore: responseHasMore || (sentEmailsRef.current?.length || 0) < total,
          oldestUid: responseOldest,
        };

        const emails = sentEmailsRef.current || incoming;
        setSelectedSentEmail((current) => {
          if (!current) return current;
          const fresh = emails.find((e) => {
            if (current.source === 'imap' || String(current.id).startsWith('imap-')) {
              return String(e.uid) === String(current.uid);
            }
            return Number(e.id) === Number(current.id);
          });
          return fresh
            ? {
                ...current,
                ...fresh,
                html: current.html || fresh.html,
                text: current.text || fresh.text,
                attachments: current.attachments || fresh.attachments,
              }
            : current;
        });
        if (res.data.imapError && !silent) {
          toast.error(`IMAP Gesendet: ${res.data.imapError}`);
        }
      }
    } catch (err) {
      if (!silent) toast.error('Fehler beim Laden der gesendeten E-Mails');
    } finally {
      fetchInFlightRef.current = false;
      if (!silent) setLoading(false);
      setAutoRefreshing(false);
      if (activeTabRef.current === 'sent') {
        window.setTimeout(() => {
          fillRemainingSent();
        }, silent ? 400 : 120);
      }
    }
  };

  const sendEmail = async () => {
    if (hasPendingSend) {
      await cancelPendingSend();
      return;
    }
  
    const messageHtml = getComposerHtml();
    if (!composerData.to.trim() || !composerData.subject.trim()) {
      toast.error('Bitte Empfänger und Betreff ausfüllen');
      return;
    }
    if (isEmptyRichText(messageHtml) && !composerLockedHtml) {
      toast.error('Bitte eine Nachricht schreiben');
      return;
    }

    const finalHtml = getComposerHtmlForSend();
    const filesForSend = await resolveComposerFilesForSend();
    const formData = new FormData();
    formData.append('to', composerData.to);
    formData.append('cc', composerData.cc || '');
    formData.append('bcc', composerData.bcc || '');
    formData.append('subject', composerData.subject);
    formData.append('html', finalHtml);
    filesForSend.forEach((file) => {
      formData.append('attachments', file);
    });
  
    const delay = Math.min(60, Math.max(0, Number(preferences.undoDelaySeconds) || 0));
    if (delay <= 0) {
      setSending(true);
      try {
        const res = await axios.post('/api/send-email-reply', formData);
        if (res.data?.success) {
          const id = composerDraftIdRef.current;
          if (id) discardComposerDraftById(id);
          else {
            void clearComposerDraft(authMailbox).then(() => refreshComposerDrafts());
          }
          toast.success('E-Mail erfolgreich gesendet!');
          closeComposer({ discardDraft: true, cancelSend: false });
          loadSentEmails({ silent: true });
        } else {
          toast.error(res.data?.error || 'Senden fehlgeschlagen');
          setSending(false);
        }
      } catch (error) {
        console.error(error);
        toast.error('Fehler beim Senden: ' + (error.response?.data?.error || error.message));
        setSending(false);
      }
      return;
    }

    // Sofort in Server-Queue legen — Reminder bleibt app-weit sichtbar
    setSending(true);
    formData.append('delay_seconds', String(delay));
    try {
      composerDraftDirtyRef.current = true;
      await ensureDraftAttachmentsReady();
      const draftId = await persistActiveComposerDraft(null, { force: true });
      const res = await axios.post('/api/scheduled-emails', formData);
      if (!res.data?.success || !res.data?.id) {
        toast.error(res.data?.error || 'Verzögerter Versand konnte nicht geplant werden');
        setSending(false);
        return;
      }
      startPending({
        scheduledId: res.data.id,
        cancelToken: res.data.cancelToken,
        to: composerData.to,
        subject: composerData.subject,
        delaySeconds: delay,
        draftId,
        mailbox: authMailbox,
      });
      setSending(false);
      closeComposer({ discardDraft: false, cancelSend: false });
      toast('Versand geplant — Reminder unten. Tippen zum Zurückkehren.', {
        icon: '⏳',
        duration: 3500,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        'Verzögerter Versand fehlgeschlagen: ' +
          (error.response?.data?.error || error.message)
      );
      setSending(false);
    }
  };


  // Entwürfe automatisch speichern — leerer Composer entfernt den aktuellen Entwurf
  useEffect(() => {
    if (!showComposer) return undefined;
    if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = window.setTimeout(() => {
      void (async () => {
        await ensureDraftAttachmentsReady();
        await persistActiveComposerDraft();
      })();
    }, 800);
    return () => {
      if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps -- getComposerHtml liest Refs
    showComposer,
    composerMode,
    composerData.to,
    composerData.cc,
    composerData.bcc,
    composerData.subject,
    composerData.html,
    composerLockedHtml,
    composerTemplateId,
    authMailbox,
  ]);

  // Ältere E-Mails automatisch nachladen (Scroll-Sentinel)
  useEffect(() => {
    const root = listScrollRef.current;
    const target = loadMoreSentinelRef.current;
    if (!root || !target) return undefined;
    if (!isInboxMailbox(activeTab)) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (!hit) return;
        if (loadingMore || loading || inboxFillInFlightRef.current) return;
        if (!(inboxHasMore || (inboxTotal > 0 && emails.length < inboxTotal))) return;
        loadMoreEmails({ quiet: true });
      },
      { root, rootMargin: '240px', threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [activeTab, inboxHasMore, inboxTotal, emails.length, loadingMore, loading]); // eslint-disable-line react-hooks/exhaustive-deps -- loadMoreEmails absichtlich ausgelassen

  useEffect(() => {
    const root = listScrollRef.current;
    const target = loadMoreSentSentinelRef.current;
    if (!root || !target) return undefined;
    if (activeTab !== 'sent') return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (!hit) return;
        if (loadingMore || loading || sentFillInFlightRef.current) return;
        if (!(sentHasMore || (sentTotal > 0 && sentEmails.length < sentTotal))) return;
        loadMoreSentEmails({ quiet: true });
      },
      { root, rootMargin: '240px', threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [activeTab, sentHasMore, sentTotal, sentEmails.length, loadingMore, loading]); // eslint-disable-line react-hooks/exhaustive-deps -- loadMoreSentEmails absichtlich ausgelassen

  const isComposing = showComposer;
  const composerTitle =
    composerMode === 'reply' ? 'Antworten' :
    composerMode === 'replyAll' ? 'Allen antworten' :
    composerMode === 'forward' ? 'Weiterleiten' :
    'Neue E-Mail';

  const readingComposer = (
    <div
      className="flex-1 min-h-0 flex flex-col bg-white dark:bg-neutral-900"
      onKeyDown={(e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          e.preventDefault();
          sendEmail();
        }
      }}
    >
      <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
        <div className="min-w-0 flex items-center gap-2">
          <button
            type="button"
            onClick={closeComposer}
            className="lg:hidden p-2 -ml-1 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
            aria-label="Zurück"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
            <p className="text-sm font-semibold text-neutral-900 dark:text-white">{composerTitle}</p>
            <p className="hidden sm:inline text-[10px] text-neutral-500">
              Strg + Enter zum Senden
              {draftSavedAt ? ` · Entwurf ${draftSavedAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}` : ''}
            </p>
            {draftSavedAt ? (
              <p className="sm:hidden text-[10px] text-neutral-500">
                Entwurf {draftSavedAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              </p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={closeComposer}
          className="hidden lg:inline-flex btn-ghost p-2"
          aria-label="Schließen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-shrink-0 px-3 sm:px-4 pt-3 space-y-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[11px] text-neutral-400 w-14 flex-shrink-0">An</label>
          <RecipientSuggestInput
            value={composerData.to}
            onChange={(v) => handleComposerChange('to', v)}
            className="flex-1 min-w-[160px]"
            inputClassName="w-full px-2.5 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800"
            placeholder="empfaenger@beispiel.de"
            autoFocus
          />
          <button type="button" onClick={() => setShowCc((v) => !v)} className={`text-[11px] px-2 py-1 rounded-md border flex-shrink-0 ${showCc ? 'border-primary-400 text-primary-600' : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300'}`}>
            CC
          </button>
          <button type="button" onClick={() => setShowBcc((v) => !v)} className={`text-[11px] px-2 py-1 rounded-md border flex-shrink-0 ${showBcc ? 'border-primary-400 text-primary-600' : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300'}`}>
            BCC
          </button>
        </div>

        {showCc && (
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-neutral-400 w-14 flex-shrink-0">CC</label>
            <RecipientSuggestInput
              value={composerData.cc}
              onChange={(v) => handleComposerChange('cc', v)}
              className="flex-1"
              inputClassName="w-full px-2.5 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800"
              placeholder="cc@beispiel.de"
            />
          </div>
        )}

        {showBcc && (
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-neutral-400 w-14 flex-shrink-0">BCC</label>
            <RecipientSuggestInput
              value={composerData.bcc}
              onChange={(v) => handleComposerChange('bcc', v)}
              className="flex-1"
              inputClassName="w-full px-2.5 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800"
              placeholder="bcc@beispiel.de"
            />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[11px] text-neutral-400 w-14 flex-shrink-0">Betreff</label>
          <input
            ref={subjectRef}
            type="text"
            value={composerData.subject}
            onFocus={() => setInsertTarget('subject')}
            onChange={(e) => handleComposerChange('subject', e.target.value)}
            className="flex-1 min-w-[140px] px-2.5 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800"
            placeholder="Betreff"
          />
          <label className="text-[11px] text-neutral-400 flex-shrink-0">Vorlage</label>
          <TemplatePickerButton
            templates={mailTemplates}
            selectedId={composerTemplateId}
            onSelect={(id) => applyMailTemplate(id)}
            label={mailTemplates.length ? 'Vorlage wählen…' : 'Keine Vorlagen vorhanden'}
            className="flex-1 min-w-[140px] px-2.5 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-left text-neutral-800 dark:text-neutral-100 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors"
          />
          {composerTemplateId ? (
            <button
              type="button"
              onClick={() => {
                setComposerTemplateId('');
                setComposerLockedHtml(null);
                setComposerData((prev) => ({ ...prev, html: '<p><br></p>' }));
                composerHtmlRef.current = '<p><br></p>';
                setComposerEditorKey((k) => k + 1);
              }}
              className="text-[11px] px-2 py-1 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex-shrink-0"
              title="Vorlage entfernen"
            >
              Leeren
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col px-3 sm:px-4 py-3 gap-2.5">
        <div className="flex-1 min-h-0 flex flex-col gap-2.5 overflow-y-auto overflow-x-hidden">
          {!composerLockedHtml ? (
            <EmailComposerAiButtons
              composerMode={composerMode}
              sourceEmail={composerSourceEmail}
              getDraftHtml={getComposerHtml}
              composerHtmlRef={composerHtmlRef}
              setComposerData={setComposerData}
              setComposerEditorKey={setComposerEditorKey}
              compact
            />
          ) : null}
          {composerLockedHtml ? (
            <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-white dark:bg-neutral-800">
              <div className="flex-shrink-0 px-3 py-2 border-b border-neutral-100 dark:border-neutral-700 flex items-center justify-between gap-2 bg-neutral-50 dark:bg-neutral-900/60">
                <p className="text-xs text-neutral-600 dark:text-neutral-300">
                  Design-Vorlage — wird so versendet. Platzhalter werden beim Senden ersetzt.
                </p>
                <button
                  type="button"
                  className="text-[11px] font-medium text-primary-600 hover:text-primary-700"
                  onClick={() => {
                    setComposerLockedHtml(null);
                    setComposerTemplateId('');
                    setComposerData((prev) => ({ ...prev, html: '<p><br></p>' }));
                    composerHtmlRef.current = '<p><br></p>';
                    setComposerEditorKey((k) => k + 1);
                  }}
                >
                  Als Text bearbeiten
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-auto bg-neutral-100 dark:bg-neutral-950 p-2">
                <iframe
                  title="Vorlagen-Vorschau"
                  srcDoc={composerLockedHtml}
                  className="w-full h-full min-h-[220px] rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white"
                  sandbox=""
                />
              </div>
            </div>
          ) : (
            <div
              className="quill-container email-message-input reading-composer-quill flex-1 min-h-0 flex flex-col"
              onFocusCapture={() => setInsertTarget('content')}
            >
              <ReactQuill
                key={composerEditorKey}
                ref={(instance) => {
                  quillRef.current = instance;
                  if (instance) {
                    window.requestAnimationFrame(() => {
                      try {
                        prepareEmailComposerQuill(instance.getEditor?.());
                      } catch (_err) {
                        /* ignore */
                      }
                    });
                  }
                }}
                theme="snow"
                defaultValue={composerData.html || '<p><br></p>'}
                onChange={handleQuillChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Nachricht schreiben…"
                className="flex-1 min-h-0 flex flex-col"
              />
            </div>
          )}

          <VariableInsertPanel
            variables={templateVars}
            insertTarget={insertTarget}
            onInsert={insertComposerPlaceholder}
            className="flex-shrink-0 max-h-36 overflow-y-auto"
          />

          {composerQuoteHtml ? (
            <div className="composer-quote-preview flex-shrink-0 max-h-[40%] overflow-y-auto rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/80 px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">
                Originalnachricht
              </p>
              <div
                className="composer-quote-body text-[13px] leading-relaxed text-neutral-600 dark:text-neutral-300"
                dangerouslySetInnerHTML={{ __html: composerQuoteHtml }}
              />
            </div>
          ) : null}
        </div>

        <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-300">
              <Paperclip className="w-4 h-4" />
              Datei anhängen
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  setComposerAttachments((prev) => [...prev, ...newFiles]);
                  e.target.value = '';
                }}
              />
            </label>
            <MediaAttachmentPickerButton
              className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              label="Bibliothek"
              onSelect={(files) => setComposerAttachments((prev) => [...prev, ...files])}
              maxFiles={10}
            />
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button type="button" onClick={closeComposer} className="btn-secondary !py-2.5 sm:!py-1.5 !px-3 text-sm sm:text-xs flex-1 sm:flex-none">
              Abbrechen
          </button>
          <button
              type="button"
              onClick={sendEmail}
              disabled={sending && !hasPendingSend}
              className={`!py-2.5 sm:!py-1.5 !px-3 text-sm sm:text-xs inline-flex items-center justify-center rounded-lg font-medium flex-1 sm:flex-none ${
                hasPendingSend
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'btn-primary'
              }`}
            >
              {hasPendingSend ? (
                <>Rückgängig · {sendCountdown}s</>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {sending ? 'Senden…' : 'Senden'}
                </>
              )}
          </button>
          </div>
        </div>

        {attachments.length > 0 && (
          <div className="flex-shrink-0 flex flex-wrap gap-1.5">
            {attachments.map((file, index) => (
              <ComposerAttachmentChip
                key={`${file.name}-${file.size}-${file.lastModified || index}`}
                file={file}
                formatFileSize={formatFileSize}
                onPreview={() => openComposerAttachmentPreview(file)}
                onRemove={() =>
                  setComposerAttachments((prev) => prev.filter((_, i) => i !== index))
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (initialLoading) {
    return <PageSkeleton variant="inbox" />;
  }

  if (!imapConfigured) {
    return (
      <div className="h-full min-h-0 flex flex-col bg-white dark:bg-neutral-950">
        <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 px-4 py-3 flex flex-wrap items-center gap-3 justify-between bg-white dark:bg-neutral-900">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-lg font-semibold text-neutral-900 dark:text-white truncate">
              Postfach
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-1 max-w-xl min-w-[12rem] opacity-50 pointer-events-none">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <div className="w-full pl-9 pr-3 py-2 text-sm bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl text-neutral-400">
                Suchen…
        </div>
            </div>
          </div>
          <div className="flex items-center gap-2 opacity-50 pointer-events-none">
            <span className="btn-primary !py-2 !px-3 inline-flex items-center">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Neue Email</span>
            </span>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="flex flex-col border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 min-h-0 flex w-full lg:w-[380px] xl:w-[420px] flex-shrink-0">
            <div className="px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-800 flex items-center gap-2 flex-shrink-0">
              <Inbox className="w-4 h-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Empfangen · Postfach offline
              </span>
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center px-4">
              <p className="text-sm text-neutral-400 text-center">Keine E-Mails</p>
            </div>
          </div>

          <div className="flex-1 min-w-0 min-h-0 hidden lg:flex flex-col bg-neutral-50/50 dark:bg-neutral-950 items-center justify-center px-6">
            <div className="max-w-sm text-center">
              <div className="w-14 h-14 rounded-2xl bg-neutral-200/80 dark:bg-neutral-800 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-neutral-500 dark:text-neutral-400" />
              </div>
              <h2 className="text-base font-semibold text-neutral-900 dark:text-white">
                Postfach nicht verbunden
              </h2>
              <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                Melde dich erneut mit E-Mail und Passwort an, um Empfangen und Gesendet zu nutzen.
              </p>
              <a href="/login" className="btn-primary inline-flex items-center mt-5 !py-2 !px-4 text-sm">
                <Mail className="w-4 h-4 mr-2" />
                Zur Anmeldung
              </a>
            </div>
          </div>
        </div>

        <div className="lg:hidden flex-shrink-0 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-4 text-center">
          <p className="text-sm font-medium text-neutral-900 dark:text-white">Postfach nicht verbunden</p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Erneut anmelden, um dein Postfach zu laden.
          </p>
          <a href="/login" className="btn-primary inline-flex items-center mt-3 !py-2 !px-3 text-sm">
            <Mail className="w-4 h-4 mr-2" />
            Zur Anmeldung
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-white dark:bg-neutral-950">
      {/* Toolbar — auf Mobile ausgeblendet wenn Detail/Composer offen */}
      <div
        className={`flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 px-3 sm:px-4 py-2 sm:py-0 sm:min-h-[63px] sm:flex sm:items-center bg-white dark:bg-neutral-900 ${
          selectedThreadId || selectedSentEmail || isComposing ? 'hidden lg:flex' : 'flex'
        }`}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 w-full min-w-0">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('odium-open-sidebar'))}
                className="lg:hidden p-2 -ml-1 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation flex-shrink-0"
                aria-label="Menü öffnen"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-white truncate">
                {activeTab === 'category'
                  ? (categories.find((c) => String(c.id) === String(activeCategoryId))?.name || 'Ordner')
                  : (FOLDER_LABELS[activeTab] || 'Empfangen')}
              </h1>
              {activeTab === 'spam' ? (
                <button
                  type="button"
                  onClick={() => navigate('/settings#spam-filter')}
                  className="btn-ghost !px-2 !py-1.5 text-xs text-neutral-500 hidden sm:inline-flex"
                  title="Spam-Filter in den Einstellungen bearbeiten"
                >
                  Filter bearbeiten
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={openNewComposer}
              className="btn-primary !py-2 !px-2.5 sm:hidden flex-shrink-0 touch-manipulation"
              title="Neue E-Mail"
              aria-label="Neue E-Mail"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 w-full sm:flex-1 sm:max-w-lg min-w-0">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 pl-9 pr-8 text-sm bg-neutral-100/90 dark:bg-neutral-800/90 border border-transparent rounded-full focus:outline-none focus:bg-white dark:focus:bg-neutral-900 focus:border-primary-300 dark:focus:border-primary-700 focus:ring-2 focus:ring-primary-500/25 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400"
                placeholder="Postfach durchsuchen"
                enterKeyHint="search"
                aria-label="Postfach durchsuchen"
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/80 dark:hover:text-neutral-200 dark:hover:bg-neutral-700"
                  aria-label="Suche leeren"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              ) : null}
            </div>
            <div className="relative flex-shrink-0 w-[5.75rem]">
              <SelectField
                value={threadSort}
                onChange={(e) => setThreadSort(e.target.value)}
                menuAlign="end"
                menuMinWidth={176}
                className="h-9 pl-2.5 py-0 text-xs font-medium bg-neutral-100/90 dark:bg-neutral-800/90 border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500/25 text-neutral-700 dark:text-neutral-100"
                chevronSize="w-3 h-3"
                chevronClassName="!right-1.5"
                title="Sortierung"
                aria-label="Sortierung"
              >
                <option value="newest">
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <ArrowDownWideNarrow className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                    <span className="truncate">Neueste</span>
                  </span>
                </option>
                <option value="oldest">
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <ArrowUpWideNarrow className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                    <span className="truncate">Älteste</span>
                  </span>
                </option>
                <option value="most">
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <Layers className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                    <span className="truncate">Meiste</span>
                  </span>
                </option>
                {isInboxMailbox(activeTab) && (
                  <option value="unread">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <MailOpen className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                      <span className="truncate">Ungelesen</span>
                    </span>
                  </option>
                )}
              </SelectField>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <button onClick={openNewComposer} className="btn-primary !py-2 !px-3">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Neue Email</span>
            </button>
          </div>
        </div>
      </div>

      {/* Split view */}
      <div className="flex-1 min-h-0 flex">
        {/* List pane */}
        <div
          className={`flex flex-col border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 min-h-0 ${
            (selectedThreadId || selectedSentEmail || isComposing)
              ? 'hidden lg:flex w-full lg:w-[380px] xl:w-[420px] flex-shrink-0'
              : 'flex w-full lg:w-[380px] xl:w-[420px] flex-shrink-0'
          }`}
        >
          <div className="px-3 sm:px-4 py-2 border-b border-neutral-100 dark:border-neutral-800 flex flex-col gap-2 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {activeTab === 'spam' ? (
                <ShieldAlert className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              ) : activeTab === 'trash' ? (
                <Trash2 className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              ) : activeTab === 'sent' ? (
                <Send className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              ) : activeTab === 'drafts' ? (
                <FileText className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              ) : activeTab === 'important' ? (
                <Star className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              ) : activeTab === 'unread' ? (
                <MailOpen className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              ) : activeTab === 'category' ? (
                <Tag className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              ) : (
                <Inbox className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              )}
              <span className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 truncate">
                {activeTab === 'drafts' ? (
                  <>
                    <span className="sm:hidden">{composerDrafts.length} Entwürfe</span>
                    <span className="hidden sm:inline">
                      Entwürfe · {composerDrafts.length}{' '}
                      {composerDrafts.length === 1 ? 'Entwurf' : 'Entwürfe'}
                    </span>
                  </>
                ) : isInboxMailbox(activeTab) || activeTab === 'category' ? (
                  <>
                    <span className="sm:hidden tabular-nums">
                      {filteredEmails.length}
                      {inboxTotal > filteredEmails.length && activeTab === 'inbox'
                        ? `/${inboxTotal}`
                        : ''}{' '}
                      · {conversationThreads.length} Kontakte
                    </span>
                    <span className="hidden sm:inline">
                      {activeTab === 'category'
                        ? `${categories.find((c) => String(c.id) === String(activeCategoryId))?.name || 'Ordner'} · ${filteredEmails.length} E-Mails · ${conversationThreads.length}${conversationThreads.length > visibleThreadLimit ? ` (angezeigt: ${visibleThreadLimit})` : ''} ${conversationThreads.length === 1 ? 'Kontakt' : 'Kontakte'}`
                        : `${FOLDER_LABELS[activeTab] || 'Empfangen'} · ${filteredEmails.length}${inboxTotal > filteredEmails.length && activeTab === 'inbox' ? ` / ${inboxTotal}` : ''} E-Mails · ${conversationThreads.length}${conversationThreads.length > visibleThreadLimit ? ` (angezeigt: ${visibleThreadLimit})` : ''} ${conversationThreads.length === 1 ? 'Kontakt' : 'Kontakte'}`}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="sm:hidden tabular-nums">
                      {sentThreads.length} Kontakte
                    </span>
                    <span className="hidden sm:inline">
                      Gesendet · {sentThreads.length}
                      {sentTotal > sentEmails.length ? ` / ${sentTotal}` : ''}
                      {sentThreads.length > visibleThreadLimit
                        ? ` (angezeigt: ${visibleThreadLimit})`
                        : ''}{' '}
                      {sentThreads.length === 1 ? 'Kontakt' : 'Kontakte'}
                    </span>
                  </>
                )}
              </span>
            </div>
            {isInboxMailbox(activeTab) && conversationThreads.length > 0 ? (
              <div className="flex items-center gap-2">
                {selectedMailUids.size > 0 ? (
                  <>
                    <span className="text-[11px] tabular-nums text-neutral-500 dark:text-neutral-400">
                      {selectedMailUids.size}
                    </span>
                    <div className="inline-flex items-stretch rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden divide-x divide-neutral-200 dark:divide-neutral-700 bg-white dark:bg-neutral-900">
                      <button
                        type="button"
                        className="px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                        onClick={clearMailSelection}
                        disabled={!!bulkDeleting}
                      >
                        Aufheben
                      </button>
                      {activeTab === 'category' ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                          onClick={removeSelectedFromActiveCategory}
                          disabled={!!bulkDeleting || !activeCategoryId}
                          title="Aus diesem Ordner entfernen"
                        >
                          <FolderMinus className="w-3 h-3" />
                          Aus Ordner
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-default"
                          onClick={selectAllVisibleThreads}
                          disabled={!!bulkDeleting || allVisibleMailsSelected}
                          title={allVisibleMailsSelected ? 'Alle bereits ausgewählt' : 'Alle auswählen'}
                        >
                          Alle
                        </button>
                      )}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-danger-700 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-950/40 disabled:opacity-50"
                        onClick={deleteSelectedMails}
                        disabled={!!bulkDeleting}
                      >
                        <Trash2 className="w-3 h-3" />
                        {activeTab === 'trash' ? 'Endgültig' : 'Löschen'}
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                    onClick={selectAllVisibleThreads}
                    disabled={!!bulkDeleting}
                  >
                    <CheckSquare className="w-3 h-3" />
                    Alle auswählen
                  </button>
                )}
              </div>
            ) : activeTab === 'sent' && sentThreads.length > 0 ? (
              <div className="flex items-center gap-2">
                {selectedSentKeys.size > 0 ? (
                  <>
                    <span className="text-[11px] tabular-nums text-neutral-500 dark:text-neutral-400">
                      {selectedSentKeys.size}
                    </span>
                    <div className="inline-flex items-stretch rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden divide-x divide-neutral-200 dark:divide-neutral-700 bg-white dark:bg-neutral-900">
                      <button
                        type="button"
                        className="px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                        onClick={clearSentMailSelection}
                        disabled={!!bulkDeleting}
                      >
                        Aufheben
                      </button>
                      <button
                        type="button"
                        className="px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-default"
                        onClick={selectAllVisibleSent}
                        disabled={!!bulkDeleting || allVisibleSentSelected}
                        title={allVisibleSentSelected ? 'Alle bereits ausgewählt' : 'Alle auswählen'}
                      >
                        Alle
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-danger-700 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-950/40 disabled:opacity-50"
                        onClick={deleteSelectedSentMails}
                        disabled={!!bulkDeleting}
                      >
                        <Trash2 className="w-3 h-3" />
                        Löschen
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2.5 py-1.5 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                    onClick={selectAllVisibleSent}
                    disabled={!!bulkDeleting}
                  >
                    <CheckSquare className="w-3 h-3" />
                    Alle auswählen
                  </button>
                )}
              </div>
            ) : null}
          </div>
        
          <div ref={listScrollRef} className="flex-1 overflow-y-auto min-h-0">
            {activeTab === 'drafts' ? (
              composerDrafts.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <FileText className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Keine Entwürfe</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Beim Schreiben werden Entwürfe automatisch hier gespeichert.
                  </p>
                  <button type="button" onClick={openNewComposer} className="btn-primary mt-4 !py-2 !px-3 text-xs">
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Neue E-Mail
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {composerDrafts.map((draft) => {
                    const isOpen = showComposer && String(composerDraftId) === String(draft.id);
                    const attCount = composerDraftAttachmentCount(draft);
                    return (
                      <div
                        key={draft.id}
                        className={`flex items-stretch border-l-2 ${
                          isOpen
                            ? 'border-neutral-900 dark:border-white bg-neutral-100 dark:bg-neutral-800'
                            : 'border-transparent hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => openComposerFromDraft(draft)}
                          className="min-w-0 flex-1 text-left px-4 py-3"
                        >
                          <div className="flex items-start gap-3">
                            <div className="relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
                              <FileText className="w-4 h-4" />
                              {attCount > 0 ? (
                                <span
                                  className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center"
                                  title={`${attCount} Anhang${attCount === 1 ? '' : 'e'}`}
                                >
                                  <Paperclip className="w-2.5 h-2.5 text-neutral-600 dark:text-neutral-300" />
                                </span>
                              ) : null}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                                  {composerDraftLabel(draft)}
                                </p>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  {attCount > 0 ? (
                                    <Paperclip
                                      className="w-3.5 h-3.5 text-neutral-400"
                                      title={`${attCount} Anhang${attCount === 1 ? '' : 'e'}`}
                                      aria-label="Anhang"
                                    />
                                  ) : null}
                                  <span className="text-[11px] text-neutral-400">
                                    {draft.savedAt
                                      ? formatMailDateTime(new Date(draft.savedAt).toISOString())
                                      : ''}
                                  </span>
                                </div>
                              </div>
                              <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate mt-0.5">
                                {draft.to ? `An ${draft.to}` : 'Kein Empfänger'}
                              </p>
                              <p className="text-[11px] text-neutral-400 mt-1">
                                Entwurf
                                {attCount > 0
                                  ? ` · ${attCount} Anhang${attCount === 1 ? '' : 'e'}`
                                  : ''}
                              </p>
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => requestDeleteComposerDraft(draft, e)}
                          className="flex-shrink-0 self-center mr-2 inline-flex items-center justify-center w-8 h-8 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                          title="Entwurf löschen"
                          aria-label="Entwurf löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )
            ) : isInboxMailbox(activeTab) ? (
              loading && filteredEmails.length === 0 && activeCategoryChildren.length === 0 ? (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800" aria-busy="true">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div key={i} className="px-4 py-3 flex items-start gap-3">
                      <SkeletonCircle size={36} className="flex-shrink-0" />
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <Skeleton className="h-3.5 w-[45%]" />
                          <Skeleton className="h-2.5 w-10" />
                        </div>
                        <Skeleton className="h-3.5 w-[75%]" />
                        <Skeleton className="h-2.5 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : conversationThreads.length === 0 && activeCategoryChildren.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <Inbox className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Keine E-Mails</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {searchTerm
                      ? 'Keine Treffer.'
                      : activeTab === 'important'
                        ? 'Noch keine als wichtig markierten E-Mails.'
                        : activeTab === 'unread'
                          ? 'Keine ungelesenen E-Mails.'
                          : activeTab === 'category'
                            ? 'In diesem Ordner liegen noch keine E-Mails.'
                          : 'Keine empfangenen E-Mails.'}
                </p>
              </div>
            ) : (
              <>
                {activeCategoryChildren.length > 0 ? (
                  <div className="border-b border-neutral-100 dark:border-neutral-800">
                    <p className="px-4 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                      Unterordner
                    </p>
                    <div className="flex flex-col divide-y divide-neutral-100 dark:divide-neutral-800">
                      {activeCategoryChildren.map((child) => {
                        const childHasChildren =
                          Array.isArray(child.children) && child.children.length > 0;
                        const childColor = child.color || '#0f766e';
                        return (
                          <button
                            key={child.id}
                            type="button"
                            onClick={() => switchTab('category', { category: child.id })}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors"
                          >
                            {childHasChildren ? (
                              <Folder
                                className="w-4 h-4 flex-shrink-0"
                                style={{ color: childColor }}
                                fill={childColor}
                                fillOpacity={0.22}
                              />
                            ) : (
                              <Mail className="w-4 h-4 flex-shrink-0" style={{ color: childColor }} />
                            )}
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-neutral-800 dark:text-neutral-100">
                              {child.name}
                            </span>
                            {Number(child.itemCount) > 0 ? (
                              <span className="text-[11px] tabular-nums text-neutral-400 flex-shrink-0">
                                {child.itemCount}
                              </span>
                            ) : null}
                            <ChevronDown className="w-3.5 h-3.5 text-neutral-300 dark:text-neutral-600 -rotate-90 flex-shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {conversationThreads.length === 0 ? (
                  activeCategoryChildren.length > 0 ? (
                    <div className="text-center py-10 px-4">
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Keine E-Mails direkt in diesem Ordner — öffne einen Unterordner oben.
                      </p>
                    </div>
                  ) : null
                ) : (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {visibleConversationThreads.map((thread) => {
                      const isUnread = thread.unreadCount > 0;
                      const isSelected = selectedThreadId === thread.id;
                      const isListOpen = listExpandedThreadId === thread.id;
                      const isBulkDeleting = bulkDeleting?.threadId === thread.id;
                      const latest = thread.latest || {};
                      const threadUids = (thread.emails || []).map((m) => String(m.uid)).filter(Boolean);
                      const selectedInThread = threadUids.filter((u) => selectedMailUids.has(u)).length;
                      const threadChecked =
                        threadUids.length > 0 && selectedInThread === threadUids.length;
                      const threadIndeterminate =
                        selectedInThread > 0 && selectedInThread < threadUids.length;
                      const threadLeaving =
                        threadUids.length > 0 && threadUids.every((u) => leavingUids.has(u));
                      const leaveClass = threadLeaving
                        ? `mail-leave ${leavingKind === 'spam' ? 'mail-leave-spam' : 'mail-leave-delete'}`
                        : '';
                      return (
                        <div
                          key={thread.id}
                          onContextMenu={(e) => openThreadContextMenu(e, thread)}
                          className={`overflow-hidden border-l-2 ${leaveClass} ${
                            isSelected
                              ? 'border-neutral-900 dark:border-white'
                              : 'border-transparent'
                          }`}
                        >
                          <div
                            className={`flex items-stretch ${
                              isSelected
                                ? 'bg-neutral-100 dark:bg-neutral-800'
                                : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
                            }`}
                          >
                            <label
                              className="flex items-center justify-center w-11 min-h-[44px] pl-1 pr-0 cursor-pointer flex-shrink-0 touch-manipulation"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                className="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 w-4 h-4"
                                checked={threadChecked}
                                ref={(el) => {
                                  if (el) el.indeterminate = threadIndeterminate;
                                }}
                                disabled={!!bulkDeleting || isBulkDeleting}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleThreadSelected(thread, e.target.checked);
                                }}
                                aria-label={`${thread.fromName || 'Konversation'} auswählen`}
                              />
                            </label>
                            <button
                              type="button"
                              draggable={!isBulkDeleting && activeTab !== 'sent'}
                              onDragStart={(e) => {
                                if (isBulkDeleting || activeTab === 'sent') {
                                  e.preventDefault();
                                  return;
                                }
                                const dragEmails =
                                  selectedInThread > 0
                                    ? (thread.emails || []).filter((m) =>
                                        selectedMailUids.has(String(m.uid))
                                      )
                                    : thread.emails;
                                startMailDrag(e, dragEmails?.length ? dragEmails : thread.emails, {
                                  kind: selectedInThread > 0 ? 'selection' : 'thread',
                                  title:
                                    selectedInThread > 1
                                      ? `${selectedInThread} E-Mails`
                                      : thread.fromName || thread.id || 'Konversation',
                                  subtitle: latest.subject || `${thread.count} Nachrichten`,
                                  initials: getInitials(thread.fromName),
                                });
                              }}
                              onDragEnd={finishMailDrag}
                              onClick={() => toggleListThread(thread)}
                              className={`min-w-0 flex-1 text-left px-2 py-3 transition-colors ${
                                !isBulkDeleting && activeTab !== 'sent'
                                  ? 'cursor-grab active:cursor-grabbing'
                                  : ''
                              }`}
                            >
                            <div className="flex items-start gap-3">
                              <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                                  isBulkDeleting
                                    ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300'
                                    : isUnread
                                      ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                                      : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
                                }`}
                              >
                                {isBulkDeleting ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  getInitials(thread.fromName)
                                )}
                        </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline justify-between gap-2">
                                  <p className={`text-sm truncate ${isUnread ? 'font-semibold text-neutral-900 dark:text-white' : 'font-medium text-neutral-700 dark:text-neutral-300'}`}>
                                    {thread.fromName}
                                  </p>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {thread.hasAttachment ? (
                                      <Paperclip
                                        className="w-3.5 h-3.5 text-neutral-400"
                                        title="Anhang"
                                        aria-label="Anhang"
                                      />
                                    ) : null}
                                    {thread.isImportant ? (
                                      <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                                    ) : null}
                                    <span className="text-[11px] text-neutral-400">{formatDate(thread.latestDate)}</span>
                                    <ChevronDown
                                      className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-200 ${
                                        isListOpen ? 'rotate-180' : ''
                                      }`}
                                    />
                      </div>
                                </div>
                                <p className={`text-sm truncate mt-0.5 ${isUnread ? 'font-medium text-neutral-800 dark:text-neutral-200' : 'text-neutral-600 dark:text-neutral-400'}`}>
                                  {latest.subject || '(Kein Betreff)'}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[11px] text-neutral-400 truncate">
                                    {isBulkDeleting
                                      ? `${bulkDeleting.done} / ${bulkDeleting.total} gelöscht`
                                      : threadLeaving && leavingKind === 'spam'
                                        ? 'Wird als Spam markiert…'
                                      : `${thread.count} ${thread.count === 1 ? 'Nachricht' : 'Nachrichten'}`}
                            </span>
                                  {!isBulkDeleting && thread.unreadCount > 0 && (
                                    <span className="text-[10px] font-semibold text-primary-600 dark:text-primary-400 flex-shrink-0">
                                      {thread.unreadCount} neu
                                    </span>
                                  )}
                          </div>
                          </div>
                        </div>
                          </button>
                          </div>

                          {isListOpen && (
                            <div className="pb-2 pl-12 pr-3 space-y-0.5">
                              {thread.emails.slice(0, visibleMessageLimit).map((msg, msgIndex) => {
                                const msgUnread = !(msg.flags && msg.flags.includes('\\Seen'));
                                const isActive = String(selectedInboxUid) === String(msg.uid);
                                const isBusyDeleting = !!bulkDeleting?.currentUids?.includes(String(msg.uid));
                                const isLeaving = leavingUids.has(String(msg.uid));
                                return (
                                  <div
                                    key={msg.uid || `${thread.id}-${msgIndex}`}
                                    className={`flex items-stretch gap-0.5 rounded-lg ${
                                      isLeaving && !threadLeaving
                                        ? `mail-leave ${leavingKind === 'spam' ? 'mail-leave-spam' : 'mail-leave-delete'}`
                                        : !isBusyDeleting && !isLeaving && activeTab !== 'sent'
                                          ? 'cursor-grab active:cursor-grabbing'
                                          : ''
                                    }`}
                                    draggable={!isBusyDeleting && !isLeaving && activeTab !== 'sent'}
                                    onContextMenu={(e) => openThreadContextMenu(e, thread, msg)}
                                    onDragStart={(e) => {
                                      e.stopPropagation();
                                      if (isBusyDeleting || isLeaving || activeTab === 'sent') {
                                        e.preventDefault();
                                        return;
                                      }
                                      startMailDrag(e, [msg], {
                                        kind: 'message',
                                        title: msg.subject || '(Kein Betreff)',
                                        subtitle: thread.fromName || thread.fromAddr || 'E-Mail',
                                        initials: getInitials(thread.fromName || msg.from),
                                      });
                                    }}
                                    onDragEnd={(e) => {
                                      e.stopPropagation();
                                      finishMailDrag();
                                    }}
                                  >
                                    <label
                                      className="flex items-center pl-1 pr-0.5 cursor-pointer flex-shrink-0"
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        className="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 w-3.5 h-3.5"
                                        checked={selectedMailUids.has(String(msg.uid))}
                                        disabled={!!bulkDeleting || isBusyDeleting}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          toggleMailUidSelected(msg.uid, e.target.checked);
                                        }}
                                        aria-label="E-Mail auswählen"
                                      />
                                    </label>
                                    <button
                                      type="button"
                                      draggable={false}
                                      onClick={(e) => handleListMessageClick(thread, msg, e)}
                                      className={`min-w-0 flex-1 text-left rounded-lg px-2.5 py-2 transition-colors ${
                                        isBusyDeleting || (isLeaving && leavingKind === 'delete')
                                          ? 'bg-danger-50 dark:bg-danger-950/30 text-neutral-800 dark:text-neutral-100'
                                          : isLeaving && leavingKind === 'spam'
                                            ? 'bg-amber-50 dark:bg-amber-950/30 text-neutral-800 dark:text-neutral-100'
                                          : isActive
                                            ? 'bg-primary-50 dark:bg-primary-900/25 text-neutral-900 dark:text-white'
                                            : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/80 text-neutral-700 dark:text-neutral-300'
                                      }`}
                                    >
                                      <div className="flex items-baseline justify-between gap-2">
                                        <p className={`text-[12px] truncate min-w-0 ${msgUnread || isActive || isBusyDeleting ? 'font-semibold' : 'font-medium'}`}>
                                          {msg.subject || '(Kein Betreff)'}
                                        </p>
                                        <span className="flex items-center gap-1.5 flex-shrink-0">
                                          {emailHasAttachment(msg) ? (
                                            <Paperclip
                                              className="w-3 h-3 text-neutral-400"
                                              title="Anhang"
                                              aria-label="Anhang"
                                            />
                                          ) : null}
                                          {isFlagged(msg.flags) ? (
                                            <Star className="w-3 h-3 text-amber-500 fill-amber-500" aria-hidden />
                                          ) : null}
                                          {isBusyDeleting ? (
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300">
                                              <RefreshCw className="w-3 h-3 animate-spin" />
                              </span>
                            ) : (
                                            <span className="text-[10px] text-neutral-400">{formatDate(msg.date)}</span>
                                          )}
                                        </span>
                                      </div>
                                      {msgUnread ? (
                                        <span className="text-[10px] text-primary-600 dark:text-primary-400">Ungelesen</span>
                                      ) : null}
                                    </button>
                            <button
                                      type="button"
                                      draggable={false}
                              onClick={(e) => {
                                e.stopPropagation();
                                        toggleEmailImportant(msg, e);
                                      }}
                                      onDragStart={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      disabled={flagBusyUid === String(msg.uid)}
                                      className={`flex-shrink-0 self-center inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
                                        isFlagged(msg.flags)
                                          ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                                          : 'text-neutral-400 hover:text-amber-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                                      }`}
                                      title={isFlagged(msg.flags) ? 'Wichtig entfernen' : 'Als wichtig markieren'}
                                    >
                                      <Star className={`w-3.5 h-3.5 ${isFlagged(msg.flags) ? 'fill-current' : ''}`} />
                            </button>
                                  </div>
                                );
                              })}
                              {thread.emails.length > visibleMessageLimit && (
                            <button
                                  type="button"
                                  onClick={() => setVisibleMessageLimit((n) => n + MESSAGES_PAGE_SIZE)}
                                  className="w-full text-left rounded-lg px-2.5 py-2 text-[11px] font-medium text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800/80 hover:text-neutral-700 dark:hover:text-neutral-300"
                                >
                                  Weitere Nachrichten laden
                                  {` (${visibleMessageLimit} / ${thread.emails.length})`}
                            </button>
                              )}
                          </div>
                          )}
                        </div>
                      );
                    })}
                  {conversationThreads.length > visibleThreadLimit && (
                    <div className="px-4 py-3 border-t border-neutral-100 dark:border-neutral-800">
                      <button
                        type="button"
                        onClick={() => setVisibleThreadLimit((n) => n + THREADS_PAGE_SIZE)}
                        className="w-full btn-secondary !py-2 text-sm justify-center"
                      >
                        Weitere Kontakte laden
                        {` (${visibleThreadLimit} / ${conversationThreads.length})`}
                      </button>
                      </div>
                  )}
                  <div ref={loadMoreSentinelRef} className="h-4" aria-hidden />
                  {!searchTerm && (loadingMore || inboxHasMore || (inboxTotal > 0 && emails.length < inboxTotal)) && (
                    <div className="px-4 py-3 border-t border-neutral-100 dark:border-neutral-800">
                      <div className="w-full flex items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 py-1">
                        {loadingMore || inboxFillInFlightRef.current ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Lade ältere E-Mails…
                            {inboxTotal > emails.length ? ` (${emails.length} / ${inboxTotal})` : ''}
                          </>
                        ) : (
                          <>
                            {emails.length} / {inboxTotal || emails.length} E-Mails im Cache
                          </>
                        )}
                    </div>
                  </div>
                  )}
              </div>
                )}
              </>
            )
            ) : sentThreads.length === 0 ? (
              <div className="text-center py-16 px-4">
                <Send className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Keine gesendeten E-Mails</p>
                <button onClick={openNewComposer} className="btn-primary mt-4 !py-2 !px-3 text-sm">
                  <Plus className="w-4 h-4 mr-1.5" />
                  Neue Email
                </button>
              </div>
            ) : (
              <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {visibleSentThreads.map((thread) => {
                    const selectedAddr = getSelectedSentThreadAddr();
                    const isSelected = selectedAddr === thread.id;
                    const isListOpen = listExpandedThreadId === thread.id;
                    const latest = thread.latest || {};
                    const threadKeys = (thread.emails || []).map(getSentMailKey).filter(Boolean);
                    const selectedInThread = threadKeys.filter((k) => selectedSentKeys.has(k)).length;
                    const threadChecked =
                      threadKeys.length > 0 && selectedInThread === threadKeys.length;
                    const threadIndeterminate =
                      selectedInThread > 0 && selectedInThread < threadKeys.length;
                    const isBulkDeleting = bulkDeleting?.threadId === 'sent';
                    return (
                      <div
                        key={thread.id}
                        onContextMenu={(e) => openThreadContextMenu(e, thread)}
                        className={`overflow-hidden border-l-2 ${
                          isSelected
                            ? 'border-neutral-900 dark:border-white'
                            : 'border-transparent'
                        }`}
                      >
                        <div
                          className={`flex items-stretch ${
                            isSelected
                              ? 'bg-neutral-100 dark:bg-neutral-800'
                              : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
                          }`}
                        >
                          <label
                            className="flex items-center pl-3 pr-1 cursor-pointer flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 w-3.5 h-3.5"
                              checked={threadChecked}
                              ref={(el) => {
                                if (el) el.indeterminate = threadIndeterminate;
                              }}
                              disabled={!!bulkDeleting || isBulkDeleting}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSentThreadSelected(thread, e.target.checked);
                              }}
                              aria-label={`${thread.toName || 'Konversation'} auswählen`}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => toggleListSentThread(thread)}
                            className="min-w-0 flex-1 text-left px-2 py-3 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-full bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                {getInitials(thread.toName)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline justify-between gap-2">
                                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 truncate">
                                    {thread.toName}
                                  </p>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {thread.hasAttachment ? (
                                      <Paperclip
                                        className="w-3.5 h-3.5 text-neutral-400"
                                        title="Anhang"
                                        aria-label="Anhang"
                                      />
                                    ) : null}
                                    {Number(thread.latest?.opened) === 1 && (
                                      <span
                                        className="inline-flex items-center gap-0.5 text-[10px] font-medium text-success-700 dark:text-success-400"
                                        title="Die neueste Mail wurde geöffnet"
                                      >
                                        <Eye className="w-3 h-3" />
                                        Geöffnet
                                      </span>
                                    )}
                                    <span className="text-[11px] text-neutral-400">{formatDate(thread.latestDate)}</span>
                                    <ChevronDown
                                      className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-200 ${
                                        isListOpen ? 'rotate-180' : ''
                                      }`}
                                    />
                                  </div>
                                </div>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate mt-0.5">
                                  {latest.subject || '(Kein Betreff)'}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[11px] text-neutral-400 truncate">
                                    {thread.count} {thread.count === 1 ? 'Nachricht' : 'Nachrichten'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>

                        {isListOpen && (
                          <div className="pb-2 pl-12 pr-3 space-y-0.5">
                            {thread.emails.slice(0, visibleMessageLimit).map((msg, msgIndex) => {
                              const isActive = isSameSentListEmail(selectedSentEmail, msg);
                              const msgKey = getSentMailKey(msg);
                              return (
                                <div
                                  key={msg.id || `${thread.id}-${msgIndex}`}
                                  className="flex items-stretch gap-0.5 rounded-lg"
                                  onContextMenu={(e) => openThreadContextMenu(e, thread, msg)}
                                >
                                  <label
                                    className="flex items-center pl-1 pr-0.5 cursor-pointer flex-shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      className="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 w-3.5 h-3.5"
                                      checked={msgKey ? selectedSentKeys.has(msgKey) : false}
                                      disabled={!!bulkDeleting}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        toggleSentKeySelected(msg, e.target.checked);
                                      }}
                                      aria-label="E-Mail auswählen"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={(e) => handleListSentMessageClick(thread, msg, e)}
                                    className={`min-w-0 flex-1 text-left rounded-lg px-2.5 py-2 transition-colors ${
                                      isActive
                                        ? 'bg-primary-50 dark:bg-primary-900/25 text-neutral-900 dark:text-white'
                                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/80 text-neutral-700 dark:text-neutral-300'
                                    }`}
                                  >
                                    <div className="flex items-baseline justify-between gap-2">
                                      <p className={`text-[12px] truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>
                                        {msg.subject || '(Kein Betreff)'}
                                      </p>
                                      <span className="inline-flex items-center gap-1.5 flex-shrink-0">
                                        {emailHasAttachment(msg) ? (
                                          <Paperclip
                                            className="w-3 h-3 text-neutral-400"
                                            title="Anhang"
                                            aria-label="Anhang"
                                          />
                                        ) : null}
                                        {Number(msg.opened) === 1 && (
                                          <span className="text-[10px] text-success-600 dark:text-success-400" title={msg.opened_at ? `Geöffnet: ${formatDate(msg.opened_at)}` : 'Geöffnet'}>
                                            <Eye className="w-3 h-3" />
                                          </span>
                                        )}
                                        <span className="text-[10px] text-neutral-400">{formatDate(msg.sent_at)}</span>
                                      </span>
                                    </div>
                                  </button>
                                </div>
                              );
                            })}
                            {thread.emails.length > visibleMessageLimit && (
                              <button
                                type="button"
                                onClick={() => setVisibleMessageLimit((n) => n + MESSAGES_PAGE_SIZE)}
                                className="w-full text-left rounded-lg px-2.5 py-2 text-[11px] font-medium text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800/80 hover:text-neutral-700 dark:hover:text-neutral-300"
                              >
                                Weitere Nachrichten laden
                                {` (${visibleMessageLimit} / ${thread.emails.length})`}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                {sentThreads.length > visibleThreadLimit && (
                  <div className="px-4 py-3 border-t border-neutral-100 dark:border-neutral-800">
                    <button
                      type="button"
                      onClick={() => setVisibleThreadLimit((n) => n + THREADS_PAGE_SIZE)}
                      className="w-full btn-secondary !py-2 text-sm justify-center"
                    >
                      Weitere Kontakte laden
                      {` (${visibleThreadLimit} / ${sentThreads.length})`}
                    </button>
          </div>
                )}
                <div ref={loadMoreSentSentinelRef} className="h-4" aria-hidden />
                {!searchTerm && (loadingMore || sentHasMore || (sentTotal > 0 && sentEmails.length < sentTotal)) && (
                  <div className="px-4 py-3 border-t border-neutral-100 dark:border-neutral-800">
                    <div className="w-full flex items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 py-1">
                      {loadingMore || sentFillInFlightRef.current ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Lade ältere gesendete E-Mails…
                          {sentTotal > sentEmails.length ? ` (${sentEmails.length} / ${sentTotal})` : ''}
                        </>
                      ) : (
                        <>
                          {sentEmails.length} / {sentTotal || sentEmails.length} E-Mails geladen
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

        {/* Reading pane */}
        <div
          className={`flex-1 min-w-0 min-h-0 flex flex-col bg-neutral-50/50 dark:bg-neutral-950 ${
            (selectedThreadId || selectedSentEmail || isComposing) ? 'flex' : 'hidden lg:flex'
          }`}
        >
          {isComposing ? (
            readingComposer
          ) : isInboxMailbox(activeTab) && selectedThreadId ? (
            inboxDetailLoading && !selectedInboxEmail ? (
              <div className="flex-1 min-h-0 flex flex-col bg-white dark:bg-neutral-900 fade-in" aria-busy="true">
                <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 px-3 sm:px-4 py-2">
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={clearInboxSelection}
                      className="p-2 -ml-1 rounded-lg text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
                      title="Schließen"
                      aria-label="E-Mail schließen"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <SkeletonCircle size={28} />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Skeleton className="h-3.5 w-[55%]" />
                      <Skeleton className="h-2.5 w-[38%]" />
              </div>
            </div>
                  <div className="mt-2.5 flex gap-1.5">
                    <Skeleton className="h-6 w-20 rounded-lg" />
                    <Skeleton className="h-6 w-14 rounded-lg" />
                    <Skeleton className="h-6 w-24 rounded-lg" />
                </div>
                </div>
                <div className="flex-1 px-3 sm:px-4 py-4 space-y-3">
                  <Skeleton className="h-2.5 w-full" />
                  <Skeleton className="h-2.5 w-[90%]" />
                  <Skeleton className="h-2.5 w-[70%]" />
                  <Skeleton className="h-2.5 w-[85%]" />
                  <Skeleton className="h-2.5 w-[55%]" />
                  </div>
              </div>
            ) : selectedInboxEmail ? (
              <>
                <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 sm:px-4 py-2 min-w-0">
                  {(() => {
                    const fromName =
                      selectedThread?.fromName ||
                      extractName(selectedInboxEmail?.from) ||
                      '';
                    const fromAddr =
                      selectedThread?.fromAddr ||
                      extractEmail(selectedInboxEmail?.from) ||
                      selectedInboxEmail?.from ||
                      '';
                    const knownSender = isKnownContactEmail(fromAddr);
                    const actionChip =
                      'inline-flex items-center justify-center gap-1 h-9 flex-shrink-0 whitespace-nowrap rounded-lg text-xs font-medium px-2.5 touch-manipulation';
                    const actionChipGhost = `${actionChip} border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800`;
                    return (
                      <>
                  <div className="flex items-start gap-2 min-w-0">
                    <button
                      type="button"
                      onClick={clearInboxSelection}
                      className="p-2 -ml-1 -mt-0.5 rounded-lg text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation flex-shrink-0"
                      title="Schließen"
                      aria-label="E-Mail schließen"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                      {getInitials(fromName || selectedInboxEmail?.from)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base font-semibold text-neutral-900 dark:text-white truncate leading-snug">
                        {selectedInboxEmail.subject || '(Kein Betreff)'}
                      </h2>
                      <div className="mt-0.5 min-w-0 text-[11px] leading-snug text-neutral-500">
                        {knownSender && fromAddr ? (
                          <button
                            type="button"
                            onClick={() =>
                              openContactDetailsFromEmail(fromAddr, { name: fromName })
                            }
                            className="block max-w-full text-left truncate hover:text-primary-700 dark:hover:text-primary-300 hover:underline underline-offset-2 decoration-primary-400/60"
                            title="Kontaktdetails öffnen"
                          >
                            <span className="text-neutral-700 dark:text-neutral-300">
                              {fromName || fromAddr}
                            </span>
                            {fromName && fromAddr && fromName.toLowerCase() !== String(fromAddr).toLowerCase() ? (
                              <span className="text-neutral-400"> · {fromAddr}</span>
                            ) : null}
                          </button>
                        ) : (
                          <p className="truncate">
                            <span className="text-neutral-700 dark:text-neutral-300">
                              {fromName || fromAddr || '—'}
                            </span>
                            {fromName && fromAddr && fromName.toLowerCase() !== String(fromAddr).toLowerCase() ? (
                              <span className="text-neutral-400"> · {fromAddr}</span>
                            ) : null}
                          </p>
                        )}
                        <p className="truncate text-[10px] text-neutral-400 mt-0.5">
                          {formatDate(selectedInboxEmail.date)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-1.5 min-w-0">
                    <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-0.5 px-0.5">
                      <button
                        type="button"
                        onClick={() => openReplyComposer('reply', selectedInboxEmail)}
                        className={`${actionChip} bg-primary-600 hover:bg-primary-700 text-white`}
                      >
                        <Reply className="w-3.5 h-3.5" />
                        Antworten
                      </button>
                      <button
                        type="button"
                        disabled={aiReplyLoading}
                        onClick={handleAiReplyQuick}
                        className={`${actionChipGhost} border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300`}
                        title="KI schlägt eine Antwort vor — bearbeiten und absenden"
                      >
                        {aiReplyLoading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        KI-Antwort
                      </button>
                      <button
                        type="button"
                        onClick={() => openReplyComposer('replyAll', selectedInboxEmail)}
                        className={actionChipGhost}
                        title="Allen antworten"
                      >
                        <ReplyAll className="w-3.5 h-3.5" />
                        Allen
                      </button>
                      <button
                        type="button"
                        onClick={() => openReplyComposer('forward', selectedInboxEmail)}
                        className={actionChipGhost}
                        title="Weiterleiten"
                      >
                        <Forward className="w-3.5 h-3.5" />
                        <span className="sm:hidden">Weiter</span>
                        <span className="hidden sm:inline">Weiterleiten</span>
                      </button>
                      {inboxCounterpartEmail ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleInboxSentHistoryPref();
                          }}
                          className={`${actionChip} border ${
                            inboxSentHistoryVisible
                              ? 'border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-950/40 text-sky-800 dark:text-sky-200'
                              : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                          }`}
                          title={
                            inboxSentHistoryVisible
                              ? 'Gesendete-Mails-Verlauf ausblenden'
                              : 'Gesendete-Mails-Verlauf anzeigen'
                          }
                        >
                          <History className="w-3.5 h-3.5" />
                          Verlauf
                          {inboxSentHistoryVisible &&
                          !inboxSentHistoryLoading &&
                          inboxSentHistory.length > 0 ? (
                            <span className="tabular-nums opacity-70">{inboxSentHistory.length}</span>
                          ) : null}
                        </button>
                      ) : null}
                      {/* Desktop: weitere Aktionen direkt */}
                      <div className="hidden sm:contents">
                        {selectedInboxEmail?.subscription?.canUnsubscribe ? (
                          <button
                            type="button"
                            disabled={unsubscribing}
                            onClick={() => unsubscribeFromEmail(selectedInboxEmail)}
                            className={`${actionChip} border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50`}
                          >
                            <UserMinus className="w-3.5 h-3.5" />
                            {unsubscribing ? '…' : 'Abmelden'}
                          </button>
                        ) : null}
                        {canOpenContactFromSelected ? (
                          <button
                            type="button"
                            onClick={() =>
                              openContactDetailsFromEmail(selectedSenderEmail, {
                                name: fromName,
                              })
                            }
                            className={`${actionChip} border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-950/30`}
                          >
                            <User className="w-3.5 h-3.5" />
                            Kontakt
                          </button>
                        ) : null}
                        {canCreateContactFromSelected ? (
                          <button
                            type="button"
                            onClick={() =>
                              openCreateContactFromSender(
                                selectedInboxEmail?.from,
                                selectedThread?.fromName,
                                selectedThread?.fromAddr || selectedSenderEmail
                              )
                            }
                            className={`${actionChip} border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-950/30`}
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            Als Kontakt
                          </button>
                        ) : null}
                        {activeTab === 'spam' ? (
                          <button
                            type="button"
                            disabled={movingSpam}
                            onClick={() => markEmailsAsNotSpam([selectedInboxEmail])}
                            className={`${actionChip} border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50`}
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Kein Spam
                          </button>
                        ) : activeTab === 'trash' ? (
                          <button
                            type="button"
                            disabled={movingSpam}
                            onClick={async () => {
                              try {
                                setMovingSpam(true);
                                await axios.post('/api/emails/move', {
                                  uids: [selectedInboxEmail.uid],
                                  from: 'trash',
                                  to: 'inbox',
                                });
                                removeEmailsLocally([selectedInboxEmail.uid]);
                                toast.success('Wiederhergestellt');
                                refreshUnreadCount();
                              } catch (err) {
                                toast.error(err.response?.data?.error || 'Wiederherstellen fehlgeschlagen');
                              } finally {
                                setMovingSpam(false);
                              }
                            }}
                            className={`${actionChip} border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50`}
                          >
                            <Inbox className="w-3.5 h-3.5" />
                            Wiederherstellen
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={movingSpam}
                            onClick={() => markEmailsAsSpam([selectedInboxEmail])}
                            className={`${actionChip} border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50`}
                          >
                            <ShieldAlert className="w-3.5 h-3.5" />
                            Spam
                          </button>
                        )}
                        {flatFolders.length > 0 && activeTab !== 'sent' ? (
                          <div className="relative flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setDetailMoreOpen(false);
                                setCategoryMenuOpen((v) => !v);
                              }}
                              className={`${actionChip} max-w-[12rem] border ${
                                selectedEmailCategories.length
                                  ? 'border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200'
                                  : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                              }`}
                            >
                              {selectedEmailCategories.length === 1 ? (
                                <Folder
                                  className="w-3.5 h-3.5 flex-shrink-0"
                                  style={{ color: selectedEmailCategories[0].color }}
                                  fill={selectedEmailCategories[0].color}
                                  fillOpacity={0.22}
                                />
                              ) : (
                                <Tag className="w-3.5 h-3.5 flex-shrink-0" />
                              )}
                              <span className="truncate">
                                {selectedEmailCategories.length === 0
                                  ? 'Ordner'
                                  : selectedEmailCategories.length === 1
                                    ? selectedEmailCategories[0].name
                                    : `${selectedEmailCategories.length} Ordner`}
                              </span>
                            </button>
                            {categoryMenuOpen ? (
                              <div className="absolute left-0 top-full mt-1 z-40 w-[min(18rem,calc(100vw-1.5rem))] max-h-64 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1">
                                {flatFolders.map((cat) => {
                                  const assigned = selectedEmailCategories.some(
                                    (c) => String(c.id) === String(cat.id)
                                  );
                                  const depth = Number(cat.depth) || 0;
                                  return (
                                    <button
                                      key={cat.id}
                                      type="button"
                                      onClick={() => assignSelectedToCategory(cat.id)}
                                      style={{ paddingLeft: `${12 + depth * 12}px` }}
                                      className={`w-full flex items-center gap-2 pr-3 py-2 text-[12px] text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 ${
                                        assigned
                                          ? 'font-medium text-primary-700 dark:text-primary-300'
                                          : 'text-neutral-700 dark:text-neutral-200'
                                      }`}
                                    >
                                      <Folder
                                        className="w-3.5 h-3.5 flex-shrink-0"
                                        style={{ color: cat.color }}
                                        fill={cat.color}
                                        fillOpacity={0.22}
                                      />
                                      <span className="flex-1 truncate">{cat.name}</span>
                                      {assigned ? (
                                        <span className="inline-flex items-center gap-0.5 text-[10px] text-danger-600 dark:text-danger-400 flex-shrink-0">
                                          <FolderMinus className="w-3 h-3" />
                                          Entfernen
                                        </span>
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => deleteInboxEmail(selectedInboxEmail)}
                          className={`${actionChip} border border-danger-200 dark:border-danger-800 text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Löschen
                        </button>
                        {isDarkMode && selectedInboxEmail.html ? (
                          <button
                            type="button"
                            onClick={toggleEmailInvert}
                            className={`${actionChip} border ${
                              invertEmailInDark
                                ? 'border-neutral-900 bg-neutral-100 text-neutral-900 dark:border-neutral-300 dark:bg-neutral-800 dark:text-white'
                                : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                            }`}
                          >
                            <Contrast className="w-3.5 h-3.5" />
                            {invertEmailInDark ? 'Originalfarben' : 'Invertieren'}
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {/* Mobile: Rest unter „Mehr“ */}
                    <div className="relative flex-shrink-0 sm:hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setCategoryMenuOpen(false);
                          setDetailMoreOpen((v) => !v);
                        }}
                        className={`${actionChipGhost} px-2.5`}
                        aria-label="Weitere Aktionen"
                        aria-expanded={detailMoreOpen}
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      {detailMoreOpen ? (
                        <>
                          <button
                            type="button"
                            className="fixed inset-0 z-30 cursor-default"
                            aria-label="Menü schließen"
                            onClick={() => setDetailMoreOpen(false)}
                          />
                          <div className="absolute right-0 top-full mt-1 z-40 w-56 max-h-[min(70dvh,22rem)] overflow-y-auto rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1">
                            {selectedInboxEmail?.subscription?.canUnsubscribe ? (
                              <button
                                type="button"
                                disabled={unsubscribing}
                                onClick={() => {
                                  setDetailMoreOpen(false);
                                  unsubscribeFromEmail(selectedInboxEmail);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50"
                              >
                                <UserMinus className="w-4 h-4" />
                                {unsubscribing ? '…' : 'Abmelden'}
                              </button>
                            ) : null}
                            {canOpenContactFromSelected ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setDetailMoreOpen(false);
                                  openContactDetailsFromEmail(selectedSenderEmail, {
                                    name: fromName,
                                  });
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-neutral-800 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                              >
                                <User className="w-4 h-4" />
                                Kontakt öffnen
                              </button>
                            ) : null}
                            {canCreateContactFromSelected ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setDetailMoreOpen(false);
                                  openCreateContactFromSender(
                                    selectedInboxEmail?.from,
                                    selectedThread?.fromName,
                                    selectedThread?.fromAddr || selectedSenderEmail
                                  );
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-neutral-800 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                              >
                                <UserPlus className="w-4 h-4" />
                                Als Kontakt speichern
                              </button>
                            ) : null}
                            {activeTab === 'spam' ? (
                              <button
                                type="button"
                                disabled={movingSpam}
                                onClick={() => {
                                  setDetailMoreOpen(false);
                                  markEmailsAsNotSpam([selectedInboxEmail]);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50"
                              >
                                <ShieldCheck className="w-4 h-4" />
                                Kein Spam
                              </button>
                            ) : activeTab === 'trash' ? (
                              <button
                                type="button"
                                disabled={movingSpam}
                                onClick={async () => {
                                  setDetailMoreOpen(false);
                                  try {
                                    setMovingSpam(true);
                                    await axios.post('/api/emails/move', {
                                      uids: [selectedInboxEmail.uid],
                                      from: 'trash',
                                      to: 'inbox',
                                    });
                                    removeEmailsLocally([selectedInboxEmail.uid]);
                                    toast.success('Wiederhergestellt');
                                    refreshUnreadCount();
                                  } catch (err) {
                                    toast.error(
                                      err.response?.data?.error || 'Wiederherstellen fehlgeschlagen'
                                    );
                                  } finally {
                                    setMovingSpam(false);
                                  }
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50"
                              >
                                <Inbox className="w-4 h-4" />
                                Wiederherstellen
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={movingSpam}
                                onClick={() => {
                                  setDetailMoreOpen(false);
                                  markEmailsAsSpam([selectedInboxEmail]);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50"
                              >
                                <ShieldAlert className="w-4 h-4" />
                                Als Spam
                              </button>
                            )}
                            {flatFolders.length > 0 && activeTab !== 'sent' ? (
                              <div className="border-t border-neutral-100 dark:border-neutral-800 py-1">
                                <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-neutral-400">
                                  Ordner
                                </p>
                                {flatFolders.map((cat) => {
                                  const assigned = selectedEmailCategories.some(
                                    (c) => String(c.id) === String(cat.id)
                                  );
                                  const depth = Number(cat.depth) || 0;
                                  return (
                                    <button
                                      key={cat.id}
                                      type="button"
                                      onClick={() => {
                                        assignSelectedToCategory(cat.id);
                                        setDetailMoreOpen(false);
                                      }}
                                      style={{ paddingLeft: `${12 + depth * 12}px` }}
                                      className={`w-full flex items-center gap-2 pr-3 py-2 text-sm text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 ${
                                        assigned
                                          ? 'font-medium text-primary-700 dark:text-primary-300'
                                          : 'text-neutral-700 dark:text-neutral-200'
                                      }`}
                                    >
                                      <Folder
                                        className="w-3.5 h-3.5 flex-shrink-0"
                                        style={{ color: cat.color }}
                                        fill={cat.color}
                                        fillOpacity={0.22}
                                      />
                                      <span className="flex-1 truncate">{cat.name}</span>
                                      {assigned ? (
                                        <FolderMinus className="w-3.5 h-3.5 text-danger-500" />
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                            {isDarkMode && selectedInboxEmail.html ? (
                              <button
                                type="button"
                                onClick={() => {
                                  toggleEmailInvert();
                                  setDetailMoreOpen(false);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-neutral-800 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 border-t border-neutral-100 dark:border-neutral-800"
                              >
                                <Contrast className="w-4 h-4" />
                                {invertEmailInDark ? 'Originalfarben' : 'Invertieren'}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => {
                                setDetailMoreOpen(false);
                                deleteInboxEmail(selectedInboxEmail);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 border-t border-neutral-100 dark:border-neutral-800"
                            >
                              <Trash2 className="w-4 h-4" />
                              Löschen
                            </button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                      </>
                    );
                  })()}
                </div>

                <div className="email-reading-scroll flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 bg-white dark:bg-neutral-900 px-4 sm:px-6 py-4 sm:py-5">
                  <div className="email-reading-inner max-w-4xl w-full min-w-0">
                    {preferences.blockExternalContent && !allowExternalImages && selectedInboxEmail.html?.includes('<img') && (
                      <div className="mb-3 p-2.5 rounded-md border border-neutral-200 dark:border-neutral-700 text-xs text-neutral-600 dark:text-neutral-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <span>Externe Bilder blockiert (Tracker-Schutz).</span>
                        <button type="button" onClick={() => setAllowExternalImages(true)} className="underline self-start sm:self-auto touch-manipulation py-1">
                          Laden
                        </button>
        </div>
      )}
                    {selectedInboxEmail.html ? (
                      <EmailHtmlBody
                        html={selectedInboxEmail.html}
                        className={emailHtmlClassName}
                        allowExternalImages={allowExternalImages}
                      />
                    ) : (
                      <div
                        className="email-body text-base leading-relaxed text-neutral-800 dark:text-neutral-200 break-words"
                        dangerouslySetInnerHTML={{ __html: plainTextToHtml(selectedInboxEmail.text) }}
                      />
                    )}

                    {selectedInboxEmail.attachments && selectedInboxEmail.attachments.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                        <div className="flex items-center text-[11px] font-medium text-neutral-500 dark:text-neutral-400 mb-1.5">
                          <Paperclip className="w-3 h-3 mr-1" />
                          Anhänge ({selectedInboxEmail.attachments.length})
              </div>
                        <div className="flex flex-col gap-1.5">
                          {selectedInboxEmail.attachments.map((attachment) => {
                            const viewUrl = getInboxAttachmentUrl(selectedInboxEmail.uid, attachment, true);
                            const downloadUrl = getInboxAttachmentUrl(selectedInboxEmail.uid, attachment, false);
                            return (
                              <div
                                key={attachment.index}
                                className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-2.5 py-2"
                              >
              <button
                                  type="button"
                                  onClick={() => openAttachment(viewUrl, attachment)}
                                  className="min-w-0 flex-1 text-left flex items-center gap-2 hover:opacity-80"
                                >
                                  <Paperclip className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[12px] font-medium text-neutral-800 dark:text-neutral-200 truncate">
                                      {attachment.filename || 'Unbenannte Datei'}
                                    </p>
                                    <p className="text-[10px] text-neutral-400 truncate">
                                      {(attachment.contentType || 'Datei')}
                                      {attachment.size ? ` · ${formatFileSize(attachment.size)}` : ''}
                                    </p>
                                  </div>
              </button>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {isPreviewableAttachment(attachment.contentType) && (
                                    <button
                                      type="button"
                                      onClick={() => openAttachment(viewUrl, attachment)}
                                      className="p-1.5 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500"
                                      title="Ansehen"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      downloadAttachmentFile(downloadUrl, attachment.filename || 'download')
                                    }
                                    className="p-1.5 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500"
                                    title="Herunterladen"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Verlauf: gesendete Mails — Timeline, kein Anhang-Look, Scroll nur über Lesebereich */}
                    {inboxSentHistoryVisible && inboxCounterpartEmail ? (
                      <div className="mt-6 pt-4 border-t border-dashed border-neutral-200 dark:border-neutral-700">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">
                          Verlauf
                          {inboxSentHistoryLoading
                            ? ' · lädt…'
                            : inboxSentHistory.length
                              ? ` · ${inboxSentHistory.length} gesendet`
                              : ' · nichts gesendet'}
                        </p>
                        {inboxSentHistoryLoading ? null : inboxSentHistory.length === 0 ? (
                          <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
                            Noch keine gesendeten Mails an diese Adresse.
                          </p>
                        ) : (
                          <div className="relative space-y-0 border-l border-neutral-200 dark:border-neutral-700 ml-1.5 pl-3">
                            {inboxSentHistory.map((item) => {
                              const open = inboxSentHistoryExpandedId === item.id;
                              const body = inboxSentHistoryBodies[item.id];
                              return (
                                <div key={item.id} className="relative py-2 first:pt-0">
                                  <span
                                    className="absolute -left-[calc(0.75rem+3.5px)] top-3.5 w-1.5 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-600"
                                    aria-hidden
                                  />
                                  <button
                                    type="button"
                                    onClick={() => toggleInboxSentHistoryItem(item)}
                                    className="w-full text-left group"
                                  >
                                    <p className="text-[12px] text-neutral-800 dark:text-neutral-100 truncate group-hover:text-sky-700 dark:group-hover:text-sky-300">
                                      {item.subject || '(Kein Betreff)'}
                                    </p>
                                    <p className="text-[10px] text-neutral-400 mt-0.5 truncate">
                                      {[
                                        item.sentAt ? formatDate(item.sentAt) : null,
                                        item.opened
                                          ? `geöffnet${item.openCount > 1 ? ` ${item.openCount}×` : ''}`
                                          : null,
                                        item.templateName || null,
                                      ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </p>
                                  </button>
                                  {open ? (
                                    <div className="mt-2 text-[12px] leading-relaxed text-neutral-600 dark:text-neutral-300">
                                      {!body ? (
                                        <p className="text-[11px] text-neutral-400">Lade Inhalt…</p>
                                      ) : body.html ? (
                                        <div
                                          className="break-words [&_img]:max-w-full [&_table]:max-w-full"
                                          dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                              stripOpenTrackingPixels(body.html),
                                              { USE_PROFILES: { html: true } }
                                            ),
                                          }}
                                        />
                                      ) : (
                                        <div
                                          className="whitespace-pre-wrap break-words"
                                          dangerouslySetInnerHTML={{
                                            __html: plainTextToHtml(body.text || ''),
                                          }}
                                        />
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null
          ) : activeTab === 'sent' && selectedSentEmail ? (
            <>
              <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 sm:px-4 py-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <button
                    type="button"
                    onClick={() => {
                      clearSentSelection();
                      setListExpandedThreadId(null);
                    }}
                    className="p-2 -ml-1 rounded-lg text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
                    title="Schließen"
                    aria-label="E-Mail schließen"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="w-7 h-7 rounded-full bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                    {getInitials(selectedSentEmail.contact_name || selectedSentEmail.to_email || selectedSentEmail.contact_email)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-neutral-900 dark:text-white truncate leading-tight">
                      {selectedSentEmail.subject || '(Kein Betreff)'}
                    </h2>
                    <p className="text-[11px] text-neutral-500 truncate leading-tight mt-0.5">
                      An:{' '}
                      {canOpenContactFromSent ? (
                        <button
                          type="button"
                          onClick={() =>
                            openContactDetailsFromEmail(selectedSentRecipientEmail, {
                              name: selectedSentEmail.contact_name || '',
                            })
                          }
                          className="hover:text-primary-700 dark:hover:text-primary-300 hover:underline underline-offset-2 decoration-primary-400/60"
                          title="Kontaktdetails öffnen"
                        >
                          {selectedSentEmail.to_email || selectedSentEmail.contact_email || '—'}
                        </button>
                      ) : (
                        selectedSentEmail.to_email || selectedSentEmail.contact_email || '—'
                      )}
                      <span className="text-neutral-400"> · {formatDate(selectedSentEmail.sent_at)}</span>
                      {Number(selectedSentEmail.opened) === 1 && (
                        <span className="ml-1.5 inline-flex items-center gap-1 text-success-600 dark:text-success-400">
                          <Eye className="w-3 h-3" />
                          Geöffnet
                          {selectedSentEmail.opened_at ? ` · ${formatDate(selectedSentEmail.opened_at)}` : ''}
                          {Number(selectedSentEmail.open_count) > 1
                            ? ` (${selectedSentEmail.open_count}×)`
                            : ''}
                        </span>
                      )}
                      {selectedSentEmail.tracking_token && Number(selectedSentEmail.opened) !== 1 && (
                        <span className="ml-1.5 text-neutral-400">· Noch nicht geöffnet</span>
                      )}
                    </p>
                  </div>
                </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={() => openReplyComposer('reply', {
                        from: selectedSentEmail.to_email || selectedSentEmail.contact_email,
                        to: selectedSentEmail.from_email,
                        subject: selectedSentEmail.subject,
                        html: selectedSentEmail.html,
                        text: selectedSentEmail.text,
                        date: selectedSentEmail.sent_at
                      })}
                      className="inline-flex items-center flex-shrink-0 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs sm:text-[11px] font-medium px-3 py-2.5 sm:px-2.5 sm:py-1 touch-manipulation"
                    >
                      <Reply className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                      Antworten
                    </button>
                    {canOpenContactFromSent ? (
                      <button
                        type="button"
                        onClick={() =>
                          openContactDetailsFromEmail(selectedSentRecipientEmail, {
                            name: selectedSentEmail.contact_name || '',
                          })
                        }
                        className="inline-flex items-center flex-shrink-0 rounded-lg border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-xs sm:text-[11px] font-medium px-3 py-2.5 sm:px-2.5 sm:py-1 hover:bg-primary-50 dark:hover:bg-primary-950/30 touch-manipulation"
                        title="Kontaktdetails öffnen"
                      >
                        <User className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                        Kontakt
                      </button>
                    ) : null}
                    {canCreateContactFromSent ? (
                      <button
                        type="button"
                        onClick={() =>
                          openCreateContactFromSender(
                            selectedSentEmail.to_email || selectedSentEmail.contact_email,
                            selectedSentEmail.contact_name || '',
                            selectedSentRecipientEmail
                          )
                        }
                        className="inline-flex items-center flex-shrink-0 rounded-lg border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-xs sm:text-[11px] font-medium px-3 py-2.5 sm:px-2.5 sm:py-1 hover:bg-primary-50 dark:hover:bg-primary-950/30 touch-manipulation"
                      >
                        <UserPlus className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                        Als Kontakt
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => deleteSentEmail(selectedSentEmail)}
                      className="inline-flex items-center flex-shrink-0 rounded-lg border border-danger-200 dark:border-danger-800 text-danger-600 dark:text-danger-400 text-xs sm:text-[11px] font-medium px-3 py-2.5 sm:px-2.5 sm:py-1 hover:bg-danger-50 dark:hover:bg-danger-900/20 touch-manipulation"
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                      Löschen
                    </button>
                    {isDarkMode && selectedSentEmail.html && (
                      <button
                        type="button"
                        onClick={toggleEmailInvert}
                        className={`inline-flex items-center flex-shrink-0 rounded-lg border text-xs sm:text-[11px] font-medium px-3 py-2.5 sm:px-2.5 sm:py-1 touch-manipulation ${
                          invertEmailInDark
                            ? 'border-neutral-900 bg-neutral-100 text-neutral-900 dark:border-neutral-300 dark:bg-neutral-800 dark:text-white'
                            : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                        }`}
                        title="Helle E-Mail-Inhalte für Dark Mode invertieren"
                      >
                        <Contrast className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                        {invertEmailInDark ? 'Originalfarben' : 'Invertieren'}
                      </button>
                    )}
                  </div>
            </div>

              <div className="email-reading-scroll flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 bg-white dark:bg-neutral-900 px-4 sm:px-6 py-4 sm:py-5">
                <div className="email-reading-inner max-w-4xl w-full min-w-0">
                  {selectedSentEmail.html ? (
                    <EmailHtmlBody
                      html={selectedSentEmail.html}
                      className={emailHtmlClassName}
                      allowExternalImages
                    />
                  ) : (
                    <div
                      className="email-body text-base leading-relaxed text-neutral-800 dark:text-neutral-200 break-words"
                      dangerouslySetInnerHTML={{ __html: plainTextToHtml(selectedSentEmail.text) }}
                    />
                  )}
                  {((selectedSentEmail.attachments && selectedSentEmail.attachments.length > 0) || selectedSentEmail.attachments_count > 0) && (
                    <div className="mt-3 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                      <div className="flex items-center text-[11px] font-medium text-neutral-500 dark:text-neutral-400 mb-1.5">
                        <Paperclip className="w-3 h-3 mr-1" />
                        Anhänge ({selectedSentEmail.attachments?.length || selectedSentEmail.attachments_count})
                      </div>
                      {selectedSentEmail.attachments && selectedSentEmail.attachments.length > 0 ? (
                        <div className="flex flex-col gap-1.5">
                          {selectedSentEmail.attachments.map((attachment) => {
                            const viewUrl = getSentAttachmentUrl(attachment, true, selectedSentEmail);
                            const downloadUrl = getSentAttachmentUrl(attachment, false, selectedSentEmail);
                            return (
                              <div
                                key={attachment.id ?? attachment.index ?? attachment.filename}
                                className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-2.5 py-2"
                              >
                <button
                  type="button"
                                  onClick={() => openAttachment(viewUrl, attachment)}
                                  className="min-w-0 flex-1 text-left flex items-center gap-2 hover:opacity-80"
                                >
                                  <Paperclip className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[12px] font-medium text-neutral-800 dark:text-neutral-200 truncate">
                                      {attachment.filename || 'Unbenannte Datei'}
                                    </p>
                                    <p className="text-[10px] text-neutral-400 truncate">
                                      {(attachment.contentType || 'Datei')}
                                      {attachment.size ? ` · ${formatFileSize(attachment.size)}` : ''}
                                    </p>
                                  </div>
                </button>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {isPreviewableAttachment(attachment.contentType) && (
                <button
                  type="button"
                                      onClick={() => openAttachment(viewUrl, attachment)}
                                      className="p-1.5 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500"
                                      title="Ansehen"
                >
                                      <Eye className="w-3.5 h-3.5" />
                </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      downloadAttachmentFile(downloadUrl, attachment.filename || 'download')
                                    }
                                    className="p-1.5 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500"
                                    title="Herunterladen"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-[11px] text-neutral-400">Anhänge werden geladen…</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              {activeTab === 'drafts' ? (
                <>
                  <FileText className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mb-4" />
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Kein Entwurf geöffnet</p>
                  <p className="text-xs text-neutral-500 mt-1 max-w-xs">
                    Wähle links einen Entwurf aus oder schreibe eine neue E-Mail.
                  </p>
                </>
              ) : (
                <>
                  <Mail className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mb-4" />
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Keine E-Mail ausgewählt</p>
                  <p className="text-xs text-neutral-500 mt-1 max-w-xs">
                    Wähle links eine Nachricht aus, um sie hier zu lesen.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Anhang-Vorschau */}
      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      {spamMarkPrompt?.emails?.length ? (
        <SpamMarkModal
          emails={spamMarkPrompt.emails}
          spamRules={spamRules}
          busy={!!movingSpam}
          onCancel={() => {
            if (!movingSpam) setSpamMarkPrompt(null);
          }}
          onConfirm={confirmMarkEmailsAsSpam}
        />
      ) : null}

      {draftToDelete && createPortal(
        <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => setDraftToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Entwurf löschen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    „{composerDraftLabel(draftToDelete)}“ wird unwiderruflich entfernt.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-1.5 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                onClick={() => setDraftToDelete(null)}
                className="inline-flex items-center justify-center rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-xs font-medium px-2.5 py-1.5 hover:bg-white dark:hover:bg-neutral-800"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={confirmDiscardComposerDraft}
                className="inline-flex items-center justify-center rounded-md bg-danger-600 hover:bg-danger-700 text-white text-xs font-medium px-2.5 py-1.5"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {mailDeletePrompt && createPortal(
        <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => setMailDeletePrompt(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    {mailDeletePrompt.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    {mailDeletePrompt.message}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                onClick={async () => {
                  const run = mailDeletePrompt.run;
                  setMailDeletePrompt(null);
                  if (typeof run === 'function') await run();
                }}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2"
              >
                {mailDeletePrompt.confirmLabel || 'Löschen'}
              </button>
              <button
                type="button"
                onClick={() => setMailDeletePrompt(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showContactModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={closeContactModal} />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <form onSubmit={saveContactFromEmail} className="flex flex-col min-h-0 flex-1">
              <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-neutral-100 dark:border-neutral-800">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Kontakt anlegen</h3>
                  <button
                    type="button"
                    onClick={closeContactModal}
                    className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Aus der E-Mail vorausgefüllt – du kannst die Daten noch anpassen.
                </p>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Name *</label>
                  <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => handleContactNameChange(e.target.value)}
                      className="input-field"
                      required
                      placeholder="z. B. Max Mustermann"
                  />
                </div>
                <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">E-Mail *</label>
                  <input
                    type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                      className="input-field"
                      required
                  />
                </div>
              <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Geschlecht</label>
                    <SelectField
                      value={contactForm.gender || 'unknown'}
                      onChange={(e) => handleContactGenderChange(e.target.value)}
                      className="input-field"
                    >
                      <option value="unknown">Keine Angabe</option>
                      <option value="male">Männlich</option>
                      <option value="female">Weiblich</option>
                    </SelectField>
                  </div>
                  <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 p-3 space-y-3 bg-neutral-50/60 dark:bg-neutral-800/40">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        2. Ansprechpartner{' '}
                        <span className="font-normal text-neutral-400">(optional)</span>
                      </label>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">
                        Bei gemeinsamer E-Mail (z. B. zwei Geschäftsführer) — Anrede wird kombiniert.
                      </p>
                      <input
                        type="text"
                        value={contactForm.name2 || ''}
                        onChange={(e) => handleContactName2Change(e.target.value)}
                        className="input-field"
                        placeholder="z. B. Erika Musterfrau"
                      />
                    </div>
                    {String(contactForm.name2 || '').trim() ? (
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                          Geschlecht (2. Person)
                        </label>
                        <SelectField
                          value={contactForm.gender2 || 'unknown'}
                          onChange={(e) => handleContactGender2Change(e.target.value)}
                          className="input-field"
                        >
                          <option value="unknown">Keine Angabe</option>
                          <option value="male">Männlich</option>
                          <option value="female">Weiblich</option>
                        </SelectField>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Unternehmen</label>
                <input
                  type="text"
                      value={contactForm.company}
                      onChange={(e) => setContactForm((prev) => ({ ...prev, company: e.target.value }))}
                      className="input-field"
                />
              </div>
              <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Telefon</label>
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm((prev) => ({ ...prev, phone: e.target.value }))}
                      className="input-field"
                      placeholder="+49 123 456 789"
                  />
                </div>
              <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Website</label>
                  <input 
                      type="text"
                      value={contactForm.website}
                      onChange={(e) => setContactForm((prev) => ({ ...prev, website: e.target.value }))}
                      className="input-field"
                      placeholder="example.com"
                    />
                  </div>
                          <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Notizen</label>
                    <textarea
                      value={contactForm.notes}
                      onChange={(e) => setContactForm((prev) => ({ ...prev, notes: e.target.value }))}
                      rows="3"
                      className="input-field resize-none"
                    />
                          </div>
                        </div>
              </div>
              <div className="flex-shrink-0 px-6 py-4 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-3 bg-white dark:bg-neutral-900">
                <button type="submit" disabled={savingContact} className="btn-primary disabled:opacity-60">
                  {savingContact ? 'Speichern…' : 'Erstellen'}
                </button>
                <button type="button" onClick={closeContactModal} disabled={savingContact} className="btn-secondary">
                  Abbrechen
                        </button>
                      </div>
            </form>
                  </div>
        </div>,
        document.body
      )}

      {viewedContact && createPortal(
        <ContactDetailModal
          contact={viewedContact}
          loading={viewedContactLoading}
          zClassName="z-[220]"
          onClose={closeViewedContact}
          onUpdated={(updated) => {
            const next = withNormalizedSubscription({
              ...updated,
              id: Number(updated.id),
            });
            setViewedContact(next);
            const key = normalizeContactEmail(next.email);
            if (key) {
              setContactsByEmail((prev) => {
                const map = new Map(prev);
                map.set(key, next);
                return map;
              });
              setContactsForGender((prev) => {
                const idx = prev.findIndex((c) => Number(c.id) === Number(next.id));
                if (idx < 0) return [...prev, next];
                const copy = [...prev];
                copy[idx] = { ...copy[idx], ...next };
                return copy;
              });
            }
          }}
          onDelete={(c) => {
            if (!c?.id) return;
            setContactToDelete(c);
          }}
          onHistory={(c) => {
            closeViewedContact();
            navigate(`/contacts?open=${c.id}&history=1`);
          }}
          onEdit={(c) => {
            closeViewedContact();
            navigate(`/contacts?open=${c.id}&edit=1`);
          }}
        />,
        document.body
      )}

      {contactToDelete && createPortal(
        <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => !deletingContact && setContactToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Kontakt löschen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    Möchtest du{' '}
                    <span className="font-medium text-neutral-800 dark:text-neutral-200">
                      „{contactToDelete.name || contactToDelete.email}“
                    </span>{' '}
                    wirklich löschen?
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                disabled={deletingContact}
                onClick={confirmDeleteViewedContact}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
              >
                {deletingContact ? 'Löschen…' : 'Löschen'}
              </button>
              <button
                type="button"
                disabled={deletingContact}
                onClick={() => setContactToDelete(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {threadContextMenu?.thread && createPortal(
        (() => {
          const ctxThread = threadContextMenu.thread;
          const ctxMessage = threadContextMenu.message || null;
          const ctxEmails = ctxMessage ? [ctxMessage] : ctxThread.emails || [];
          const isSingle = !!ctxMessage;
          const isSentCtx = activeTab === 'sent';
          const singleFlagged = isSingle && isFlagged(ctxMessage.flags);
          const ctxDisplayName = isSentCtx
            ? (ctxThread.toName || ctxThread.id)
            : (ctxThread.fromName || ctxThread.id);
          const ctxContactEmail = isSentCtx
            ? (ctxThread.toAddr || ctxThread.id)
            : (ctxThread.fromAddr || ctxThread.id);
          const ctxContactFrom = isSentCtx
            ? (ctxThread.toName ? `${ctxThread.toName} <${ctxContactEmail}>` : ctxContactEmail)
            : ctxThread.from;
          return (
        <div
          className="fixed z-[200] min-w-[12rem] rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl py-1"
          style={{ left: threadContextMenu.x, top: threadContextMenu.y }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="px-3 py-2 border-b border-neutral-100 dark:border-neutral-800">
            <p className="text-xs font-medium text-neutral-800 dark:text-neutral-100 truncate max-w-[11rem]">
              {isSingle
                ? (ctxMessage.subject || '(Kein Betreff)')
                : ctxDisplayName}
            </p>
            <p className="text-[11px] text-neutral-400 truncate max-w-[11rem]">
              {isSingle
                ? (ctxDisplayName || 'Einzelne Nachricht')
                : `${ctxThread.count} Nachricht${ctxThread.count === 1 ? '' : 'en'}`}
            </p>
          </div>
          {!isSentCtx ? (
              <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
            disabled={!!bulkDeleting || !!flagBusyUid}
            onClick={() => {
              setThreadContextMenu(null);
              if (isSingle) toggleEmailImportant(ctxMessage);
              else toggleThreadImportant(ctxThread);
            }}
          >
            <Star
              className={`w-4 h-4 ${
                (isSingle ? singleFlagged : ctxThread.isImportant)
                  ? 'text-amber-500 fill-amber-500'
                  : 'text-neutral-400'
              }`}
            />
            {(isSingle ? singleFlagged : ctxThread.isImportant)
              ? 'Wichtig entfernen'
              : 'Als wichtig markieren'}
              </button>
          ) : null}
          {!isKnownContactEmail(ctxContactEmail) ? (
              <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              onClick={() => {
                setThreadContextMenu(null);
                openCreateContactFromSender(
                  ctxContactFrom,
                  ctxDisplayName,
                  ctxContactEmail
                );
              }}
            >
              <UserPlus className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              Als Kontakt anlegen
              </button>
          ) : (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              onClick={() =>
                openContactDetailsFromEmail(ctxContactEmail, {
                  name: ctxDisplayName || '',
                })
              }
            >
              <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              Kontaktdetails öffnen
            </button>
          )}
          {activeTab === 'category' &&
          activeCategoryId &&
          (threadContextMenu.assignedCategoryIds?.has(String(activeCategoryId)) ?? true) ? (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 border-t border-neutral-100 dark:border-neutral-800 mt-1"
              onClick={async () => {
                setThreadContextMenu(null);
                await removeEmailsFromCategory(ctxEmails, activeCategoryId);
              }}
            >
              <FolderMinus className="w-4 h-4 text-neutral-500" />
              Aus diesem Ordner entfernen
            </button>
          ) : null}
          {flatFolders.length > 0 && activeTab !== 'sent' && (
            <div className="border-t border-neutral-100 dark:border-neutral-800 mt-1 pt-1">
              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 flex items-center gap-1.5">
                <Tag className="w-3 h-3" />
                In Ordner
              </p>
              {flatFolders.map((cat) => {
                const assigned = !!(
                  threadContextMenu.assignedCategoryIds &&
                  threadContextMenu.assignedCategoryIds.has(String(cat.id))
                );
                return (
                  <button
                    key={cat.id}
                    type="button"
                    style={{ paddingLeft: `${12 + (Number(cat.depth) || 0) * 12}px` }}
                    className="w-full flex items-center gap-2 pr-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                    onClick={async () => {
                      setThreadContextMenu(null);
                      if (assigned) {
                        await removeEmailsFromCategory(ctxEmails, cat.id);
                      } else {
                        await assignEmailsToCategory(ctxEmails, cat.id);
                      }
                    }}
                  >
                    <Folder
                      className="w-3.5 h-3.5 flex-shrink-0"
                      style={{ color: cat.color }}
                      fill={cat.color}
                      fillOpacity={0.22}
                    />
                    <span className="flex-1 truncate text-left">{cat.name}</span>
                    {assigned ? (
                      <span className="text-[10px] text-danger-600 dark:text-danger-400 flex-shrink-0">
                        Entfernen
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
          {activeTab === 'spam' ? (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              onClick={() => {
                setThreadContextMenu(null);
                markEmailsAsNotSpam(ctxEmails);
              }}
            >
              <ShieldCheck className="w-4 h-4" />
              Kein Spam
            </button>
          ) : activeTab !== 'sent' ? (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30"
              onClick={() => {
                setThreadContextMenu(null);
                markEmailsAsSpam(ctxEmails);
              }}
            >
              <ShieldAlert className="w-4 h-4" />
              Als Spam markieren
            </button>
          ) : null}
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 disabled:opacity-50"
            disabled={!!bulkDeleting}
            onClick={() => {
              setThreadContextMenu(null);
              if (isSingle) {
                if (activeTab === 'sent') deleteSentEmail(ctxMessage);
                else deleteInboxEmail(ctxMessage);
              } else if (activeTab === 'sent') {
                deleteSentThread(ctxThread);
              } else {
                deleteInboxThread(ctxThread);
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
            {isSingle ? 'Löschen' : 'Alle Nachrichten löschen'}
          </button>
        </div>
          );
        })(),
        document.body
      )}

    </div>
  );
};

export default EmailInbox;
